import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, index, numeric, uniqueIndex, uuid, unique } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

import { tenants, companies, projects, sites, areas, users } from './common';
import type { PtwValidationGatesShape } from "../ptw-gate-modes";
import { shiftHandovers, workers } from './workers';

// PTW CAP-14 (Task #1367) — Multi-Operator / Joint-Venture role enum.
// Mirrors the operator role taxonomy described in the task spec.
//   * `operator`               — actively operates the asset / scope.
//   * `non_operating_partner`  — equity stake without operational control.
//   * `duty_holder`            — legally accountable HSE/regulatory entity.
//   * `regulator_observer`     — observer (e.g. national regulator with
//                                read-only audit visibility).
export const PTW_OPERATOR_ROLES = [
  "operator",
  "non_operating_partner",
  "duty_holder",
  "regulator_observer",
] as const;
export type PtwOperatorRole = typeof PTW_OPERATOR_ROLES[number];

export type PtwActionType =
  | "return_for_revision" | "amendment" | "pause_work" | "resume_work"
  | "close_permit" | "cancel_permit" | "add_comment" | "field_safety_check" | "isolation_point"
  | "extend_permit" | "emergency_stop";
export type PtwVisibilityCondition = {
  type: "always" | "at_steps" | "at_statuses" | "after_hours" | "field_value";
  steps?: string[];
  statuses?: string[];
  afterHours?: number;
  fieldKey?: string;
  fieldValue?: string;
};
export type PtwRequiredInputField = {
  fieldKey: string;
  label: string;
  fieldType: "short_text" | "long_text" | "yes_no" | "file_upload" | "checkbox_declaration";
  required: boolean;
};
export type PtwActionConfig = {
  id: string;
  actionType: PtwActionType;
  displayName: string;
  enabled: boolean;
  permittedRoles: string[];
  visibilityCondition: PtwVisibilityCondition;
  requiredInputFields: PtwRequiredInputField[];
  signatureMode?: "drawing" | "re_authentication" | "button_declaration" | "none";
  postActionTransition?: string;
  notifyRoles: string[];
};

// ── PTW Field Safety Check — Canonical check-type registry (CAP-1) ────
//
// `field_safety_check_records` is the canonical store for *every* permit-time
// field safety check (gas test, atmospheric test, hot-work gas test, fit-test,
// BA test, periodic atmospheric retest, weather/heat-stress check, …). The
// legacy `ptw_gas_tests` and `ptw_hot_work_gas_tests` tables have been retired
// (Task #1731) — runtime reads/writes go through the canonical store via
// the projections in `server/storage/ptw/isolations-gas.ts`.
//
// `checkTypeId` carries either one of the canonical IDs below or a tenant-
// scoped ID minted by the Check-Types Builder (`PtwCheckTypeConfig`). The
// canonical IDs interop with the engine's gating logic and the cron's retest
// enforcement; tenant-scoped IDs are valid but receive no engine-side gating
// beyond what the template's `requiredFrequencyHours` configures.
export const PTW_CANONICAL_CHECK_TYPES = {
  GAS_TEST: "gas_test",                                 // ex-ptw_gas_tests
  HOT_WORK_GAS_TEST: "hot_work_gas_test",               // ex-ptw_hot_work_gas_tests
  ATMOSPHERIC_TEST: "atmospheric_test",                 // confined-space atmospheric reading
  PERIODIC_ATMOSPHERIC_RETEST: "periodic_atmospheric_retest",
  FIT_TEST: "fit_test",                                 // mask/respirator quantitative fit
  BA_TEST: "ba_test",                                   // breathing apparatus pre-use
  WEATHER_CHECK: "weather_check",                       // wind/lightning/visibility
  HEAT_STRESS_CHECK: "heat_stress_check",               // WBGT / heat-strain
} as const;
export type PtwCanonicalCheckTypeId = typeof PTW_CANONICAL_CHECK_TYPES[keyof typeof PTW_CANONICAL_CHECK_TYPES];
export const PTW_CANONICAL_CHECK_TYPE_IDS: PtwCanonicalCheckTypeId[] = Object.values(PTW_CANONICAL_CHECK_TYPES);

/** Default human-readable label for a canonical check type. */
export const PTW_CANONICAL_CHECK_TYPE_LABELS: Record<PtwCanonicalCheckTypeId, string> = {
  gas_test: "Gas Test",
  hot_work_gas_test: "Hot Work Gas Test",
  atmospheric_test: "Atmospheric Test",
  periodic_atmospheric_retest: "Periodic Atmospheric Retest",
  fit_test: "Fit Test",
  ba_test: "Breathing Apparatus Test",
  weather_check: "Weather Check",
  heat_stress_check: "Heat Stress Check",
};

/**
 * Mandatory retest cadence per canonical check type, in hours. `null` means
 * "no platform-mandated cadence — falls back to the workflow template's
 * `gasTestIntervalHours` or the check-type's `requiredFrequencyHours`".
 *
 * Used by the gas-retest-enforcement cron (`background-worker.ts`) to compute
 * `nextDueAt` defaults when a write does not explicitly set one. CAP-9 will
 * extend this to drive auto-suspend cadence per check type.
 */
export const PTW_CANONICAL_CHECK_TYPE_RETEST_HOURS: Record<PtwCanonicalCheckTypeId, number | null> = {
  gas_test: 4,                       // industry default for confined-space / general
  hot_work_gas_test: 1,              // hot work continuous-monitoring cadence
  atmospheric_test: 4,
  periodic_atmospheric_retest: 2,
  fit_test: null,                    // annual cadence handled by credentials, not per-permit
  ba_test: null,                     // pre-use only, no retest
  weather_check: 4,
  heat_stress_check: 1,
};

// ── PTW Check Type Config Types ────────────────────────────────────────
export type PtwCheckFormField = {
  fieldKey: string;
  label: string;
  fieldType: "number" | "short_text" | "yes_no" | "single_select";
  unit?: string;
  min?: number;
  max?: number;
  passThreshold?: number;
  required: boolean;
};
export type PtwCheckTypeConfig = {
  id: string;
  name: string;
  icon: string;
  logFormFields: PtwCheckFormField[];
  requiredFrequencyHours?: number;
  requiredBeforeStart: boolean;
  requiredBeforeClose: boolean;
  permittedRoles: string[];
  requiresSignature: boolean;
  enabled: boolean;
};

// ── PTW Start Work Config Types ────────────────────────────────────────
export type PtwStartWorkConfig = {
  preStartChecklist: { id: string; label: string; mandatory: boolean }[];
  requiredCheckTypeBeforeStart?: string;
  locationVerification: boolean;
  signatureMode: "drawing" | "re_authentication" | "button_declaration";
  briefingScreen?: {
    enabled: boolean;
    title: string;
    body: string;
    ackLabel: string;
  };
  /**
   * Task #2672 — When `true` (the default), the sign-on gate additionally
   * verifies that the permit's assigned `performing_authority` is among the
   * signed-on workers before the permit can transition to `active`.
   *
   * Set to `false` on templates where this check is intentionally relaxed
   * (e.g. short-duration / unattended automated operations).
   */
  requirePerformingAuthoritySignOn?: boolean;
};

// ── PTW Conflict Detection Config Types ───────────────────────────────
export type PtwConflictDetectionConfig = {
  enabled: boolean;
  timeWindowHours: number;
  scope: "area" | "site" | "both";
  statusFilter: string[];
  severity: "warning" | "blocking";
  displayLocations: ("wizard" | "live_permit")[];
  displayFormat: "inline_banner" | "modal_alert" | "sidebar_panel";
  customMessage?: string;
};
export const ptwWorkflowTemplates = pgTable("ptw_workflow_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  name: text("name").notNull(),
  description: text("description").notNull().default(""),
  version: integer("version").notNull().default(1),
  status: text("status").notNull().default("draft"),
  isDefault: boolean("is_default").notNull().default(false),
  workCategories: text("work_categories").array().default([]),
  siteScope: text("site_scope").array().default([]),
  tenantScope: text("tenant_scope"),
  // PTW Phase 4.A (Task #1262): each gate now carries an optional `mode`
  // surfaced in the template editor. The legacy `enabled`/`mandatory` pair
  // is kept so the runtime engine continues to evaluate gates without a
  // schema migration. `requireSafetySignoff` is a top-level toggle that
  // forces an HSE Officer sign-off at every issuance.
  validationGates: jsonb("validation_gates").$type<PtwValidationGatesShape>().default({}),
  amendmentConfig: jsonb("amendment_config").$type<{
    amendableFields?: { field: string; label: string; requiresReApproval: boolean; approverRole?: string }[];
    allowedRoles?: string[];
    documentReUploadRequiresApproval?: boolean;
  }>().default({}),
  preStartConfig: jsonb("pre_start_config").$type<{
    enabled?: boolean;
    required?: boolean;
    items?: { id: string; label: string; mandatory: boolean }[];
  }>().default({}),
  startWorkConfig: jsonb("start_work_config").$type<PtwStartWorkConfig>().default({
    preStartChecklist: [],
    locationVerification: false,
    signatureMode: "button_declaration",
  }),
  actionsConfig: jsonb("actions_config").$type<PtwActionConfig[]>().default([]),
  checkTypesConfig: jsonb("check_types_config").$type<PtwCheckTypeConfig[]>().default([]),
  conflictDetectionConfig: jsonb("conflict_detection_config").$type<PtwConflictDetectionConfig>().default({
    enabled: false,
    timeWindowHours: 0,
    scope: "area",
    statusFilter: ["draft", "submitted", "under_review", "approved", "issued", "active"],
    severity: "warning",
    displayLocations: ["wizard"],
    displayFormat: "inline_banner",
  }),
  sourceMode: text("source_mode").notNull().default("tenant"),
  // PTW Phase 4.B (Task #1263): publish lifecycle metadata.
  // `publishedAt`   – timestamp when the template most recently transitioned
  //                   draft → active. NULL for any template that has never
  //                   been published (drafts that were never promoted).
  // `supersededAt`  – timestamp when this active version was replaced by a
  //                   newer version (i.e. the moment another template with
  //                   the same `name` was published). NULL while the row is
  //                   the canonical published version.
  // `supersededByTemplateId` – the id of the template that replaced this
  //                   one. Forms a versioning chain so the publish preview
  //                   can diff vs the previously published version and so
  //                   permits hold a stable lineage pointer.
  publishedAt: timestamp("published_at", { withTimezone: true }),
  supersededAt: timestamp("superseded_at", { withTimezone: true }),
  supersededByTemplateId: text("superseded_by_template_id"),
  // PTW CAP-7 (Task #1356) — Super-Admin Blueprint Cascade subscription model.
  //
  // When a tenant adopts a super-admin blueprint, the tenant copy keeps a
  // pointer back to the upstream blueprint template id (`upstream_blueprint_id`)
  // and the version it was last adopted from (`upstream_version`). Whenever
  // the SA publishes a new version of that blueprint, every tenant template
  // pointing at it is flipped to `cascade_status='update_available'` and a
  // `ptw.template.blueprint_published` notification is fanned out. The tenant
  // admin then opens the cascade drawer and chooses adopt / decline / fork:
  //   - adopt   → re-runs publish-preview safety gates, re-versions the
  //               tenant copy onto the new upstream version, sets
  //               cascade_status='in_sync', upstream_version=newVersion.
  //   - decline → pins to current version (cascade_status='declined') —
  //               future SA publishes will re-light the badge.
  //   - fork    → severs the link entirely (cascade_status='forked',
  //               upstream_blueprint_id=NULL). The tenant now owns the copy.
  //
  // SA blueprint rows themselves carry `source_mode='blueprint'` and either
  // a NULL upstream_blueprint_id or a self-reference; the cascade fan-out
  // explicitly skips templates whose own id equals upstream_blueprint_id.
  upstreamBlueprintId: text("upstream_blueprint_id"),
  upstreamVersion: integer("upstream_version"),
  cascadeStatus: text("cascade_status").notNull().default("in_sync"),
  // Task #3806 — Blueprint Governance: timestamp when cascadeStatus last changed.
  // Enables the SA Blueprint Drift Report to surface long-standing declines.
  cascadeStatusChangedAt: timestamp("cascade_status_changed_at", { withTimezone: true }),
  createdBy: text("created_by"),
  // Task #3803 — PTW Template Backend Integrity & Safety.
  // Tracks which PTW Library slug this template was installed from.
  // NULL for manually created templates and blueprint copies.
  // Used by the duplicate-install guard: a second install of the same
  // slug is rejected with 409 unless `?force=true` is passed.
  sourceLibrarySlug: text("source_library_slug"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // PTW CAP-7 (Task #1356) — Index used by the SA Blueprint Library
  // subscriber-count query and the publish fan-out helper. Partial
  // (NOT NULL) because the vast majority of rows are tenant-owned and
  // do not subscribe to any blueprint.
  idxPtwTemplatesUpstreamBlueprint: index("idx_ptw_workflow_templates_upstream").on(t.upstreamBlueprintId),
  // Task #3464 — composite unique prevents two concurrent publishes from
  // producing duplicate (tenant, name, version) template rows.
  uqPtwTemplatesTenantNameVer: uniqueIndex("ptw_templates_tenant_name_ver_uidx").on(t.tenantId, t.name, t.version),
  // Task #3803 — duplicate-install guard + library provenance lookup.
  // Partial (WHERE source_library_slug IS NOT NULL): only library-installed
  // templates carry this field; keeps the index lean and matches the migration.
  idxPtwTemplatesSourceLibrarySlug: index("idx_ptw_templates_source_library_slug").on(t.tenantId, t.sourceLibrarySlug).where(sql`source_library_slug IS NOT NULL`),
}));
// PTW CAP-7 (Task #1356) — cascade_status enumeration. Persisted as text
// so future states (e.g. `pending_review`) don't require a migration; the
// runtime guards against unknown values explicitly.
export const PTW_TEMPLATE_CASCADE_STATUSES = [
  "in_sync",
  "update_available",
  "declined",
  "forked",
] as const;
export type PtwTemplateCascadeStatus = (typeof PTW_TEMPLATE_CASCADE_STATUSES)[number];
export const insertPtwWorkflowTemplateSchema = createInsertSchema(ptwWorkflowTemplates).omit({ tenantId: true, createdAt: true, updatedAt: true });
export type InsertPtwWorkflowTemplate = z.infer<typeof insertPtwWorkflowTemplateSchema>;
export type PtwWorkflowTemplate = typeof ptwWorkflowTemplates.$inferSelect;

// ── PTW Phase 4.B (Task #1263) — Template Cascade Jobs ────────────────
//
// Captures durable state for the opt-in cascade that re-versions active
// permits onto a freshly published template version. Cascade is intentionally
// opt-in (it's the riskiest moment in template management) and the job row
// holds the structured conflict report so admins can audit which permits
// were migrated, which were skipped, and which conflicted at runtime.
//
// `status` lifecycle:
//   pending   — created but worker has not yet picked it up.
//   running   — worker is iterating permits; counters update incrementally.
//   completed — every permit was processed (success / conflict / skipped).
//   failed    — the worker bailed (e.g. fatal DB error). `errorMessage`
//                                 explains why; partial counters are preserved.
//
// Conflict shape (`conflicts` jsonb):
//   { permitId, permitNumber, code, message, details? }
//   * code values: "step_mismatch" | "incompatible_status" | "missing_data"
//                  | "engine_error"
export const PTW_TEMPLATE_CASCADE_JOB_STATUSES = [
  "pending",
  "running",
  "completed",
  "failed",
] as const;
export type PtwTemplateCascadeJobStatus = (typeof PTW_TEMPLATE_CASCADE_JOB_STATUSES)[number];

export const PTW_TEMPLATE_CASCADE_CONFLICT_CODES = [
  "step_mismatch",
  "incompatible_status",
  "missing_data",
  "engine_error",
] as const;
export type PtwTemplateCascadeConflictCode = (typeof PTW_TEMPLATE_CASCADE_CONFLICT_CODES)[number];

export type PtwTemplateCascadeConflict = {
  permitId: string;
  permitNumber: string;
  code: PtwTemplateCascadeConflictCode;
  message: string;
  details?: Record<string, unknown>;
};

export const ptwTemplateCascadeJobs = pgTable("ptw_template_cascade_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // Source/target template ids — `from` is the previously-published version
  // (or the original draft id when there was no prior published version)
  // and `to` is the freshly published version.
  fromTemplateId: text("from_template_id").notNull(),
  toTemplateId: text("to_template_id").notNull().references(() => ptwWorkflowTemplates.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"),
  totalPermits: integer("total_permits").notNull().default(0),
  migratedPermits: integer("migrated_permits").notNull().default(0),
  conflictedPermits: integer("conflicted_permits").notNull().default(0),
  skippedPermits: integer("skipped_permits").notNull().default(0),
  conflicts: jsonb("conflicts").$type<PtwTemplateCascadeConflict[]>().notNull().default([]),
  // Free-form options the requester provided (e.g. statuses to include,
  // dryRun flag). Persisted so the job is fully self-describing.
  options: jsonb("options").$type<Record<string, unknown>>().notNull().default({}),
  requestedBy: text("requested_by"),
  requestedByName: text("requested_by_name"),
  errorMessage: text("error_message"),
  startedAt: timestamp("started_at", { withTimezone: true }),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // Hot path: list cascade jobs for a target template (the "after publish"
  // history view) and resolve a single job by tenant.
  idxPtwTemplateCascadeJobsTenantTarget: index("idx_ptw_template_cascade_jobs_tenant_target")
    .on(t.tenantId, t.toTemplateId),
  idxPtwTemplateCascadeJobsTenantStatus: index("idx_ptw_template_cascade_jobs_tenant_status")
    .on(t.tenantId, t.status),
}));
export const insertPtwTemplateCascadeJobSchema = createInsertSchema(ptwTemplateCascadeJobs).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwTemplateCascadeJob = z.infer<typeof insertPtwTemplateCascadeJobSchema>;
export type PtwTemplateCascadeJob = typeof ptwTemplateCascadeJobs.$inferSelect;

// ── Task #3458 — Per-permit cascade outcome log ────────────────────────
//
// One row per permit processed by a cascade job. Persisted inside the
// same db.transaction as the permit/step-instance rewrites so the log
// is always consistent with what was actually applied. `resumeCascade`
// reads this table to skip permits that were already processed when
// replaying a stalled or failed job.
export const ptwTemplateCascadeLog = pgTable("ptw_template_cascade_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: text("job_id").notNull().references(() => ptwTemplateCascadeJobs.id, { onDelete: "cascade" }),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id").notNull(),
  permitNumber: text("permit_number").notNull(),
  outcome: text("outcome").$type<"migrated" | "conflicted" | "skipped">().notNull(),
  conflictCode: text("conflict_code"),
  conflictMessage: text("conflict_message"),
  appliedAt: timestamp("applied_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwCascadeLogJob: index("idx_ptw_cascade_log_job").on(t.jobId),
  idxPtwCascadeLogPermit: index("idx_ptw_cascade_log_permit").on(t.permitId),
  // Unique per permit per job — prevents duplicate log rows on replay
  uniqPtwCascadeLogJobPermit: unique("uniq_ptw_cascade_log_job_permit").on(t.jobId, t.permitId),
}));
export const insertPtwTemplateCascadeLogSchema = createInsertSchema(ptwTemplateCascadeLog).omit({
  id: true,
  appliedAt: true,
});
export type InsertPtwTemplateCascadeLog = z.infer<typeof insertPtwTemplateCascadeLogSchema>;
export type PtwTemplateCascadeLog = typeof ptwTemplateCascadeLog.$inferSelect;

// ── PTW CAP-7 (Task #1356) — Blueprint Cascade Events ────────────────
//
// One row per tenant decision against a super-admin blueprint publish.
// The SA Blueprint Library renders these to show "X tenants adopted, Y
// declined, Z forked" per blueprint version, and tenant audit views show
// the per-template decision history. The row is written by the adopt /
// decline / fork endpoints in `server/ptw-routes.ts` and is the canonical
// audit record for cross-tenant cascade activity (the per-tenant copy of
// the blueprint already gets a normal `ptw_audit_log` row on the synthetic
// `template:<id>` permit id).
export const PTW_BLUEPRINT_CASCADE_DECISIONS = ["adopt", "decline", "fork"] as const;
export type PtwBlueprintCascadeDecision = (typeof PTW_BLUEPRINT_CASCADE_DECISIONS)[number];

export type PtwBlueprintCascadeImpactSummary = {
  // Counters mirrored from the publish-preview that ran on adopt; for
  // decline/fork these are zeroed out (no permits are touched).
  totalActivePermits?: number;
  migratedPermits?: number;
  conflictedPermits?: number;
  // The cascade job id created by `runPtwTemplateCascade` (adopt only).
  cascadeJobId?: string | null;
  // Free-form notes — e.g. the reason text the tenant admin supplied.
  notes?: string | null;
};

export const ptwBlueprintCascadeEvents = pgTable("ptw_blueprint_cascade_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // The tenant whose template was on the receiving end. NOT the SA's
  // tenant id (SA blueprints typically belong to a "platform" tenant).
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // The upstream SA blueprint template id and the published version of
  // it that this decision relates to.
  blueprintTemplateId: text("blueprint_template_id").notNull(),
  blueprintVersion: integer("blueprint_version").notNull(),
  // The tenant copy that adopted/declined/forked. NULL on first-time
  // adoption flows where the row hadn't been created yet — the adopt
  // endpoint always backfills this once the tenant copy is materialised.
  tenantTemplateId: text("tenant_template_id"),
  decision: text("decision").notNull(),
  // The version the tenant copy was on before the decision (NULL on
  // first-time subscription) and after the decision.
  fromVersion: integer("from_version"),
  toVersion: integer("to_version"),
  impactSummary: jsonb("impact_summary").$type<PtwBlueprintCascadeImpactSummary>().notNull().default({}),
  decidedBy: text("decided_by"),
  decidedByName: text("decided_by_name"),
  decidedAt: timestamp("decided_at", { withTimezone: true }).notNull().defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // Hot paths: SA Blueprint Library lists every event for a given
  // blueprint template across all tenants (cross-tenant — explicitly run
  // under platform scope). Tenant audit lists every event for a tenant
  // template.
  idxPtwBpCascadeBlueprint: index("idx_ptw_bp_cascade_blueprint")
    .on(t.blueprintTemplateId, t.blueprintVersion),
  idxPtwBpCascadeTenant: index("idx_ptw_bp_cascade_tenant")
    .on(t.tenantId, t.blueprintTemplateId),
  idxPtwBpCascadeTenantTemplate: index("idx_ptw_bp_cascade_tenant_template")
    .on(t.tenantId, t.tenantTemplateId),
}));
export const insertPtwBlueprintCascadeEventSchema = createInsertSchema(ptwBlueprintCascadeEvents).omit({
  id: true,
  tenantId: true,
  createdAt: true,
});
export type InsertPtwBlueprintCascadeEvent = z.infer<typeof insertPtwBlueprintCascadeEventSchema>;
export type PtwBlueprintCascadeEvent = typeof ptwBlueprintCascadeEvents.$inferSelect;

// ── PTW Field Descriptor Types ────────────────────────────────────────
export type PtwFieldSourceMode = "manual" | "linked_equipment" | "linked_sites" | "linked_areas" | "linked_companies" | "linked_workers";
export type PtwFieldType = "short_text" | "long_text" | "number" | "date_time" | "yes_no" | "single_select" | "multi_select" | "file_upload" | "display";
export type PtwFieldValidationRule = {
  type: "min_length" | "max_length" | "numeric_range" | "regex" | "date_constraint";
  value: string | number;
  message?: string;
};
export type PtwFieldDescriptor = {
  fieldKey: string;
  fieldType: PtwFieldType;
  label: string;
  helpText?: string;
  placeholder?: string;
  enabled: boolean;
  required: boolean;
  sourceMode: PtwFieldSourceMode;
  multiSelect?: boolean;
  options?: { value: string; label: string }[];
  validationRules?: PtwFieldValidationRule[];
  order: number;
  isCustom?: boolean;
  standardFieldKey?: string;
  // PTW CAP-3 (Task #1352): optional per-field visibility predicate. The
  // shape is identical to action-level visibility (`PtwVisibilityCondition`)
  // and is evaluated by `evaluatePtwVisibility` in `shared/ptw-visibility.ts`
  // both inside the create wizard and in the Detail panels. `undefined` is
  // semantically equivalent to `{ type: "always" }`.
  visibilityCondition?: PtwVisibilityCondition;
};
export type PtwSignatureRequirement = {
  id: string;
  mode: "drawing" | "re_authentication" | "button_declaration";
  role: string;
  label: string;
  prompt?: string;
  order: number;
  // PTW CAP-19 (Task #1372) — Optional per-signature minimum step-up
  // factor. When omitted, the engine uses the step-level
  // `config.minStepUpFactor` (which itself falls back to the tenant
  // default). The floor is applied additively — a signature requirement
  // can RAISE the floor above the step config but cannot lower it.
  // Allowed values mirror `STEP_UP_FACTORS` from
  // `shared/schema/sso.ts`:
  //   "password" | "password+totp" | "webauthn" | "webauthn+presence"
  //   | "workos_step_up"
  minStepUpFactor?:
    | "password"
    | "password+totp"
    | "webauthn"
    | "webauthn+presence"
    | "workos_step_up";
  // PTW CAP-19 — When true, the engine will REFUSE the WebAuthn /
  // password fallback if `workos_step_up` is required. Defaults to the
  // tenant-level `tenant_step_up_policy.forbid_fallback`.
  noFallback?: boolean;
};
export const ptwWorkflowSteps = pgTable("ptw_workflow_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  // PTW Phase 2.A (Task #1256): FK to ptw_workflow_templates.id with ON DELETE CASCADE.
  // Steps are owned by their parent template; deleting the template removes its steps.
  templateId: text("template_id").notNull().references(() => ptwWorkflowTemplates.id, { onDelete: "cascade" }),
  stepOrder: integer("step_order").notNull(),
  name: text("name").notNull(),
  description: text("description").notNull().default(""),
  stepType: text("step_type").notNull(),
  actorRoles: text("actor_roles").array().default([]),
  approvalMode: text("approval_mode").notNull().default("serial"),
  required: boolean("required").notNull().default(true),
  skippable: boolean("skippable").notNull().default(false),
  visibleInTimeline: boolean("visible_in_timeline").notNull().default(true),
  dueTimeHours: integer("due_time_hours"),
  config: jsonb("config").$type<{
    gasTestRequired?: boolean;
    isolationRequired?: boolean;
    toolboxTalkRequired?: boolean;
    checklistItems?: { id: string; label: string; mandatory: boolean }[];
    requiredAttachments?: string[];
    requiredFields?: string[];
    skipConditionField?: string;
    skipConditionValue?: string;
    branchConditionField?: string;
    branchConditionValue?: string;
    // PTW Phase 4.D (Task #1265) — Quorum / N-of-M approval. Required when
    // `approvalMode === "quorum"`. The engine creates one sibling step
    // instance per actor role (like "parallel"), but only `quorumCount`
    // completions are needed to advance — the rest are auto-completed
    // with `skipped: true` and a `skipReason` referencing the quorum.
    quorumCount?: number;
    // PTW CAP-3 (Task #1352): optional per-step visibility predicate.
    // Mirrors `PtwFieldDescriptor.visibilityCondition`. Stored on the
    // step's `config` jsonb so no migration is required.
    visibilityCondition?: PtwVisibilityCondition;
    // PTW CAP-19 (Task #1372): step-level minimum step-up factor floor
    // applied to every signature requirement on this step. Per-
    // signature `minStepUpFactor` may RAISE the floor; tenant default
    // and signature requirement combine via `meetsStepUpFloor` in
    // `server/services/step-up-policy.ts`. Allowed values match
    // `STEP_UP_FACTORS` from `shared/schema/sso.ts`.
    minStepUpFactor?:
      | "password"
      | "password+totp"
      | "webauthn"
      | "webauthn+presence"
      | "workos_step_up";
    // PTW CAP-19: when true, refuse WebAuthn / password fallback if
    // workos_step_up is required. Default = tenant policy.
    noFallback?: boolean;
  }>().default({}),
  fieldsConfig: jsonb("fields_config").$type<PtwFieldDescriptor[]>().default([]),
  signatureRequirements: jsonb("signature_requirements").$type<PtwSignatureRequirement[]>().default([]),
});
export const insertPtwWorkflowStepSchema = createInsertSchema(ptwWorkflowSteps).omit({ tenantId: true });
export type InsertPtwWorkflowStep = z.infer<typeof insertPtwWorkflowStepSchema>;
export type PtwWorkflowStep = typeof ptwWorkflowSteps.$inferSelect;
export const ptwRoleMappings = pgTable("ptw_role_mappings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  systemRole: text("system_role").notNull(),
  tenantTitle: text("tenant_title").notNull(),
  tenantGroup: text("tenant_group"),
  description: text("description").notNull().default(""),
}, (t) => ({
  uniqTenantSystemRole: uniqueIndex("ptw_role_mappings_tenant_system_role_uidx").on(t.tenantId, t.systemRole),
}));
export const insertPtwRoleMappingSchema = createInsertSchema(ptwRoleMappings).omit({ id: true, tenantId: true });
export type InsertPtwRoleMapping = z.infer<typeof insertPtwRoleMappingSchema>;
export type PtwRoleMapping = typeof ptwRoleMappings.$inferSelect;
export const ptwPermits = pgTable("ptw_permits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  permitNumber: text("permit_number").notNull(),
  // PTW Phase 2.A (Task #1256): FK to ptw_workflow_templates.id with ON DELETE RESTRICT.
  // Permits are operational records; templates with live permits cannot be deleted —
  // they must be retired/versioned instead. The `template_version` column captures the
  // template state at issue time so versioned amendments remain auditable.
  templateId: text("template_id").notNull().references(() => ptwWorkflowTemplates.id, { onDelete: "restrict" }),
  templateVersion: integer("template_version").notNull().default(1),
  // PTW Phase 2.B (Task #1257) — Engine concurrency optimistic-lock counter.
  // Bumped exactly once per state-changing engine action (`executeAction`,
  // `createAmendment`, `approveAmendment`, `rejectAmendment`) under
  // `SELECT … FOR UPDATE` so concurrent writers see a deterministic
  // serialisation order. Clients pass `expectedVersion` on submit; mismatch
  // triggers HTTP 409 + the current version so the UI can refresh and retry.
  version: integer("version").notNull().default(1),
  status: text("status").notNull().default("draft"),
  currentStepId: text("current_step_id"),

  // Task #3603 (400) — FK to sites/areas with ON DELETE RESTRICT. Both stay
  // nullable (not every permit is bound to a registered site/area). Pre-FK
  // orphan rows were swept to NULL by migration 0177.
  siteId: text("site_id").references(() => sites.id, { onDelete: "restrict" }),
  areaId: text("area_id").references(() => areas.id, { onDelete: "restrict" }),
  // facilityId has no backing table — kept as free-text (documented, no FK).
  facilityId: text("facility_id"),
  workLocation: text("work_location").notNull(),
  equipmentReference: text("equipment_reference"),
  workDescription: text("work_description").notNull(),
  workType: text("work_type").notNull(),

  // Task #3603 (400) — FK to users. Added NOT VALID by migration 0177 because
  // legacy rows carry a 'system' sentinel / deleted-user id; the FK enforces
  // referential integrity for all new writes without rejecting the legacy corpus.
  requestorUserId: text("requestor_user_id").notNull().references(() => users.id),
  requestorName: text("requestor_name").notNull(),
  performingCompany: text("performing_company"),
  performingCompanyId: text("performing_company_id").references(() => companies.id),
  projectId: text("project_id").references(() => projects.id),
  workTeam: text("work_team").array().default([]),
  numberOfWorkers: integer("number_of_workers").notNull().default(1),

  requestedStartTime: text("requested_start_time"),
  requestedEndTime: text("requested_end_time"),
  issuedStartTime: text("issued_start_time"),
  issuedExpiryTime: text("issued_expiry_time"),
  actualWorkStartTime: text("actual_work_start_time"),
  actualWorkEndTime: text("actual_work_end_time"),
  closedTime: text("closed_time"),
  cancelledTime: text("cancelled_time"),
  decidedAt: timestamp("decided_at", { withTimezone: true }),

  riskCategory: text("risk_category").notNull().default("medium"),
  traLevel: integer("tra_level").notNull().default(1),
  simopsFlag: boolean("simops_flag").notNull().default(false),

  hazards: text("hazards").array().default([]),
  controlMeasures: text("control_measures").array().default([]),
  ppeRequirements: text("ppe_requirements").array().default([]),

  gasTestRequired: boolean("gas_test_required").notNull().default(false),
  isolationRequired: boolean("isolation_required").notNull().default(false),

  linkedWorkOrder: text("linked_work_order"),
  linkedMethodStatement: text("linked_method_statement"),
  linkedDrawings: text("linked_drawings").array().default([]),
  linkedErp: text("linked_erp"),

  attachments: jsonb("attachments").$type<{ name: string; fileId: string; type: string; uploadedBy?: string; uploadedAt?: string }[]>().default([]),

  approvedByIssuer: text("approved_by_issuer"),
  approvedByAreaAuthority: text("approved_by_area_authority"),
  acceptedByPermitAcceptor: text("accepted_by_permit_acceptor"),
  isolationConfirmedBy: text("isolation_confirmed_by"),
  gasTestConfirmedBy: text("gas_test_confirmed_by"),
  closedVerifiedBy: text("closed_verified_by"),

  workCompletedFlag: boolean("work_completed_flag"),
  areaLeftSafe: boolean("area_left_safe"),
  deIsolationRequired: boolean("de_isolation_required"),
  deIsolationCompleted: boolean("de_isolation_completed"),
  longTermIsolation: boolean("long_term_isolation"),
  continuationPermitRef: text("continuation_permit_ref"),
  continuationOf: text("continuation_of"),

  closureOutcome: text("closure_outcome"),

  suspendedTimes: jsonb("suspended_times").$type<{ timestamp: string; reason: string; suspendedBy: string }[]>().default([]),
  restartTimes: jsonb("restart_times").$type<{ timestamp: string; approvedBy: string; notes: string }[]>().default([]),

  preStartConfirmations: jsonb("pre_start_confirmations").$type<{
    completedAt?: string;
    completedBy?: string;
    items?: { itemId: string; label: string; confirmed: boolean }[];
  }>(),

  permitMetadata: jsonb("permit_metadata").$type<Record<string, unknown>>().default({}),

  // PTW Phase 2.C (Task #1258): attachment retention metadata.
  // `retainUntil` is the per-row purge horizon used by the sweep engine
  // (server/services/ptw-attachment-retention.ts). NULL means "fall back
  // to the resolved retention policy" — the sweep computes the cutoff
  // from `created_at + retention_days` (resolved via tenant_retention_overrides
  // exactly like every other policy in retention-engine.ts).
  // `legalHold` is a per-row hard block: while true, the sweep MUST skip
  // the row regardless of `retainUntil`. Kept locally on the PTW tables
  // (rather than only on the tenant-wide tenant_retention_overrides row)
  // so an investigation can freeze a single permit without freezing every
  // other permit in the tenant.
  retainUntil: timestamp("retain_until", { withTimezone: true }),
  legalHold: boolean("legal_hold").notNull().default(false),

  // PTW CAP-20 (Task #1373) — Drill / Training Mode for Permits.
  // Each permit is tagged with one of:
  //   * `production` — the default; live operational permit subject to all
  //     gates (capacity, SIMOPS, notifications, regulator export).
  //   * `training`  — exercise / classroom permit. The engine SKIPS capacity
  //     enforcement, SIMOPS conflict detection, and operational notifications
  //     for training rows; they route to the Training Inbox and are excluded
  //     from regulator/audit exports by default. Auto-cleanup retention is
  //     governed by `operations.ptw.basicPermits.trainingAutoCleanupDays`.
  //   * `drill`     — emergency-response drill. Behaves like a production
  //     permit (full gates apply) but is watermarked "DRILL" on PDFs/UI and
  //     audit rows are stamped with `drill_id` + `drill_facilitator` so the
  //     drill can be replayed independently of live operational evidence.
  // The column is non-null with a `production` default so all pre-CAP-20 rows
  // (which never carried a mode) continue to behave as production permits.
  mode: text("mode").notNull().default("production"),

  // Task #2195 — PTW Backend Hardening.
  // `draft_data`: JSONB blob storing partial wizard data for draft permits.
  //   Enables an auto-save "resume later" flow without prematurely committing
  //   a real permit row.
  // `simops_hold`: when true the permit is blocked by SIMOPS conflict and
  //   waiting for the background polling pass to lift the hold.
  // `simops_unblocked_at`: stamped by the SIMOPS polling job when it lifts
  //   the hold so the UI can show "SIMOPS cleared at …".
  draftData: jsonb("draft_data").$type<Record<string, unknown>>(),
  simopsHold: boolean("simops_hold").notNull().default(false),
  simopsUnblockedAt: timestamp("simops_unblocked_at", { withTimezone: true }),

  // Task #2231 — Inline gas test readings attached directly on the permit form.
  // Permit managers can capture location, gas type, and per-gas readings (LEL %,
  // O₂ %, H₂S ppm, CO ppm) as structured rows without needing the
  // `field_safety_check_records` workflow. The industrial certificate PDF
  // renderer reads this column when the operational gas-test table is empty.
  gasTests: jsonb("gas_tests").$type<{
    id: string;
    location?: string | null;
    gasType?: string | null;
    lel?: number | null;
    o2?: number | null;
    h2s?: number | null;
    co?: number | null;
    result?: "pass" | "fail" | null;
    time?: string | null;
  }[]>().default([]),

  // Task #3797 — PTW-Only Tenant Standalone Sites & Zones.
  // Nullable FK to ptw_manual_sites for tenants that have no real site/area
  // records (PTW-only deployments without the access / gate-management module).
  // When set, siteId and areaId are NULL. ON DELETE SET NULL so removing a
  // manual site does not cascade-delete existing permits.
  manualSiteId: text("manual_site_id").references(() => ptwManualSites.id, { onDelete: "set null" }),
  manualAreaId: text("manual_area_id").references(() => ptwManualSites.id, { onDelete: "set null" }),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxPtwPermitsTenantStatus: index("idx_ptw_permits_tenant_status").on(t.tenantId, t.status),
  idxPtwPermitsTenantCreatedAt: index("idx_ptw_permits_tenant_created_at").on(t.tenantId, t.createdAt),
  // Task #3797 — PTW-Only Tenant Standalone Sites & Zones.
  idxPtwPermitsManualSiteId: index("idx_ptw_permits_manual_site_id").on(t.manualSiteId),
  idxPtwPermitsManualAreaId: index("idx_ptw_permits_manual_area_id").on(t.manualAreaId),
  // PTW Phase 2.A (Task #1256): hot path for Phase 2.C auto-suspend pagination —
  // sweep job pages "expired but still active" permits per tenant ordered by expiry.
  // `issued_expiry_time` is text-stored ISO 8601 which sorts lexically as if numeric.
  idxPtwPermitsTenantExpiry: index("idx_ptw_permits_tenant_expiry").on(t.tenantId, t.issuedExpiryTime),
  // PTW Phase 2.C (Task #1258): hot path for the attachment retention sweep
  // engine — pages rows where retain_until is past the cutoff per tenant.
  idxPtwPermitsTenantRetain: index("idx_ptw_permits_tenant_retain").on(t.tenantId, t.retainUntil),
  idxPtwPermitsContinuationOf: index("idx_ptw_permits_continuation_of").on(t.continuationOf),
  // PTW CAP-20 (Task #1373) — hot read path for the Training Inbox
  // (mode='training') and the drill replay screen (mode='drill') per tenant.
  idxPtwPermitsTenantMode: index("idx_ptw_permits_tenant_mode").on(t.tenantId, t.mode),
  // Task #3464 — per-tenant unique permit number replaces the global unique
  // constraint. `permit_number` is unique within a tenant but two different
  // tenants may legitimately issue the same numbering sequence.
  uqPtwPermitsTenantNumber: uniqueIndex("ptw_permits_tenant_number_uidx").on(t.tenantId, t.permitNumber),
  // Task #3603 (409) — hot read paths for tenant-scoped project & performing-
  // company permit listings (filter dropdowns + compliance roll-ups).
  idxPtwPermitsTenantProject: index("idx_ptw_permits_tenant_project").on(t.tenantId, t.projectId),
  idxPtwPermitsTenantPerformingCompany: index("idx_ptw_permits_tenant_performing_company").on(t.tenantId, t.performingCompanyId),
  // Task #3603 (475) — GIN index on the work_team text[] so "permits where
  // worker X is on the team" membership lookups use an index instead of a scan.
  idxPtwPermitsWorkTeamGin: index("idx_ptw_permits_work_team_gin").using("gin", t.workTeam),
}));

// PTW CAP-20 (Task #1373) — canonical mode enum. Imported by the engine,
// route layer, and React wizard to keep the three call sites in lock-step
// without ever having to re-declare the literal strings.
export const PTW_PERMIT_MODES = ["production", "training", "drill"] as const;
export type PtwPermitMode = (typeof PTW_PERMIT_MODES)[number];

/**
 * Returns true when a permit should bypass operational gates
 * (capacity, SIMOPS, regulator notifications). Only `training` permits
 * bypass — `drill` permits run with full gates so the drill exercises
 * the real engine path.
 */
export function ptwModeBypassesOperationalGates(mode: string | null | undefined): boolean {
  return mode === "training";
}

/**
 * Returns true when a permit is operational (i.e. should appear in
 * regulator/audit exports and active-permit dashboards by default).
 * `training` rows are excluded; `drill` rows are included but visually
 * marked.
 */
export function ptwModeIsOperational(mode: string | null | undefined): boolean {
  return mode !== "training";
}
export const insertPtwPermitSchema = createInsertSchema(ptwPermits).omit({ tenantId: true });
export type InsertPtwPermit = z.infer<typeof insertPtwPermitSchema>;
export type PtwPermit = typeof ptwPermits.$inferSelect;

// ─── PTW Permit Assignments (Task #2170) ────────────────────────────────────
// Formal join table replacing the `work_team` text-array workaround.
// Stores per-user, per-role assignments on a permit so:
//   • The "Permit Team" UI can show role badges next to each member.
//   • The PTW step engine can resolve role-based actors for apex demo data.
//   • The PTW console role-aware filtering works correctly per tenant.
//
// A unique index on (permit_id, user_id, role) prevents duplicate assignments.
// The `work_team` column on `ptw_permits` is preserved for backward-compat but
// is superseded by this table for all new writes.

export const PTW_PERMIT_ASSIGNMENT_ROLES = [
  "permit_requestor",
  "permit_issuer",
  "area_authority",
  "isolation_authority",
  "gas_tester",
  "permit_acceptor",
  "performing_authority",
  "hse_officer",
  "lifting_engineer",
] as const;
export type PtwPermitAssignmentRole = (typeof PTW_PERMIT_ASSIGNMENT_ROLES)[number];

export const ptwPermitAssignments = pgTable("ptw_permit_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  userId: text("user_id").notNull(),
  role: text("role").notNull(),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  assignedAt: timestamp("assigned_at", { withTimezone: true }).defaultNow(),
  assignedBy: text("assigned_by"),
}, (t) => ({
  uxPermitUserRole: uniqueIndex("ux_ptw_permit_assignments_permit_user_role").on(t.permitId, t.userId, t.role),
  idxPermitId: index("idx_ptw_permit_assignments_permit").on(t.permitId),
  idxTenantId: index("idx_ptw_permit_assignments_tenant").on(t.tenantId),
  idxUserId: index("idx_ptw_permit_assignments_user").on(t.userId),
}));
export const insertPtwPermitAssignmentSchema = createInsertSchema(ptwPermitAssignments).omit({ id: true, assignedAt: true });
export type InsertPtwPermitAssignment = z.infer<typeof insertPtwPermitAssignmentSchema>;
export type PtwPermitAssignment = typeof ptwPermitAssignments.$inferSelect;

export const PTW_PERMIT_DEPENDENCY_RELATIONSHIPS = [
  "closure_required",
  "issuance_required",
  "mutual_exclusion",
] as const;
export type PtwPermitDependencyRelationship =
  (typeof PTW_PERMIT_DEPENDENCY_RELATIONSHIPS)[number];

export const ptwPermitDependencies = pgTable("ptw_permit_dependencies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  predecessorPermitId: text("predecessor_permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  successorPermitId: text("successor_permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  relationship: text("relationship").notNull(),
  createdBy: text("created_by"),
  createdByName: text("created_by_name"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uqPtwPermitDependency: uniqueIndex("uq_ptw_permit_dependency").on(t.predecessorPermitId, t.successorPermitId, t.relationship),
  idxPtwPermitDependencyTenant: index("idx_ptw_permit_dependency_tenant").on(t.tenantId),
  idxPtwPermitDependencyPredecessor: index("idx_ptw_permit_dependency_predecessor").on(t.predecessorPermitId),
  idxPtwPermitDependencySuccessor: index("idx_ptw_permit_dependency_successor").on(t.successorPermitId),
}));
export const insertPtwPermitDependencySchema = createInsertSchema(ptwPermitDependencies).omit({
  id: true,
  tenantId: true,
  createdAt: true,
});
export type InsertPtwPermitDependency = z.infer<typeof insertPtwPermitDependencySchema>;
export type PtwPermitDependency = typeof ptwPermitDependencies.$inferSelect;

export const ptwStepInstances = pgTable("ptw_step_instances", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  // Task #3464 — FK to ptw_permits.id ON DELETE CASCADE: step instances have no
  // meaning without their parent permit and should be removed with it.
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  stepId: text("step_id").notNull(),
  stepOrder: integer("step_order").notNull(),
  stepName: text("step_name").notNull(),
  stepType: text("step_type").notNull(),
  status: text("status").notNull().default("pending"),
  actorRoles: text("actor_roles").array().default([]),
  assignedTo: text("assigned_to"),
  completedBy: text("completed_by"),
  completedAt: text("completed_at"),
  comments: text("comments"),
  formData: jsonb("form_data").$type<Record<string, any>>().default({}),
  checklistResults: jsonb("checklist_results").$type<{ itemId: string; checked: boolean; value?: string }[]>().default([]),
  attachments: jsonb("attachments").$type<{ name: string; fileId: string; type: string }[]>().default([]),
  dueAt: text("due_at"),
  skipped: boolean("skipped").notNull().default(false),
  skipReason: text("skip_reason"),
  // PTW CAP-4 (Task #1353) — Signature jsonb is sealed by the audit chain
  // (HMAC over the audit row). Per-mode metadata is captured here so the
  // audit row + step instance carry identical envelope fields:
  //   * `mode`  — one of "drawing" | "re_authentication" | "button_declaration".
  //   * `device` — userAgent string at capture time (non-PII forensic crumb).
  //   * `keyId` — for re-auth, the verifying user id; for drawing, the canvas
  //     hash so two rows reusing the same image are distinguishable.
  //   * `reauthChallengeId` — UUID minted by `/api/ptw-v2/verify-reauth`,
  //     bound to the audit row so CAP-5 hardening can rotate / revoke a
  //     specific challenge without affecting the chain.
  //   * `verifiedAt` — ISO timestamp when re-auth was verified.
  //   * `strokeCount` — stroke count for drawing mode (sanity floor).
  //   * `declarationText` — declaration prompt for button_declaration mode.
  signature: jsonb("signature").$type<{
    data: string;
    signedBy: string;
    signedAt: string;
    method?: string;
    mode?: "drawing" | "re_authentication" | "button_declaration";
    device?: string;
    keyId?: string;
    reauthChallengeId?: string;
    verifiedAt?: string;
    strokeCount?: number;
    declarationText?: string;
  }>(),
  returned: boolean("returned").notNull().default(false),
  returnedReason: text("returned_reason"),
  returnedBy: text("returned_by"),
  returnedAt: text("returned_at"),
  // PTW Phase 2.C (Task #1258): see ptw_permits.retainUntil/legalHold above.
  retainUntil: timestamp("retain_until", { withTimezone: true }),
  legalHold: boolean("legal_hold").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // PTW Phase 2.A (Task #1256): hot read path — every permit detail page hydrate
  // and every engine state-machine call lists step instances by permit_id.
  idxPtwStepInstancesPermit: index("idx_ptw_step_instances_permit_id").on(t.permitId),
  // PTW Phase 2.C (Task #1258): hot path for the attachment retention sweep
  // engine — pages step instances where retain_until is past the cutoff per tenant.
  idxPtwStepInstancesTenantRetain: index("idx_ptw_step_instances_tenant_retain").on(t.tenantId, t.retainUntil),
}));
export const insertPtwStepInstanceSchema = createInsertSchema(ptwStepInstances).omit({ tenantId: true });
export type InsertPtwStepInstance = z.infer<typeof insertPtwStepInstanceSchema>;
export type PtwStepInstance = typeof ptwStepInstances.$inferSelect;
export const ptwAuditLog = pgTable("ptw_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Task #3464 — ON DELETE RESTRICT so a tenant cannot be deleted while audit
  // records remain; preserves the compliance audit trail.
  tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "restrict" }),
  // NOTE: ptw_audit_log.permit_id is intentionally NOT a FK to ptw_permits.id
  // because audit entries for template lifecycle operations use a synthetic
  // "template:<id>" permit_id that is not a real permit row. A strict FK would
  // reject those writes. Referential integrity for real permit IDs is enforced
  // at the application layer via createPtwAuditLog.
  permitId: text("permit_id").notNull(),
  stepId: text("step_id"),
  actionType: text("action_type").notNull(),
  // Task #3464 — actorId must not be null; system-initiated actions use the
  // literal 'system' sentinel. Back-filled by migration 0171.
  actorId: text("actor_id").notNull(),
  actorName: text("actor_name"),
  actorRole: text("actor_role"),
  timestamp: text("timestamp").notNull(),
  previousState: text("previous_state"),
  newState: text("new_state"),
  changedFields: text("changed_fields").array().default([]),
  comments: text("comments"),
  attachments: jsonb("attachments").$type<{ name: string; fileId: string }[]>().default([]),
  signatureRef: text("signature_ref"),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  // PTW Phase 6.B (Task #1272) — HMAC tamper-evident audit chain. Each row's
  // `signature` is HMAC-SHA256(AUDIT_HMAC_KEY, prevHash || canonicalRowPayload)
  // and `prevHash` is the previous row's signature (per-permit chain). The
  // first row in a permit's chain stores `prevHash = ''` (empty string).
  // `chainPosition` is the 1-based index in the chain (used by the verifier to
  // detect deletions). All three columns are nullable in the database to allow
  // (a) the rolling backfill (shadow → cutover) of pre-Phase-6.B rows, and
  // (b) fail-soft writes when AUDIT_HMAC_KEY is not configured (the chain is
  // marked unverified rather than blocking the audit write).
  prevHash: text("prev_hash"),
  signature: text("signature"),
  chainPosition: text("chain_position"),
  // PTW CAP-14 (Task #1367) — Multi-Operator / Joint-Venture audit dimension.
  // When an actor signs on behalf of a specific operator (in a JV permit
  // context) the operator's company_id is captured here so cross-operator
  // visibility filters and per-operator report slices can join cleanly.
  // Nullable because pre-CAP-14 rows + single-operator permits don't carry
  // an explicit operator dimension (the implicit operator is the permit's
  // sole operator-of-record).
  operatorId: text("operator_id"),
  // PTW CAP-20 (Task #1373) — Audit-row scope mirrors the parent permit's
  // `mode` (production | training | drill) so cross-permit audit queries can
  // filter / partition rows without re-joining `ptw_permits`. Defaulted to
  // `production` so pre-CAP-20 rows continue to behave as production audit.
  // The chain payload (HMAC) deliberately does NOT include `scope` —
  // changing scope after-the-fact is forbidden anyway, and excluding it
  // means the chain stays compatible with the pre-CAP-20 audit corpus.
  scope: text("scope").notNull().default("production"),
  // PTW CAP-25 (Task #1378) — durable cross-reference into `ptw_signing_keys`.
  // The Phase 6.B audit chain originally HMAC-signed every row under a
  // single platform key (`process.env.AUDIT_HMAC_KEY`). CAP-25 promotes that
  // single key to a registry of (key_id, key_material, status) tuples so a
  // tenant-or-platform key rotation never invalidates historical seals: the
  // verifier reads `signing_key_id` on each row and looks up the matching
  // key (active OR rotated OR revoked) to validate the signature.
  //
  // Pre-CAP-25 rows leave `signing_key_id` NULL — the verifier falls back to
  // `process.env.AUDIT_HMAC_KEY` so legacy chains keep verifying without a
  // backfill. New rows written by `createPtwAuditLog` stamp the active key
  // id at sign-time.
  signingKeyId: text("signing_key_id"),
}, (t) => ({
  idxPtwAuditLogTenantPermitId: index("idx_ptw_audit_log_tenant_permit_id").on(t.tenantId, t.permitId),
  idxPtwAuditLogTenantTimestamp: index("idx_ptw_audit_log_tenant_timestamp").on(t.tenantId, t.timestamp),
  // Task #3603 (409) — per-actor audit filtering ("everything actor X did in
  // this tenant") used by the SA actor drill-down + compliance review.
  idxPtwAuditLogTenantActor: index("idx_ptw_audit_log_tenant_actor").on(t.tenantId, t.actorId),
  // Task #3603 (477) — GIN index on the metadata jsonb so audit searches that
  // filter on metadata keys (e.g. drill_id, operator_id) use an index.
  idxPtwAuditLogMetadataGin: index("idx_ptw_audit_log_metadata_gin").using("gin", t.metadata),
  // PTW Phase 2.A (Task #1256): hot path for Phase 6 audit queries — fetch the
  // chronological audit trail for a single permit (page reads + PDF export).
  // `timestamp` is text-stored ISO 8601 which sorts lexically as if numeric.
  idxPtwAuditLogPermitTimestamp: index("idx_ptw_audit_log_permit_timestamp").on(t.permitId, t.timestamp),
  // PTW Phase 6.B (Task #1272) — verifier read pattern: load a permit's chain
  // ordered by `chain_position`. Index is created CONCURRENTLY at startup by
  // server/schema-drift-fix.ts; declared here so validate-schema does not
  // flag a schema↔DB drift.
  idxPtwAuditLogPermitChainPosition: index("idx_ptw_audit_log_permit_chain_position").on(t.permitId, t.chainPosition),
  // Partial index used by scripts/backfill-ptw-audit-chain.ts to page over
  // permits whose chain still contains un-signed rows. The validator
  // currently surfaces this as a partial index without the WHERE clause —
  // declaring the column-set keeps the introspection contract green.
  // Task #3464 — tenantId leads the index so the backfill script and per-tenant
  // chain-verification queries can page the unsigned-row set without a seqscan.
  idxPtwAuditLogSignaturePresent: index("idx_ptw_audit_log_signature_present").on(t.tenantId, t.permitId),
  // PTW CAP-25 (Task #1378) — verifier read pattern: when validating a
  // historical seal we need to look up the row's signing key. The index is
  // created CONCURRENTLY by the migration so it can land on a live table.
  idxPtwAuditLogSigningKeyId: index("idx_ptw_audit_log_signing_key_id").on(t.signingKeyId),
}));
// Phase 6.B: omit the chain columns from the insert schema. They are computed
// server-side by `createPtwAuditLog` (which fetches the previous row's
// signature under a serialisable lock). Callers MUST NOT supply them.
// CAP-25: also omit `signingKeyId` — the writer stamps it from the active
// key in the registry; callers MUST NOT supply it.
export const insertPtwAuditLogSchema = createInsertSchema(ptwAuditLog).omit({
  tenantId: true,
  prevHash: true,
  signature: true,
  chainPosition: true,
  signingKeyId: true,
});
export type InsertPtwAuditLog = z.infer<typeof insertPtwAuditLogSchema>;
export type PtwAuditLogEntry = typeof ptwAuditLog.$inferSelect;

// PTW Phase 6.B (Task #1272) — Export history.
// Captures every export of audit data (audit-explorer CSV/JSON/PDF, per-permit
// PDF, bulk ZIP) so HSE / Tenant-Admin investigators have an evidence chain
// describing *who* exported *what* and *with which redactions/filters*. This
// surfaces in the Phase 6.A explorer (drill-down Export History tab) and is
// indexed by tenant + creation time for the regulator hand-off bundle.
export const ptwAuditExportHistory = pgTable("ptw_audit_export_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Task #3603 (404) — onDelete normalised to RESTRICT (immutable audit/
  // compliance record): tenant deletion must archive export history first,
  // matching the policy applied to the other PTW audit tables.
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  // `exportType` distinguishes the three export surfaces. PDF / CSV / JSON all
  // record the same `exportType = 'audit_explorer'` because they share filters.
  // `permit_pdf` is a per-permit PDF (the permit + its audit trail). `bulk_zip`
  // is the bulk ZIP export. Future export surfaces extend the enum check below
  // (and the matching CHECK constraint in the migration).
  exportType: text("export_type").notNull(),
  permitId: text("permit_id"),
  format: text("format").notNull(),
  exportedById: text("exported_by_id").notNull(),
  exportedByName: text("exported_by_name"),
  exportedByRole: text("exported_by_role"),
  filters: jsonb("filters").$type<Record<string, unknown>>().default({}),
  redactionProfile: jsonb("redaction_profile").$type<Record<string, unknown>>().default({}),
  rowCount: integer("row_count"),
  byteSize: integer("byte_size"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // Hot read path — drill-down export history is queried per tenant ordered by
  // recency; this composite index supports both the listing and per-permit
  // sub-listing (tenant_id, created_at DESC) used in the Phase 6.A UI.
  idxPtwAuditExportHistoryTenantCreated: index("idx_ptw_audit_export_history_tenant_created")
    .on(t.tenantId, t.createdAt),
  idxPtwAuditExportHistoryTenantPermit: index("idx_ptw_audit_export_history_tenant_permit")
    .on(t.tenantId, t.permitId),
}));
export const insertPtwAuditExportHistorySchema = createInsertSchema(ptwAuditExportHistory).omit({
  id: true,
  createdAt: true,
});
export type InsertPtwAuditExportHistory = z.infer<typeof insertPtwAuditExportHistorySchema>;
export type PtwAuditExportHistory = typeof ptwAuditExportHistory.$inferSelect;
export const ptwPermitAmendments = pgTable("ptw_permit_amendments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  // Task #3464 — FK ON DELETE CASCADE: amendments have no meaning without their
  // parent permit and are removed when the permit is deleted.
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  amendmentType: text("amendment_type").notNull(),
  fieldName: text("field_name").notNull(),
  fieldLabel: text("field_label").notNull().default(""),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  reason: text("reason").notNull(),
  changedBy: text("changed_by").notNull(),
  changedByName: text("changed_by_name").notNull(),
  changedByRole: text("changed_by_role").notNull(),
  approvalStatus: text("approval_status").notNull().default("approved"),
  requiresApproval: boolean("requires_approval").notNull().default(false),
  approverRole: text("approver_role"),
  approvedBy: text("approved_by"),
  approvedByName: text("approved_by_name"),
  approvedAt: text("approved_at"),
  rejectedBy: text("rejected_by"),
  rejectedReason: text("rejected_reason"),
  // Typed rejection timestamp added by migration 0153 (Task #715).
  rejectedAt: timestamp("rejected_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  appliedAt: text("applied_at"),
}, (t) => ({
  // PTW Phase 2.A (Task #1256): hot read path — amendment history for a single
  // permit is fetched on every permit detail render and Phase 2.B (concurrency
  // engine) iterates amendments per permit when applying / version-bumping.
  idxPtwPermitAmendmentsPermit: index("idx_ptw_permit_amendments_permit_id").on(t.permitId),
  // Task #3464 — tenant-scoped listing (amendment dashboard, reporting) and
  // time-ordered per-permit amendment history for the detail panel / PDF export.
  idxPtwPermitAmendmentsTenant: index("ptw_amendments_tenant_idx").on(t.tenantId),
  idxPtwPermitAmendmentsPermitCreatedAt: index("ptw_amendments_permit_created_at_idx").on(t.permitId, t.createdAt),
}));
export const insertPtwPermitAmendmentSchema = createInsertSchema(ptwPermitAmendments).omit({ tenantId: true });
export type InsertPtwPermitAmendment = z.infer<typeof insertPtwPermitAmendmentSchema>;
export type PtwPermitAmendment = typeof ptwPermitAmendments.$inferSelect;
// PTW-AUD-711 — a single point-by-point re-energisation verification item on a
// LOTO isolation point. The graphical isolation register renders these and the
// engine gates safe re-energisation on every mandatory item being verified.
export type PtwIsolationReenergiseItem = {
  item: string;
  verified: boolean;
  verifiedBy?: string | null;
  verifiedAt?: string | null;
};

export const ptwIsolations = pgTable("ptw_isolations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  permitId: text("permit_id").notNull(),
  isolationType: text("isolation_type").notNull(),
  pointOrTag: text("point_or_tag"),
  lockNumber: text("lock_number"),
  // PTW-AUD-711 — graphical LOTO register: per-point padlock/tag tracking and a
  // re-energisation verification checklist. `tagNumber` is the danger-tag id
  // (distinct from `lockNumber`); `isolationDeviceType` is the physical device
  // (padlock, blank, valve, breaker, …); `reenergiseChecklist` is the
  // point-by-point verification list gating safe re-energisation.
  tagNumber: text("tag_number"),
  isolationDeviceType: text("isolation_device_type"),
  reenergiseChecklist: jsonb("reenergise_checklist").$type<PtwIsolationReenergiseItem[]>(),
  placedBy: text("placed_by"),
  verifiedBy: text("verified_by"),
  placedAt: text("placed_at"),
  verifiedAt: text("verified_at"),
  status: text("status").notNull().default("pending"),
  removalRequired: boolean("removal_required").notNull().default(true),
  removalConfirmedBy: text("removal_confirmed_by"),
  removalConfirmedAt: text("removal_confirmed_at"),
  longTermFlag: boolean("long_term_flag").notNull().default(false),
  longTermApprovedBy: text("long_term_approved_by"),
  notes: text("notes"),
}, (t) => ({
  // Task #3464 — tenant-scoped listing (isolation register page) and hot
  // read path for permit detail hydration.
  idxPtwIsolationsTenant: index("ptw_isolations_tenant_idx").on(t.tenantId),
  idxPtwIsolationsPermit: index("ptw_isolations_permit_idx").on(t.permitId),
}));
export const insertPtwIsolationSchema = createInsertSchema(ptwIsolations).omit({ tenantId: true });
export type InsertPtwIsolation = z.infer<typeof insertPtwIsolationSchema>;
export type PtwIsolation = typeof ptwIsolations.$inferSelect;
// CAP-1 — The `ptw_gas_tests` DB table has been retired (Task #1731).
// All runtime CRUD goes through `field_safety_check_records` via the
// projection helpers in `server/storage/ptw/isolations-gas.ts`. The
// types and insert schema below are standalone replacements so the
// legacy API surface continues to compile without a backing Drizzle
// table definition.
export type PtwGasTest = {
  id: string;
  tenantId: string;
  permitId: string;
  testReason: string;
  atmosphereType: string | null;
  readings: { gas: string; value: number; unit: string; threshold: number }[] | null;
  passFail: string;
  instrumentUsed: string | null;
  instrumentCalibratedDate: string | null;
  testerId: string | null;
  testerName: string | null;
  testedAt: string | null;
  retestDueAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  notes: string | null;
};
export const insertPtwGasTestSchema = z.object({
  id: z.string().optional(),
  permitId: z.string(),
  testReason: z.string(),
  atmosphereType: z.string().nullish(),
  readings: z.array(z.object({ gas: z.string(), value: z.number(), unit: z.string(), threshold: z.number() })).nullish(),
  passFail: z.string().default("pending"),
  instrumentUsed: z.string().nullish(),
  instrumentCalibratedDate: z.string().nullish(),
  testerId: z.string().nullish(),
  testerName: z.string().nullish(),
  testedAt: z.string().nullish(),
  retestDueAt: z.string().nullish(),
  reviewedBy: z.string().nullish(),
  reviewedAt: z.string().nullish(),
  notes: z.string().nullish(),
});
export type InsertPtwGasTest = z.infer<typeof insertPtwGasTestSchema>;

// ── Field Safety Check Records (unified, replaces gas_tests & isolation_records for new workflows) ─
export const fieldSafetyCheckRecords = pgTable("field_safety_check_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  // Task #3603 (402) — FK to ptw_permits with ON DELETE CASCADE: a field
  // safety check record has no meaning without its parent permit.
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  checkTypeId: text("check_type_id").notNull(),
  checkTypeName: text("check_type_name").notNull(),
  // Task #3603 (402) — FK to users with ON DELETE RESTRICT: the logger identity
  // is part of the compliance record and must not be silently orphaned.
  loggedBy: text("logged_by").notNull().references(() => users.id, { onDelete: "restrict" }),
  loggedByName: text("logged_by_name"),
  loggedAt: timestamp("logged_at", { withTimezone: true }).defaultNow(),
  formData: jsonb("form_data").$type<Record<string, any>>().default({}),
  passed: boolean("passed"),
  notes: text("notes"),
  signature: jsonb("signature").$type<{ data: string; signedBy: string; signedAt: string; mode: string }>(),
  nextDueAt: timestamp("next_due_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  /** Task #3026 — stamped when the 60-minute expiry warning notification has been sent for this record. */
  expiryWarning60mSentAt: timestamp("expiry_warning_60m_sent_at", { withTimezone: true }),
  /** Task #3026 — stamped when the 15-minute expiry warning notification has been sent for this record. */
  expiryWarning15mSentAt: timestamp("expiry_warning_15m_sent_at", { withTimezone: true }),
}, (t) => ({
  // Task #3603 (402) — the table previously had no indexes. Tenant-scoped permit
  // hydration (permit detail loads its safety-check records) + check-type analytics.
  idxFscrTenantPermit: index("idx_fscr_tenant_permit").on(t.tenantId, t.permitId),
  idxFscrCheckType: index("idx_fscr_check_type").on(t.checkTypeId),
}));
export const insertFieldSafetyCheckRecordSchema = createInsertSchema(fieldSafetyCheckRecords).omit({ tenantId: true, createdAt: true });
export type InsertFieldSafetyCheckRecord = z.infer<typeof insertFieldSafetyCheckRecordSchema>;
export type FieldSafetyCheckRecord = typeof fieldSafetyCheckRecords.$inferSelect;

// ── PTW Work Team Members (Task #3603, finding 407) ────────────────────
// Normalised junction replacing the denormalised ptw_permits.work_team text[]
// for worker membership. The text[] column is RETAINED this task (dual-write)
// and DROPPED in the immediate follow-up once all readers are migrated. Only
// entries that resolve to a real worker are represented here; free-text crew
// labels remain in the legacy array until the follow-up.
export const ptwWorkTeamMembers = pgTable("ptw_work_team_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  workerId: text("worker_id").notNull().references(() => workers.id, { onDelete: "restrict" }),
  addedBy: text("added_by"),
  addedAt: timestamp("added_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  uqPtwWorkTeamMember: uniqueIndex("uq_ptw_work_team_member").on(t.permitId, t.workerId),
  idxPtwWorkTeamMembersPermit: index("idx_ptw_work_team_members_permit").on(t.permitId),
  idxPtwWorkTeamMembersWorker: index("idx_ptw_work_team_members_worker").on(t.workerId),
  idxPtwWorkTeamMembersTenant: index("idx_ptw_work_team_members_tenant").on(t.tenantId),
}));
export const insertPtwWorkTeamMemberSchema = createInsertSchema(ptwWorkTeamMembers).omit({
  id: true,
  addedAt: true,
});
export type InsertPtwWorkTeamMember = z.infer<typeof insertPtwWorkTeamMemberSchema>;
export type PtwWorkTeamMember = typeof ptwWorkTeamMembers.$inferSelect;

// ── PTW Audit Verify Checkpoints (Task #3603, finding 476) ─────────────
// Per-permit high-water-mark for incremental audit-chain verification. The
// verifier records the last verified chain_position + signature so a re-verify
// only walks rows added since the last checkpoint instead of the entire chain.
export const ptwAuditVerifyCheckpoints = pgTable("ptw_audit_verify_checkpoints", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  lastVerifiedPosition: integer("last_verified_position").notNull().default(0),
  lastVerifiedSignature: text("last_verified_signature"),
  verifiedAt: timestamp("verified_at", { withTimezone: true }).defaultNow().notNull(),
  verifiedBy: text("verified_by"),
}, (t) => ({
  uqPtwAuditVerifyCheckpointPermit: uniqueIndex("uq_ptw_audit_verify_checkpoint_permit").on(t.permitId),
  idxPtwAuditVerifyCheckpointTenant: index("idx_ptw_audit_verify_checkpoint_tenant").on(t.tenantId),
}));
export const insertPtwAuditVerifyCheckpointSchema = createInsertSchema(ptwAuditVerifyCheckpoints).omit({
  id: true,
  verifiedAt: true,
});
export type InsertPtwAuditVerifyCheckpoint = z.infer<typeof insertPtwAuditVerifyCheckpointSchema>;
export type PtwAuditVerifyCheckpoint = typeof ptwAuditVerifyCheckpoints.$inferSelect;
export const ptwExportConfigs = pgTable("ptw_export_configs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  templateId: text("template_id").notNull().unique(),
  sections: jsonb("sections").$type<{
    id: string;
    visible: boolean;
    order: number;
  }[]>().default([]),
  branding: jsonb("branding").$type<{
    accentColor?: string;
    logoUrl?: string;
    headerText?: string;
    footerText?: string;
    customWatermarkText?: string;
  }>().default({}),
  preferences: jsonb("preferences").$type<{
    showCredentialBadges?: boolean;
    showEquipmentAssignments?: boolean;
    showAreaHazardContext?: boolean;
    showAuditTrail?: boolean;
    showGasTests?: boolean;
    showIsolations?: boolean;
    showSignatureImages?: boolean;
    showPhotoAttachments?: boolean;
    certificateFormat?: "industrial" | "report";
    includeGasTestTable?: boolean;
    includeIsolationRegister?: boolean;
    includeAuditTrailAppendix?: boolean;
    watermarkTextOverride?: string;
    pageOrientation?: "portrait_a4" | "landscape_a3";
    /**
     * Task #2988: When true, prepends a dedicated cover page (permit summary
     * + QR verification code) before all other content. Defaults to true for
     * industrial certificate format; opt-in for report format.
     */
    includeCoverPage?: boolean;
  }>().default({}),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPtwExportConfigSchema = createInsertSchema(ptwExportConfigs).omit({ tenantId: true, updatedAt: true });
export type InsertPtwExportConfig = z.infer<typeof insertPtwExportConfigSchema>;
export type PtwExportConfig = typeof ptwExportConfigs.$inferSelect;
export const ptwTenantExportDefaults = pgTable("ptw_tenant_export_defaults", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  sections: jsonb("sections").$type<{
    id: string;
    visible: boolean;
    order: number;
  }[]>().default([]),
  branding: jsonb("branding").$type<{
    accentColor?: string;
    headerText?: string;
    footerText?: string;
    customWatermarkText?: string;
    logoUrl?: string;
  }>().default({}),
  preferences: jsonb("preferences").$type<{
    showCredentialBadges?: boolean;
    showEquipmentAssignments?: boolean;
    showAreaHazardContext?: boolean;
    showAuditTrail?: boolean;
    showGasTests?: boolean;
    showIsolations?: boolean;
    showSignatureImages?: boolean;
    showPhotoAttachments?: boolean;
    certificateFormat?: "industrial" | "report";
    includeGasTestTable?: boolean;
    includeIsolationRegister?: boolean;
    includeAuditTrailAppendix?: boolean;
    watermarkTextOverride?: string;
    pageOrientation?: "portrait_a4" | "landscape_a3";
    /** Task #2988: See ptwExportConfigs.preferences.includeCoverPage. */
    includeCoverPage?: boolean;
  }>().default({}),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPtwTenantExportDefaultsSchema = createInsertSchema(ptwTenantExportDefaults).omit({ tenantId: true, updatedAt: true });
export type InsertPtwTenantExportDefaults = z.infer<typeof insertPtwTenantExportDefaultsSchema>;
export type PtwTenantExportDefaults = typeof ptwTenantExportDefaults.$inferSelect;
// ── PTW Verification Tokens (Phase 6.C: lifecycle states + rotation) ────────
//
// `status` lifecycle:
//   active     — currently valid, served as the canonical token for the permit.
//   superseded — replaced by a newer token because a safety-material amendment
//                changed a fact bound to the QR (expiry, hot-work confirmation,
//                isolation status, etc). Old QRs in the field land on a
//                "permit-amended-rescan" public-verifier state so the worker
//                knows to scan the latest copy rather than reject the permit.
//   revoked    — explicitly invalidated (e.g. operator action). Treated like
//                "invalid" by the verifier.
export const ptwVerificationTokens = pgTable("ptw_verification_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id").notNull(),
  token: text("token").notNull().unique(),
  keyId: text("key_id").notNull(),
  status: text("status").notNull().default("active"),
  supersededAt: timestamp("superseded_at", { withTimezone: true }),
  supersededByTokenId: text("superseded_by_token_id"),
  supersededReason: text("superseded_reason"),
  amendmentId: text("amendment_id"),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  revokedReason: text("revoked_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  // Phase 6.C — index supports the hot lookup
  // `getPtwActiveVerificationTokenForPermit` (permit_id + status='active')
  // and the bulk-supersede sweep on amendment.
  permitStatusIdx: index("idx_ptw_verification_tokens_permit_status").on(table.permitId, table.status),
}));
export const insertPtwVerificationTokenSchema = createInsertSchema(ptwVerificationTokens).omit({ id: true, createdAt: true });
export type InsertPtwVerificationToken = z.infer<typeof insertPtwVerificationTokenSchema>;
export type PtwVerificationToken = typeof ptwVerificationTokens.$inferSelect;

// ── PTW Tenant Privacy Config (Phase 6.C: public verifier whitelist) ────────
//
// The public verifier must NOT leak privacy-sensitive worker / company data
// to anyone who scans a QR code (e.g. emails, phone numbers, internal IDs).
// This table captures per-tenant overrides on top of a default-secure
// platform baseline (defined in server/engines/ptw-engine.ts as
// `PUBLIC_VERIFIER_DEFAULT_WHITELIST`). Each known field key maps to a
// boolean — `true` exposes the field in the public verifier render, `false`
// hides it. Unknown keys are ignored (never override the default).
export const ptwTenantPrivacyConfig = pgTable("ptw_tenant_privacy_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  fieldOverrides: jsonb("field_overrides").$type<Record<string, boolean>>().notNull().default({}),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertPtwTenantPrivacyConfigSchema = createInsertSchema(ptwTenantPrivacyConfig).omit({
  id: true,
  tenantId: true,
  updatedAt: true,
  createdAt: true,
});
export type InsertPtwTenantPrivacyConfig = z.infer<typeof insertPtwTenantPrivacyConfigSchema>;
export type PtwTenantPrivacyConfig = typeof ptwTenantPrivacyConfig.$inferSelect;

// ── Safety-material amendable fields (Phase 6.C: QR rotation triggers) ─────
//
// When an amendment changes any of these fields on an issued/active permit,
// the public-verifier QR token is rotated so an out-of-date QR cannot be
// presented as if it represented the current safety state of the permit.
// The list intentionally errs on the side of safety: scope (work team /
// description / location), hazards & controls, expiry, and gas/isolation
// flags all rotate.
export const PTW_SAFETY_MATERIAL_AMENDMENT_FIELDS = [
  "issuedExpiryTime",
  "issuedStartTime",
  "workLocation",
  "workDescription",
  "workType",
  "hazards",
  "controlMeasures",
  "ppeRequirements",
  "workTeam",
  "numberOfWorkers",
  "riskCategory",
  "gasTestRequired",
  "isolationRequired",
] as const;
export type PtwSafetyMaterialAmendmentField = (typeof PTW_SAFETY_MATERIAL_AMENDMENT_FIELDS)[number];

// ── Public verifier render fields (Phase 6.C: privacy whitelist baseline) ──
//
// The full canonical list of field keys the public verifier may render. The
// PUBLIC_VERIFIER_DEFAULT_WHITELIST in the engine flips a default boolean
// per key; tenants may override per-key via `ptw_tenant_privacy_config`.
export const PTW_PUBLIC_VERIFIER_FIELD_KEYS = [
  "permitNumber",
  "status",
  "workType",
  "workLocation",
  "workDescription",
  "requestorName",
  "performingCompany",
  "requestedStartTime",
  "requestedEndTime",
  "issuedStartTime",
  "issuedExpiryTime",
  "actualWorkStartTime",
  "actualWorkEndTime",
  "closedTime",
  "closureOutcome",
  "signatories",
  "permitId",
  "tenantId",
] as const;
export type PtwPublicVerifierFieldKey = (typeof PTW_PUBLIC_VERIFIER_FIELD_KEYS)[number];
export const ptwVerificationScans = pgTable("ptw_verification_scans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tokenId: text("token_id"),
  permitId: text("permit_id"),
  // PTW Phase 2.A (Task #1256): tenant_id is NOT NULL with FK to tenants(id).
  // Anonymous-token failure paths (malformed / invalid_key / invalid_type) no
  // longer persist a scan row — the public verify handler skips persistence
  // when no tenant can be resolved (the tenant binding is in the JWT payload
  // or the matched ptw_verification_tokens row). Pre-existing orphan rows
  // are deleted by scripts/backfill-ptw-verification-scans-tenant.ts.
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  result: text("result").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  scannedAt: timestamp("scanned_at", { withTimezone: true }).defaultNow(),
});
export const insertPtwVerificationScanSchema = createInsertSchema(ptwVerificationScans).omit({ id: true, scannedAt: true });
export type InsertPtwVerificationScan = z.infer<typeof insertPtwVerificationScanSchema>;
export type PtwVerificationScan = typeof ptwVerificationScans.$inferSelect;
/**
 * Canonical store for in-permit comments (PTW CAP-2 / Task #1351).
 *
 * This typed table is the single source of truth for all permit comments
 * surfaced on the unified permit Detail panel and the Audit panel. It
 * carries per-row ids (so write events can be audited), mention chips
 * (`mentioned_roles`), read-receipts (`read_by`), and is tenant-scoped
 * via the FK below — none of which the legacy `ptw_permits.comments`
 * jsonb blob can support. The legacy column is retained only until the
 * follow-up retire migration drops it; do not write to it.
 *
 * Reads/writes go exclusively through the storage helpers in
 * `server/storage/ptw.ts` (`getPtwCommentsByPermit`, `createPtwComment`,
 * `markPtwCommentRead`), which are wired into `data.record_*` audit
 * events for the audit-events surface.
 */
export const ptwPermitComments = pgTable("ptw_permit_comments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "restrict" }),
  // Task #3464 — FK ON DELETE CASCADE: comments have no meaning without their
  // parent permit and are removed when the permit is deleted.
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  authorId: text("author_id").notNull(),
  authorName: text("author_name").notNull(),
  authorRole: text("author_role"),
  text: text("text").notNull(),
  mentionedRoles: text("mentioned_roles").array().default([]),
  readBy: text("read_by").array().default([]),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // Task #3464 — tenant-scoped listing (comment feed per tenant) and
  // chronological per-permit comment thread for the detail panel.
  idxPtwPermitCommentsTenant: index("ptw_comments_tenant_idx").on(t.tenantId),
  idxPtwPermitCommentsPermitCreatedAt: index("ptw_comments_permit_created_at_idx").on(t.permitId, t.createdAt),
}));
export const insertPtwPermitCommentSchema = createInsertSchema(ptwPermitComments).omit({ id: true, tenantId: true, createdAt: true });
export type InsertPtwPermitComment = z.infer<typeof insertPtwPermitCommentSchema>;
export type PtwPermitComment = typeof ptwPermitComments.$inferSelect;

// ─── SIMOPS Reviews ──────────────────────────────────────────────────
// PTW Phase 7.B (Task #1276): adds the "accept-with-mitigation" decision
// captured by the new visual board flow. The legacy `approved` status
// remains for back-compat; `accepted_with_mitigation` is a distinct
// terminal state that records the signing actor + a single mitigation
// note alongside the existing free-form mitigations array. The five
// `acceptedWithMitigation*` columns are the audit-grade snapshot of
// "who pressed the button, when, and why" — they are also written into
// every conflicting permit's audit chain via
// `acceptSimopsReviewWithMitigation`.
export const SIMOPS_STATUSES = ["draft", "reviewed", "approved", "accepted_with_mitigation"] as const;
export type SimopsStatus = (typeof SIMOPS_STATUSES)[number];

export const simopsReviews = pgTable("simops_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  siteId: text("site_id"),
  reviewDate: text("review_date").notNull(),
  coordinatorId: text("coordinator_id"),
  conflictingPermitIds: text("conflicting_permit_ids").array().default([]),
  mitigations: jsonb("mitigations").$type<{ description: string; owner: string; completedAt?: string }[]>().default([]),
  status: text("status").notNull().default("draft"),
  notes: text("notes"),
  // PTW Phase 7.B — accept-with-mitigation decision snapshot. Populated
  // atomically by `acceptSimopsReviewWithMitigation`; nullable so the
  // legacy "draft → reviewed → approved" flow is unaffected.
  mitigationNote: text("mitigation_note"),
  acceptedWithMitigationAt: timestamp("accepted_with_mitigation_at", { withTimezone: true }),
  acceptedWithMitigationByUserId: text("accepted_with_mitigation_by_user_id"),
  acceptedWithMitigationByName: text("accepted_with_mitigation_by_name"),
  acceptedWithMitigationByRole: text("accepted_with_mitigation_by_role"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // PTW Phase 7.B (Task #1276): supports the conflict-trend dashboards which
  // page through accept-with-mitigation events per-tenant on a time axis.
  idxSimopsReviewsAcceptedAt: index("idx_simops_reviews_accepted_at")
    .on(t.acceptedWithMitigationAt, t.tenantId),
}));
export const insertSimopsReviewSchema = createInsertSchema(simopsReviews)
  .omit({
    id: true,
    tenantId: true,
    createdAt: true,
    updatedAt: true,
    mitigationNote: true,
    acceptedWithMitigationAt: true,
    acceptedWithMitigationByUserId: true,
    acceptedWithMitigationByName: true,
    acceptedWithMitigationByRole: true,
  })
  .extend({ status: z.enum(SIMOPS_STATUSES).default("draft") });
export type InsertSimopsReview = z.infer<typeof insertSimopsReviewSchema>;
export type SimopsReview = typeof simopsReviews.$inferSelect;

// PTW Phase 7.B — body schema for `POST /api/ptw/simops-reviews/:id/accept-with-mitigation`.
// `mitigationNote` is mandatory because the whole point of this flow is to
// capture WHY the conflicting permits are safe to run concurrently.
export const acceptSimopsReviewWithMitigationSchema = z.object({
  mitigationNote: z.string().trim().min(8, "Mitigation note must be at least 8 characters").max(4000),
  additionalMitigations: z
    .array(
      z.object({
        description: z.string().trim().min(1).max(500),
        owner: z.string().trim().min(1).max(120),
        completedAt: z.string().optional(),
      }),
    )
    .max(20)
    .optional(),
});
export type AcceptSimopsReviewWithMitigationInput = z.infer<typeof acceptSimopsReviewWithMitigationSchema>;

// ─── Hot Work Gas Tests — standalone types (Task #1731) ───────────────
// CAP-1 — The `ptw_hot_work_gas_tests` DB table has been retired. All
// runtime CRUD goes through `field_safety_check_records` (checkTypeId
// 'hot_work_gas_test') via the projection helpers in
// `server/storage/ptw/isolations-gas.ts` and `export-comments.ts`.
export type PtwHotWorkGasTest = {
  id: string;
  tenantId: string;
  permitId: string;
  testTime: Date;
  o2Percent: string | null;
  lelPercent: string | null;
  h2sPpm: string | null;
  co2Percent: string | null;
  recordedByUserId: string | null;
  notes: string | null;
  createdAt: Date | null;
};
export const insertPtwHotWorkGasTestSchema = z.object({
  permitId: z.string(),
  testTime: z.union([z.date(), z.string().min(1).transform((v) => new Date(v))]),
  o2Percent: z.union([z.string(), z.number()]).optional().transform((v) => v != null ? String(v) : undefined),
  lelPercent: z.union([z.string(), z.number()]).optional().transform((v) => v != null ? String(v) : undefined),
  h2sPpm: z.union([z.string(), z.number()]).optional().transform((v) => v != null ? String(v) : undefined),
  co2Percent: z.union([z.string(), z.number()]).optional().transform((v) => v != null ? String(v) : undefined),
  recordedByUserId: z.string().nullish(),
  notes: z.string().nullish(),
});
export type InsertPtwHotWorkGasTest = z.infer<typeof insertPtwHotWorkGasTestSchema>;

// ─── PTW Phase 2.B (Task #1257) — Action Idempotency Cache ──────────────
// Stores the (request_hash, response_body) of every state-changing engine
// action keyed by `(tenant_id, key)`. Duplicate submissions (network retry,
// double-tap, offline-queue replay) detected by `key` collision return the
// cached response instead of re-running the action — guaranteeing exactly-
// once semantics from the client's perspective. This is the contract Phase 10
// (Offline Queue) builds on.
//
// `request_hash` lets the engine reject "same key, different payload" replays
// as 409 mismatches; `response_status` + `response_body` reproduce the
// original HTTP envelope on replay. Rows are insert-only and pruned by an
// out-of-band sweeper (Phase 10) once they fall out of the offline-queue
// retention window.
export const ptwActionIdempotency = pgTable("ptw_action_idempotency", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id").notNull(),
  key: text("key").notNull(),
  requestHash: text("request_hash").notNull(),
  responseHash: text("response_hash").notNull(),
  responseStatus: integer("response_status").notNull().default(200),
  responseBody: jsonb("response_body").$type<unknown>(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  // Uniqueness on (tenant_id, key) so two tenants can independently use the
  // same client-generated UUID without collision. Created CONCURRENTLY at
  // startup by server/schema-drift-fix.ts (CREATE INDEX inside the migration
  // would trip the migration-safety lock-check rule).
  uniqIdempotencyTenantKey: uniqueIndex("uniq_ptw_action_idempotency_tenant_key").on(t.tenantId, t.key),
  idxIdempotencyPermit: index("idx_ptw_action_idempotency_permit").on(t.permitId),
  idxIdempotencyCreatedAt: index("idx_ptw_action_idempotency_created_at").on(t.createdAt),
}));
export const insertPtwActionIdempotencySchema = createInsertSchema(ptwActionIdempotency).omit({ id: true, createdAt: true });
export type InsertPtwActionIdempotency = z.infer<typeof insertPtwActionIdempotencySchema>;
export type PtwActionIdempotency = typeof ptwActionIdempotency.$inferSelect;

// ─── PTW ↔ Shift Handover Link Table (PTW Phase 2.D, Task #1259) ──────
//
// Hard-relational link between a `shift_handovers` record and the PTW
// permit (or PTW step instance) it communicates context about. Replaces
// the application-resolved `shift_handovers.active_permit_ids` text array
// with foreign keys both ways so safety-critical handover instructions
// cannot be silently lost when permit/step IDs change or are deleted.
//
// Cascade strategy:
//   * handover_id      → ON DELETE CASCADE  (link is owned by the handover; deleting
//                                            the handover removes its links)
//   * permit_id        → ON DELETE CASCADE  (link is meaningless without its permit;
//                                            deleting a permit removes its handover
//                                            links — note that PTW lifecycle uses
//                                            "close" rather than DELETE for normal
//                                            operations, so this only fires on hard
//                                            archival deletes)
//   * step_instance_id → ON DELETE CASCADE  (same rationale as permit_id; step
//                                            instances are removed when the parent
//                                            permit is hard-deleted)
//   * tenant_id        → references tenants(id), notNull (tenant isolation)
//
// At least one of (permit_id, step_instance_id) MUST be set. This is enforced
// in the storage layer (createShiftHandoverPermitLink) and re-asserted by the
// migration's CHECK constraint `shift_handover_permits_target_present`.
//
// Phase 5.C dependency contract:
//   * `getOutstandingHandoverPermitLinks({ supervisorId })` returns links where
//     `acknowledged_at IS NULL` joined to handovers whose outgoing supervisor
//     matches. Phase 5.C consumes this for the unresolved-handover badge on
//     the supervisor permit card.
//   * `getHandoverHistoryForPermit(permitId)` returns the chronological link
//     history (ordered by handover.handover_time DESC) for a given permit.
//     Phase 5.C consumes this for the handover history pop-over.
//   * `acknowledgeShiftHandoverPermitLink(linkId, userId)` stamps
//     `acknowledged_at = now()` and `acknowledged_by_user_id = userId`. It is
//     idempotent — calling it on an already-acknowledged link is a no-op.
export const shiftHandoverPermits = pgTable("shift_handover_permits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  handoverId: text("handover_id").notNull().references(() => shiftHandovers.id, { onDelete: "cascade" }),
  // Either permit_id or step_instance_id (or both) must be set; enforced by
  // CHECK constraint shift_handover_permits_target_present in the migration
  // and by the storage helper createShiftHandoverPermitLink at write time.
  permitId: text("permit_id").references(() => ptwPermits.id, { onDelete: "cascade" }),
  stepInstanceId: text("step_instance_id").references(() => ptwStepInstances.id, { onDelete: "cascade" }),
  // low | medium | high — surfaces as the badge color in Phase 5.C UI.
  criticality: text("criticality").notNull().default("medium"),
  note: text("note"),
  // NULL until the incoming supervisor acknowledges; Phase 5.C surfaces
  // unresolved (NULL) links as the handover badge on the permit card.
  acknowledgedAt: timestamp("acknowledged_at", { withTimezone: true }),
  acknowledgedByUserId: text("acknowledged_by_user_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // Hot read paths:
  //   - Phase 5.C handover history pop-over: list links by permit_id
  //   - Phase 5.C unresolved badge: list links by handover_id
  //   - Tenant-scoped admin lookups: list by tenant
  idxShiftHandoverPermitsHandover: index("idx_shift_handover_permits_handover_id").on(t.handoverId),
  idxShiftHandoverPermitsPermit: index("idx_shift_handover_permits_permit_id").on(t.permitId),
  idxShiftHandoverPermitsStepInstance: index("idx_shift_handover_permits_step_instance_id").on(t.stepInstanceId),
  idxShiftHandoverPermitsTenantUnack: index("idx_shift_handover_permits_tenant_unack").on(t.tenantId, t.acknowledgedAt),
}));
export const insertShiftHandoverPermitSchema = createInsertSchema(shiftHandoverPermits, {
  criticality: () => z.enum(["low", "medium", "high"]).default("medium"),
}).omit({ id: true, tenantId: true, createdAt: true, acknowledgedAt: true, acknowledgedByUserId: true });
export type InsertShiftHandoverPermit = z.infer<typeof insertShiftHandoverPermitSchema>;
export type ShiftHandoverPermit = typeof shiftHandoverPermits.$inferSelect;

/**
 * PTW CAP-12 (Task #1361) — Attachments Bucket Governance.
 *
 * Sidecar for every object stored in the `ptw-attachments` bucket
 * (`server/utils/object-storage.ts`). The Replit `@replit/object-storage`
 * client does not expose an arbitrary-metadata API on objects (no
 * S3-style `x-amz-meta-*` headers and no S3 Object Lock primitive), so
 * the governance metadata that an S3 deployment would attach to the
 * object header lives here instead. The contract is identical:
 *
 *   * `retain_until` is the per-object purge horizon. The PTW retention
 *     sweep engine in `server/services/ptw-attachment-retention.ts`
 *     refuses to delete the underlying bucket object before this
 *     timestamp, exactly the way an S3 Object-Lock retention policy
 *     would block a `DeleteObject` call.
 *   * `legal_hold = true` is a hard block: the sweep MUST skip the
 *     physical delete regardless of `retain_until`. Mirrors S3
 *     Object-Lock legal-hold semantics. Settable / clearable per object
 *     so a single attachment can be frozen for an investigation
 *     without freezing every other attachment in the tenant.
 *
 * Every column on this table is governance metadata about the bucket
 * object — never a copy of the file contents themselves. The file
 * remains in the bucket; this row tells the engine what to do with it.
 *
 * Storage discipline:
 *   - Created in the same transaction as the upload via
 *     `uploadPtwAttachment` (server/utils/object-storage.ts) so the
 *     sidecar row and the bucket object are committed atomically. If
 *     the bucket upload succeeds but the sidecar insert fails, the
 *     bucket object is rolled back via a compensating delete.
 *   - The retention sweep deletes the bucket object FIRST, then the
 *     sidecar row, both inside one transaction — so a crash mid-sweep
 *     can never leave a tenant accountable for an attachment that
 *     was already physically purged.
 *
 * The legal-hold and retention-horizon columns mirror the per-row
 * `retain_until` / `legal_hold` columns on `ptw_permits` and
 * `ptw_step_instances` (Phase 2.C, Task #1258), but those are tied to
 * the permit row's lifecycle while THIS row is tied to the bucket
 * object's lifecycle. A permit may be deleted while attachments live
 * on under retention; conversely an attachment may be on legal hold
 * even after its owning permit has been hard-purged.
 *
 * This sidecar is the contract that any future "standalone-mode"
 * attachment store (PTW backlog A8.9) MUST consume — the sidecar
 * row carries the canonical governance metadata regardless of which
 * physical store backs the bytes.
 */
export const ptwAttachmentObjects = pgTable("ptw_attachment_objects", {
  // The object key in the bucket (e.g. "ptw-attachments/1714512345-abc.pdf").
  // Acts as the primary key — every distinct bucket object has exactly one
  // sidecar row, and the bucket key is the only handle a download path has
  // to look the row up.
  objectKey: text("object_key").primaryKey(),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // Owning permit / step instance when known at upload time. Both nullable
  // because the upload endpoint (`POST /api/uploads`) accepts files BEFORE
  // they are bound to a permit (the wizard uploads the file, then writes
  // the resulting fileId into the step's attachments array on submit).
  // Backfill happens via `bindAttachmentObjectToPermit` after the bind.
  permitId: text("permit_id"),
  stepInstanceId: text("step_instance_id"),
  // Best-effort uploader identity captured at upload time. Persisted so
  // the audit row for a download can resolve "uploaded by X / accessed by Y"
  // without a second join.
  uploadedById: text("uploaded_by_id"),
  uploadedByName: text("uploaded_by_name"),
  uploadedByRole: text("uploaded_by_role"),
  originalName: text("original_name"),
  mimeType: text("mime_type"),
  sizeBytes: integer("size_bytes"),
  // Same governance contract as ptw_permits.retain_until / legal_hold above.
  retainUntil: timestamp("retain_until", { withTimezone: true }),
  legalHold: boolean("legal_hold").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  // Touched on every successful serve so the SA UI can spot
  // never-accessed objects ripe for early purge.
  lastAccessedAt: timestamp("last_accessed_at", { withTimezone: true }),

  // ─── PTW CAP-17 (Task #1370) — Rich Mobile Evidence provenance ─────
  //
  // The mobile EvidencePicker captures field-side evidence under harsh
  // conditions and persists provenance metadata that an investigator,
  // auditor, or public verifier can use to trust the evidence later. The
  // EvidencePicker component (client/src/components/ptw/EvidencePicker.tsx)
  // and the offline queue (`client/src/lib/ptw-offline-queue.ts`) populate
  // these columns from device sensors at capture time, NOT at upload time —
  // the device may be offline for hours between capture and the eventual
  // POST /api/uploads that lands the bytes in the bucket.
  //
  // None of the columns below are required (every legacy upload path
  // remains valid) but populated rows light up the audit trail's
  // "Captured at 14:02 by X, GPS 23.604°N 58.456°E ±8 m, on iPhone 14"
  // banner and let the retention sweep / public verifier reason about
  // the capture context.
  //
  //   * `capture_kind`       — "camera" | "voice" | "video" | "file"
  //   * `device_id`          — opaque per-device id (random UUID stored in
  //                            localStorage on first launch). Lets a tenant
  //                            spot "all evidence on this permit came from
  //                            the same handset" without exposing PII.
  //   * `captured_at`        — wall-clock time the device captured the
  //                            evidence; distinct from `created_at` which
  //                            is the row insert time on the server.
  //   * `geo_lat`/`geo_lon`  — WGS-84 decimal degrees from the browser's
  //                            Geolocation API at capture time.
  //   * `geo_accuracy_m`     — claimed accuracy radius in metres (the
  //                            audit row renders this as "±8 m").
  //   * `network_state`      — "online" if uploaded immediately,
  //                            "offline_queued" if it sat in IndexedDB
  //                            until the Phase 10.B sync engine drained
  //                            it.
  //   * `exif`               — jsonb capture metadata: `camera`, `width`,
  //                            `height`, `orientation`, plus any EXIF
  //                            fields the browser exposed.
  //   * `burst_group_id`     — UUID shared by every photo in a single
  //                            tap-and-hold burst (up to 5 photos per
  //                            spec). Lets the Detail UI render bursts
  //                            as one collapsible bundle.
  //   * `transcript`         — voice-note transcript (when the
  //                            `ptw_voice_transcribe` feature flag is
  //                            on). NULL otherwise.
  //   * `transcript_status`  — "pending" | "complete" | "failed" |
  //                            "disabled". Lets the UI show a spinner /
  //                            error / dash without an extra column.
  captureKind: text("capture_kind"),
  deviceId: text("device_id"),
  capturedAt: timestamp("captured_at", { withTimezone: true }),
  geoLat: numeric("geo_lat", { precision: 10, scale: 7 }),
  geoLon: numeric("geo_lon", { precision: 10, scale: 7 }),
  geoAccuracyM: numeric("geo_accuracy_m", { precision: 10, scale: 2 }),
  networkState: text("network_state"),
  exif: jsonb("exif").$type<Record<string, unknown>>(),
  burstGroupId: text("burst_group_id"),
  transcript: text("transcript"),
  transcriptStatus: text("transcript_status"),
  // Task #3467 — SHA-256 hex digest of the raw file bytes, computed at upload
  // time and stored so the regulator audit pack manifest can include a
  // verifiable content hash without a second read from object storage.
  sha256: text("sha256"),
  // Task #3603 (453) — attachment version lineage. `supersedesId` points to the
  // object key this upload replaces (NULL for first version); `version` is the
  // 1-based revision number; `deletedAt` is a soft-delete tombstone so a
  // replaced/removed attachment stays auditable instead of vanishing.
  supersedesId: text("supersedes_id"),
  version: integer("version").notNull().default(1),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
}, (t) => ({
  // Per-tenant retention sweep pagination — list candidates for purge
  // ordered by retain_until per tenant.
  idxPtwAttachmentObjectsTenantRetain: index("idx_ptw_attachment_objects_tenant_retain").on(t.tenantId, t.retainUntil),
  // Permit-detail render path — list every attachment object for one permit
  // (e.g. legal-hold flag toggle UI, "this permit has 3 held attachments").
  idxPtwAttachmentObjectsPermit: index("idx_ptw_attachment_objects_permit_id").on(t.permitId),
  // Burst grouping render path — fetch every photo in a burst.
  idxPtwAttachmentObjectsBurst: index("idx_ptw_attachment_objects_burst").on(t.burstGroupId),
  // Task #3603 (453) — version-lineage lookup ("show me the chain of revisions").
  idxPtwAttachmentObjectsSupersedes: index("idx_ptw_attachment_objects_supersedes").on(t.supersedesId),
}));
export const insertPtwAttachmentObjectSchema = createInsertSchema(ptwAttachmentObjects).omit({
  createdAt: true,
  lastAccessedAt: true,
});
export type InsertPtwAttachmentObject = z.infer<typeof insertPtwAttachmentObjectSchema>;
export type PtwAttachmentObject = typeof ptwAttachmentObjects.$inferSelect;

/**
 * PTW CAP-17 (Task #1370) — Per-tenant evidence-capture toggles.
 *
 * Tenant admin can disable any of the four EvidencePicker capture paths
 * (camera / voice / video / file) and the photo-burst sub-mode. Disabling
 * does NOT remove already-captured evidence; it only hides the relevant
 * button on the EvidencePicker so future captures are blocked at the UI
 * layer. The standalone `ptw_voice_transcribe` flag is a tenant feature
 * (server-side gate in the upload route) — when off, voice notes still
 * upload but no transcription job is enqueued and `transcript_status`
 * lands as `disabled`.
 *
 * The row is upserted by tenant admins via
 * `PATCH /api/ptw-v2/tenant-evidence-capture-settings`. A missing row is
 * equivalent to "all toggles on" so a tenant that has never visited the
 * settings page sees the full EvidencePicker out of the box.
 */
export const ptwEvidenceCaptureSettings = pgTable("ptw_evidence_capture_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  allowCamera: boolean("allow_camera").notNull().default(true),
  allowVoice: boolean("allow_voice").notNull().default(true),
  allowVideo: boolean("allow_video").notNull().default(true),
  allowFile: boolean("allow_file").notNull().default(true),
  allowBurst: boolean("allow_burst").notNull().default(true),
  voiceTranscribeEnabled: boolean("voice_transcribe_enabled").notNull().default(false),
  /** Maximum photos in a single tap-and-hold burst. Spec is 5. */
  burstMaxPhotos: integer("burst_max_photos").notNull().default(5),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});
export const insertPtwEvidenceCaptureSettingsSchema = createInsertSchema(ptwEvidenceCaptureSettings).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwEvidenceCaptureSettings = z.infer<typeof insertPtwEvidenceCaptureSettingsSchema>;
export type PtwEvidenceCaptureSettings = typeof ptwEvidenceCaptureSettings.$inferSelect;

/** Default settings returned when a tenant has no row yet. */
export const PTW_DEFAULT_EVIDENCE_CAPTURE_SETTINGS = {
  allowCamera: true,
  allowVoice: true,
  allowVideo: true,
  allowFile: true,
  allowBurst: true,
  voiceTranscribeEnabled: false,
  burstMaxPhotos: 5,
} as const;

/** Capture kinds recognised by the EvidencePicker + provenance column. */
export const PTW_EVIDENCE_CAPTURE_KINDS = ["camera", "voice", "video", "file"] as const;
export type PtwEvidenceCaptureKind = (typeof PTW_EVIDENCE_CAPTURE_KINDS)[number];

/** Recognised values for `network_state` on the provenance column. */
export const PTW_EVIDENCE_NETWORK_STATES = ["online", "offline_queued"] as const;
export type PtwEvidenceNetworkState = (typeof PTW_EVIDENCE_NETWORK_STATES)[number];

/** Recognised values for `transcript_status`. */
export const PTW_EVIDENCE_TRANSCRIPT_STATUSES = [
  "pending",
  "complete",
  "failed",
  "disabled",
] as const;
export type PtwEvidenceTranscriptStatus = (typeof PTW_EVIDENCE_TRANSCRIPT_STATUSES)[number];

// ─── PTW CAP-5 (Task #1354) — Re-Auth Endpoint Hardening ────────────
//
// Two append-only tables underpin the hardened password-based re-auth
// path used by the PTW signature flow (`/api/ptw-v2/verify-reauth`):
//
//   ptw_reauth_challenges — every challenge id minted by the endpoint
//     is persisted with its TTL (5 min) and its binding context (actor +
//     optional permit + optional step instance + optional permit version).
//     `consumed_at` is stamped atomically when the engine seals an action
//     with that signature; replay returns 409 (`reason="consumed"`).
//     Records also carry the `auth_method` discriminator so a downstream
//     auditor can tell a vanilla password attempt from a
//     `password_fallback_with_workos_session` attempt without joining
//     against `sso_connections`.
//
//   ptw_reauth_attempts   — one row per actor, atomically updated on
//     every attempt. `failure_count` resets on success; the row tracks
//     `last_failure_at`, `lockout_count` (how many lockouts have
//     accumulated for the actor — drives exponential backoff), and
//     `locked_until` (the absolute unlock timestamp). The lockout
//     thresholds default to OWASP guidance (5 failed attempts → 15min
//     lock, doubling per repeat lockout up to a cap) and are
//     tenant-configurable via `ptw_tenant_reauth_policy` (env-backed
//     today; tenant table is a follow-up in CAP-19).
//
// Both tables are intentionally append-only / upsert-only — no row is
// ever deleted by the runtime; an opportunistic janitor (run by
// `purgeExpiredReauthChallenges`) prunes consumed/expired challenges
// older than 24 hours so the table stays bounded without losing the
// short-term audit trail.
export const ptwReauthChallenges = pgTable("ptw_reauth_challenges", {
  id: varchar("id").primaryKey(),
  tenantId: text("tenant_id"),
  actorId: text("actor_id").notNull(),
  // Auth method recorded at issue time. One of:
  //   - "password"
  //   - "password_fallback_with_workos_session"
  //   - "webauthn_passkey"           (CAP-19 — passkey assertion)
  //   - "webauthn_passkey_uv"        (CAP-19 — passkey w/ user verification)
  //   - "workos_step_up"             (CAP-19 — WorkOS-issued step-up)
  authMethod: text("auth_method").notNull(),
  // Optional binding context. When supplied at challenge creation, the
  // engine MUST present matching values at consume time or the consume
  // is rejected with `reason="binding_mismatch"`. When omitted, the
  // challenge is bound only to the actor (legacy callers).
  permitId: text("permit_id"),
  permitVersion: integer("permit_version"),
  stepInstanceId: text("step_instance_id"),
  // Forensic crumbs — best-effort, never trusted as auth signal.
  ipAddress: text("ip_address"),
  deviceFingerprint: text("device_fingerprint"),
  userAgent: text("user_agent"),
  workosSessionPresent: boolean("workos_session_present").notNull().default(false),
  issuedAt: timestamp("issued_at", { withTimezone: true }).notNull().defaultNow(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  consumedAt: timestamp("consumed_at", { withTimezone: true }),
  consumedForAction: text("consumed_for_action"),
  consumedByPermitVersion: integer("consumed_by_permit_version"),
}, (t) => ({
  idxPtwReauthChallengesActorIssued: index("idx_ptw_reauth_challenges_actor_issued").on(t.actorId, t.issuedAt),
  idxPtwReauthChallengesExpires: index("idx_ptw_reauth_challenges_expires").on(t.expiresAt),
  idxPtwReauthChallengesPermit: index("idx_ptw_reauth_challenges_permit").on(t.permitId),
}));
export const insertPtwReauthChallengeSchema = createInsertSchema(ptwReauthChallenges).omit({
  consumedAt: true,
  consumedForAction: true,
  consumedByPermitVersion: true,
});
export type InsertPtwReauthChallenge = z.infer<typeof insertPtwReauthChallengeSchema>;
export type PtwReauthChallenge = typeof ptwReauthChallenges.$inferSelect;

export const ptwReauthAttempts = pgTable("ptw_reauth_attempts", {
  // Single row per actor — actorId is the natural primary key.
  actorId: text("actor_id").primaryKey(),
  // Last tenant context observed for this actor's re-auth attempt.
  // Intentionally nullable: lockouts and counters are per-actor across
  // all tenants (a brute-force adversary cannot escape lockout simply
  // by switching active tenant). The column exists for forensic
  // pairing with audit rows and to satisfy tenant-coverage tooling.
  tenantId: text("tenant_id"),
  // Number of *consecutive* failures since the last success or unlock.
  // Reset to 0 on success or after `locked_until` elapses.
  failureCount: integer("failure_count").notNull().default(0),
  // Cumulative number of lockouts ever observed for this actor — used to
  // drive exponential backoff (lockout duration doubles with each
  // accumulated lockout, capped by policy).
  lockoutCount: integer("lockout_count").notNull().default(0),
  lastFailureAt: timestamp("last_failure_at", { withTimezone: true }),
  lastSuccessAt: timestamp("last_success_at", { withTimezone: true }),
  lastIp: text("last_ip"),
  lastDeviceFingerprint: text("last_device_fingerprint"),
  lockedUntil: timestamp("locked_until", { withTimezone: true }),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxPtwReauthAttemptsLockedUntil: index("idx_ptw_reauth_attempts_locked_until").on(t.lockedUntil),
}));
export const insertPtwReauthAttemptSchema = createInsertSchema(ptwReauthAttempts);
export type InsertPtwReauthAttempt = z.infer<typeof insertPtwReauthAttemptSchema>;
export type PtwReauthAttempt = typeof ptwReauthAttempts.$inferSelect;

// ─── PTW Phase 8.A — Standalone Fallback Registries (Task #1280) ─────
//
// When the Workers, Equipment, or Access modules are unlicensed for a
// tenant, the PTW engine cannot pull cross-module readiness signals.
// These four tables ship the PTW-native fallbacks so the standalone
// product still has full lifecycle coverage:
//
//   ptw_manual_workers      — tenant-scoped roster of workers, used when
//                             `workforce.workers` is OFF.
//   ptw_manual_equipment    — tenant-scoped equipment registry + the
//                             checklist that must be signed off before
//                             a permit is issued. Used when
//                             `operations.equipment` is OFF.
//   ptw_manual_sites        — tenant-scoped local site/area registry,
//                             used when `access.gateManagement` is OFF.
//   ptw_manual_attestations — per-permit per-gate attestation evidence
//                             (supervisor sign-off / equipment checklist
//                             results / area approval) captured by the
//                             fallback flows above.
//
// Whenever the engine evaluates a gate in fallback mode, it stamps
// `permitMetadata.manualFallbackActive=true` and lists the affected gates
// in `permitMetadata.manualFallbackGates`. The PermitConsole already
// exposes a `manual_fallback` filter chip that consumes those flags;
// the cover page of the PTW PDF carries a matching badge.

export const ptwManualWorkers = pgTable("ptw_manual_workers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  fullName: text("full_name").notNull(),
  externalRef: text("external_ref"),
  role: text("role").notNull().default(""),
  trade: text("trade"),
  companyName: text("company_name"),
  contactPhone: text("contact_phone"),
  notes: text("notes"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwManualWorkersTenant: index("idx_ptw_manual_workers_tenant").on(t.tenantId),
}));
export const insertPtwManualWorkerSchema = createInsertSchema(ptwManualWorkers).omit({
  id: true, tenantId: true, createdAt: true, updatedAt: true,
});
export type InsertPtwManualWorker = z.infer<typeof insertPtwManualWorkerSchema>;
export type PtwManualWorker = typeof ptwManualWorkers.$inferSelect;

export type PtwManualEquipmentChecklistItem = {
  id: string;
  label: string;
  mandatory: boolean;
};

export const ptwManualEquipment = pgTable("ptw_manual_equipment", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  equipmentType: text("equipment_type").notNull().default("general"),
  assetCode: text("asset_code"),
  defaultChecklist: jsonb("default_checklist").$type<PtwManualEquipmentChecklistItem[]>().notNull().default([]),
  notes: text("notes"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwManualEquipmentTenant: index("idx_ptw_manual_equipment_tenant").on(t.tenantId),
}));
export const insertPtwManualEquipmentSchema = createInsertSchema(ptwManualEquipment).omit({
  id: true, tenantId: true, createdAt: true, updatedAt: true,
});
export type InsertPtwManualEquipment = z.infer<typeof insertPtwManualEquipmentSchema>;
export type PtwManualEquipment = typeof ptwManualEquipment.$inferSelect;

export const PTW_MANUAL_SITE_TYPES = ["site", "area", "facility"] as const;
export type PtwManualSiteType = (typeof PTW_MANUAL_SITE_TYPES)[number];

export const ptwManualSites = pgTable("ptw_manual_sites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  type: text("type").notNull().default("site"),
  parentId: text("parent_id"),
  description: text("description"),
  notes: text("notes"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwManualSitesTenant: index("idx_ptw_manual_sites_tenant").on(t.tenantId),
}));
export const insertPtwManualSiteSchema = createInsertSchema(ptwManualSites).omit({
  id: true, tenantId: true, createdAt: true, updatedAt: true,
});
export type InsertPtwManualSite = z.infer<typeof insertPtwManualSiteSchema>;
export type PtwManualSite = typeof ptwManualSites.$inferSelect;

export const PTW_MANUAL_ATTESTATION_GATES = [
  "workerReadiness",
  "equipmentReadiness",
  "areaAccessPolicy",
] as const;
export type PtwManualAttestationGate = (typeof PTW_MANUAL_ATTESTATION_GATES)[number];

export type PtwManualAttestationPayload = {
  // Workers fallback: rostered worker ids/names + supervisor attestation
  workers?: { id?: string; name: string; role?: string; companyName?: string }[];
  supervisorStatement?: string;
  // Equipment fallback: per-equipment checklist results
  equipment?: {
    equipmentId?: string;
    name: string;
    checklist: { id: string; label: string; checked: boolean; notes?: string }[];
  }[];
  // Access fallback: which manual site/area was approved + approval reason
  siteId?: string;
  areaId?: string;
  approvalReason?: string;
};

export const ptwManualAttestations = pgTable("ptw_manual_attestations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id").notNull(),
  gate: text("gate").notNull(),
  attestedBy: text("attested_by"),
  attestedByName: text("attested_by_name").notNull(),
  attestedByRole: text("attested_by_role"),
  attestedAt: timestamp("attested_at", { withTimezone: true }).defaultNow().notNull(),
  payload: jsonb("payload").$type<PtwManualAttestationPayload>().notNull().default({}),
  signature: jsonb("signature").$type<{ data: string; signedBy: string; signedAt: string; method?: string }>(),
  notes: text("notes"),
  // Task #2195 — PTW Backend Hardening: structured reason code and
  // declaration text for the canonical manual-attestation route.
  reasonCode: text("reason_code"),
  declarationText: text("declaration_text"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwManualAttestationsPermit: index("idx_ptw_manual_attestations_permit").on(t.permitId),
  idxPtwManualAttestationsTenant: index("idx_ptw_manual_attestations_tenant").on(t.tenantId),
}));
export const insertPtwManualAttestationSchema = createInsertSchema(ptwManualAttestations).omit({
  id: true, tenantId: true, createdAt: true, attestedAt: true,
});
export type InsertPtwManualAttestation = z.infer<typeof insertPtwManualAttestationSchema>;
export type PtwManualAttestation = typeof ptwManualAttestations.$inferSelect;

// ─── PTW CAP-14 (Task #1367) — Multi-Operator / Joint-Venture join tables ─────
//
// Oil & gas (and many EPC contracts) routinely operate under Joint Ventures: a
// site may be jointly operated by Aramco + Shell + TotalEnergies, with each
// operator holding a stake and a different approval matrix. Pre-CAP-14, the
// model encoded this as a single `operatorCompanyId` FK on `projects`,
// `sites`, and an implicit `performingCompanyId` on `ptw_permits`. This
// flattened model could not capture per-operator stake, role, or approval
// requirement.
//
// CAP-14 introduces three structurally identical join tables — one per layer
// of the operational hierarchy:
//
//   project_operators  — operators of the project (long-lived, multi-year).
//   site_operators     — operators of the site (per-site, may differ from
//                         project, e.g. a sub-block where Shell is the OoR).
//   permit_operators   — operators of a single permit (the per-instance
//                         override, materialised at permit-creation time so
//                         the audit trail is a stable snapshot even if the
//                         site/project rosters later change).
//
// Each join row carries:
//   * `companyId`              — FK to the operator company.
//   * `role`                   — one of PTW_OPERATOR_ROLES (operator,
//                                 non_operating_partner, duty_holder,
//                                 regulator_observer).
//   * `stakePercent`           — equity/working-interest percentage; numeric
//                                 (0-100), nullable for roles that don't
//                                 carry stake (e.g. regulator_observer).
//   * `isOperatorOfRecord`     — exactly one row per join level should set
//                                 this to true; the engine uses this row's
//                                 matrix as the tie-breaker for entitlement
//                                 conflicts.
//   * `approvalRequired`       — when true, the engine adds this operator's
//                                 approver to the actor list and refuses to
//                                 advance the permit until this operator
//                                 has signed.
//   * `tenantId`               — every row tenant-scoped (see `tenantFilter`
//                                 in server/storage/helpers.ts).
//
// Backfill: the migration (0114_ptw_cap14_multi_operator.sql) populates each
// table from the existing single-operator FK so single-operator deployments
// keep the same behaviour without code changes.

export const projectOperators = pgTable("project_operators", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  projectId: text("project_id").notNull().references(() => projects.id, { onDelete: "cascade" }),
  companyId: text("company_id").notNull().references(() => companies.id),
  role: text("role").notNull().default("operator"),
  stakePercent: numeric("stake_percent"),
  isOperatorOfRecord: boolean("is_operator_of_record").notNull().default(false),
  approvalRequired: boolean("approval_required").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // Hot read path: roster-by-project (used on every PTW wizard hydrate).
  idxProjectOperatorsProject: index("idx_project_operators_project").on(t.projectId),
  // Tenant-scoped uniqueness: a company appears at most once per project.
  uniqueProjectOperator: uniqueIndex("uq_project_operators_project_company").on(t.projectId, t.companyId),
}));
export const insertProjectOperatorSchema = createInsertSchema(projectOperators).omit({
  id: true,
  tenantId: true,
  createdAt: true,
});
export type InsertProjectOperator = z.infer<typeof insertProjectOperatorSchema>;
export type ProjectOperator = typeof projectOperators.$inferSelect;

export const siteOperators = pgTable("site_operators", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  siteId: text("site_id").notNull().references(() => sites.id, { onDelete: "cascade" }),
  companyId: text("company_id").notNull().references(() => companies.id),
  role: text("role").notNull().default("operator"),
  stakePercent: numeric("stake_percent"),
  isOperatorOfRecord: boolean("is_operator_of_record").notNull().default(false),
  approvalRequired: boolean("approval_required").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxSiteOperatorsSite: index("idx_site_operators_site").on(t.siteId),
  uniqueSiteOperator: uniqueIndex("uq_site_operators_site_company").on(t.siteId, t.companyId),
}));
export const insertSiteOperatorSchema = createInsertSchema(siteOperators).omit({
  id: true,
  tenantId: true,
  createdAt: true,
});
export type InsertSiteOperator = z.infer<typeof insertSiteOperatorSchema>;
export type SiteOperator = typeof siteOperators.$inferSelect;

export const permitOperators = pgTable("permit_operators", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  companyId: text("company_id").notNull().references(() => companies.id),
  role: text("role").notNull().default("operator"),
  stakePercent: numeric("stake_percent"),
  isOperatorOfRecord: boolean("is_operator_of_record").notNull().default(false),
  approvalRequired: boolean("approval_required").notNull().default(false),
  // Tracks per-operator approval gate completion. The engine sets this to
  // the actor's id + timestamp when the operator's approver signs.
  approvedById: text("approved_by_id"),
  approvedByName: text("approved_by_name"),
  approvedAt: timestamp("approved_at", { withTimezone: true }),
  // Typed rejection timestamp added by migration 0153 (Task #715).
  rejectedAt: timestamp("rejected_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxPermitOperatorsPermit: index("idx_permit_operators_permit").on(t.permitId),
  idxPermitOperatorsTenantCompany: index("idx_permit_operators_tenant_company").on(t.tenantId, t.companyId),
  uniquePermitOperator: uniqueIndex("uq_permit_operators_permit_company").on(t.permitId, t.companyId),
}));
export const insertPermitOperatorSchema = createInsertSchema(permitOperators).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  approvedById: true,
  approvedByName: true,
  approvedAt: true,
});
export type InsertPermitOperator = z.infer<typeof insertPermitOperatorSchema>;
export type PermitOperator = typeof permitOperators.$inferSelect;

// ─── PTW Phase 8.C — Manual External Approvals (Task #1282) ──────────
//
// When the Partner Portal is unavailable for a tenant (standalone PTW),
// the engine cannot route an external authority through an actor inbox
// to record their decision. This table captures that decision manually:
// the supervisor (or whoever physically met the external authority)
// transcribes the approver's name/role/organisation, the decision, any
// conditions, and a captured signature (drawn-on-screen or scanned). It
// is the audit-of-record for "external approval was obtained" and is
// surfaced on the Permit Detail page + cover-page badge of the PDF.
//
// Decision values:
//   pending     — no decision recorded yet (used while a multi-stage
//                 conversation is in progress).
//   approved    — green-lights issuance/closeout.
//   rejected    — refuses the request, blocks the gate.
//   conditional — approves subject to listed `conditions` (free-text).
//
// The `signature` jsonb intentionally mirrors the shape of the manual
// attestation signature so consumers (PDF renderer, audit chain) can
// share rendering helpers with Phase 8.A.
export const PTW_EXTERNAL_APPROVAL_DECISIONS = [
  "pending",
  "approved",
  "rejected",
  "conditional",
] as const;
export type PtwExternalApprovalDecision = (typeof PTW_EXTERNAL_APPROVAL_DECISIONS)[number];

export type PtwExternalApprovalSignature = {
  data: string;
  signedBy: string;
  signedAt: string;
  method?: "drawing" | "wet" | "uploaded";
};

export const ptwExternalApprovals = pgTable("ptw_external_approvals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id").notNull(),
  // Identity of the external authority whose decision was captured.
  approverName: text("approver_name").notNull(),
  approverRole: text("approver_role"),
  approverOrganization: text("approver_organization"),
  approverEmail: text("approver_email"),
  approverPhone: text("approver_phone"),
  // Decision + conditions. `decision` is one of PTW_EXTERNAL_APPROVAL_DECISIONS.
  decision: text("decision").notNull().default("pending"),
  conditions: text("conditions"),
  notes: text("notes"),
  // Signature evidence — drawn on-screen or pasted/uploaded scan.
  signature: jsonb("signature").$type<PtwExternalApprovalSignature>(),
  // Who in the platform transcribed the decision.
  capturedBy: text("captured_by"),
  capturedByName: text("captured_by_name"),
  capturedByRole: text("captured_by_role"),
  capturedAt: timestamp("captured_at", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwExternalApprovalsPermit: index("idx_ptw_external_approvals_permit").on(t.permitId),
  idxPtwExternalApprovalsTenant: index("idx_ptw_external_approvals_tenant").on(t.tenantId),
}));

export const insertPtwExternalApprovalSchema = createInsertSchema(ptwExternalApprovals).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  capturedAt: true,
});
export type InsertPtwExternalApproval = z.infer<typeof insertPtwExternalApprovalSchema>;
export type PtwExternalApproval = typeof ptwExternalApprovals.$inferSelect;

// ─── PTW Phase 8.C — In-app queue daily export log (Task #1282) ──────
//
// When `platform.notifications` is OFF for a tenant, the PTW engine
// clamps every notification it would have routed via email/SMS/push to
// in-app delivery only. To give compliance / regulator hand-off teams a
// daily snapshot of "what would have gone out", the daily-export job
// (or an on-demand admin export) writes a row to this table with the
// date covered, the row count, the export format, and the user who ran
// it. This is the durable audit trail; the actual CSV/JSON payload is
// streamed to the caller and not persisted server-side.
export const PTW_INAPP_QUEUE_EXPORT_FORMATS = ["csv", "json"] as const;
export type PtwInAppQueueExportFormat = (typeof PTW_INAPP_QUEUE_EXPORT_FORMATS)[number];

export const ptwInAppQueueExports = pgTable("ptw_inapp_queue_exports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // The day covered by the export, stored as ISO date string (YYYY-MM-DD)
  // for trivial sortability and so callers can request idempotent re-exports.
  exportDate: text("export_date").notNull(),
  format: text("format").notNull().default("csv"),
  itemCount: integer("item_count").notNull().default(0),
  exportedBy: text("exported_by"),
  exportedByName: text("exported_by_name"),
  exportedAt: timestamp("exported_at", { withTimezone: true }).defaultNow().notNull(),
  notes: text("notes"),
}, (t) => ({
  idxPtwInAppQueueExportsTenantDate: index("idx_ptw_inapp_queue_exports_tenant_date").on(t.tenantId, t.exportDate),
}));

export const insertPtwInAppQueueExportSchema = createInsertSchema(ptwInAppQueueExports).omit({
  id: true,
  tenantId: true,
  exportedAt: true,
});
export type InsertPtwInAppQueueExport = z.infer<typeof insertPtwInAppQueueExportSchema>;
export type PtwInAppQueueExport = typeof ptwInAppQueueExports.$inferSelect;

// ─── PTW Phase 8.B (Task #1281) — Native Hazards/Controls/PPE Library ───
//
// PTW-native catalogue editable per tenant. When the Safety module
// (`safety.*`) is OFF, the wizard cannot pull hazards from the safety
// observations / incident registry, so the standalone PTW product needs
// its own curated library. `kind` discriminates the entry type:
//
//   "hazard"  — identified hazard description (e.g. "Working at height").
//   "control" — control measure / mitigation (e.g. "Body harness with
//               twin lanyard anchored to certified anchor point").
//   "ppe"     — personal protective equipment (e.g. "Hard hat",
//               "Safety glasses", "Steel toe boots").
//
// Stored as one table (rather than three) so a single CRUD surface
// covers all three lists, the order index sorts within `kind`, and a
// future "category" column can group entries inside a kind without
// schema churn. `code` is an optional tenant-side stable id for cross-
// referencing presets / external safety catalogues; `label` is the
// canonical human-readable string that lands on the permit's
// `hazards` / `controlMeasures` / `ppeRequirements` arrays.
export const PTW_HAZARD_LIBRARY_KINDS = ["hazard", "control", "ppe"] as const;
export type PtwHazardLibraryKind = (typeof PTW_HAZARD_LIBRARY_KINDS)[number];

export const ptwHazardLibrary = pgTable("ptw_hazard_library", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  code: text("code"),
  label: text("label").notNull(),
  description: text("description").notNull().default(""),
  category: text("category"),
  active: boolean("active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  // Hot read path — wizard render fetches `tenant_id, kind, active=true`
  // ordered by `sort_order, label`.
  idxPtwHazardLibraryTenantKind: index("idx_ptw_hazard_library_tenant_kind").on(t.tenantId, t.kind),
  // Per-tenant uniqueness of (kind, code) when code is supplied — prevents
  // duplicate library entries from sloppy admin edits.
  uniqPtwHazardLibraryTenantKindCode: uniqueIndex("uniq_ptw_hazard_library_tenant_kind_code")
    .on(t.tenantId, t.kind, t.code),
}));
export const insertPtwHazardLibrarySchema = createInsertSchema(ptwHazardLibrary, {
  kind: () => z.enum(PTW_HAZARD_LIBRARY_KINDS),
}).omit({ id: true, tenantId: true, createdAt: true, updatedAt: true });
export type InsertPtwHazardLibrary = z.infer<typeof insertPtwHazardLibrarySchema>;
export type PtwHazardLibrary = typeof ptwHazardLibrary.$inferSelect;

// ─── PTW Phase 8.B (Task #1281) — Native Toolbox Talk Capture ───────────
//
// PTW-native equivalent of the Safety-module `toolbox_talks` table. The
// existing `toolbox_talks` rows are gated by `incident.view` /
// `incident.report` permissions which require the Safety module licence,
// so this PTW-native variant exists for tenants where Safety is OFF and
// PTW must still carry the TBT evidence chain (attendees, language,
// photo, signature, missed attendees).
//
// `permitId` is nullable because TBTs may be conducted at the start of a
// shift independently of any single permit (e.g. one talk covers six
// permits); when bound, the link is enforced application-side rather
// than via a hard FK so a permit hard-delete does not cascade-destroy a
// TBT that may have evidentiary value beyond the permit lifecycle.
//
// Retention metadata mirrors the per-row `retain_until` / `legal_hold`
// contract from PTW Phase 2.C — the attachment-retention sweep clears
// the `photo_file_id` / `signature_data` payload past the cutoff while
// preserving the row itself for audit compliance.
export const ptwNativeToolboxTalks = pgTable("ptw_native_toolbox_talks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id"),
  supervisorId: text("supervisor_id"),
  supervisorName: text("supervisor_name").notNull(),
  siteId: text("site_id"),
  areaId: text("area_id"),
  topic: text("topic").notNull(),
  // ISO-639-1 language code captured at run time (e.g. "en", "ar", "hi").
  // Drives the audit trail showing which language the talk was delivered in.
  language: text("language").notNull().default("en"),
  hazardCategories: text("hazard_categories").array().default([]),
  notes: text("notes"),
  // Photo attachment — bucket object key written via `uploadPtwAttachment`
  // so the governance sidecar (`ptw_attachment_objects`) carries the
  // retention metadata for the bytes themselves.
  photoFileId: text("photo_file_id"),
  // Signature blob captured by the supervisor (drawing pad) at the end of
  // the talk. Same shape as the PTW step instance signature column so the
  // verifier can render it identically.
  signatureData: jsonb("signature_data").$type<{
    data: string;
    signedBy: string;
    signedAt: string;
    method?: string;
  }>(),
  conductedAt: timestamp("conducted_at", { withTimezone: true }).defaultNow().notNull(),
  // Retention metadata mirrors the per-row contract from PTW Phase 2.C.
  // NULL `retainUntil` falls back to the resolved per-tenant policy.
  retainUntil: timestamp("retain_until", { withTimezone: true }),
  legalHold: boolean("legal_hold").notNull().default(false),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwNativeTbtTenantPermit: index("idx_ptw_native_tbt_tenant_permit").on(t.tenantId, t.permitId),
  idxPtwNativeTbtTenantConducted: index("idx_ptw_native_tbt_tenant_conducted").on(t.tenantId, t.conductedAt),
  // Retention sweep pagination — list talks past the cutoff per tenant.
  idxPtwNativeTbtTenantRetain: index("idx_ptw_native_tbt_tenant_retain").on(t.tenantId, t.retainUntil),
}));
export const insertPtwNativeToolboxTalkSchema = createInsertSchema(ptwNativeToolboxTalks).omit({
  id: true, tenantId: true, createdAt: true,
}).extend({
  conductedAt: z.union([z.date(), z.string().datetime()])
    .transform(v => typeof v === "string" ? new Date(v) : v).optional(),
  // Optional at the API boundary — the POST /api/ptw-v2/toolbox-talks
  // route stamps supervisorId/supervisorName/createdBy from the session
  // actor when omitted (mobile shop-floor flow). The DB column stays
  // NOT NULL; the route guarantees a value before insert.
  supervisorName: z.string().optional(),
});
export type InsertPtwNativeToolboxTalk = z.infer<typeof insertPtwNativeToolboxTalkSchema>;
export type PtwNativeToolboxTalk = typeof ptwNativeToolboxTalks.$inferSelect;

// PTW-native TBT attendance roster. Captures both attendees and missed
// attendees (with reason) so the supervisor can attest "everyone on the
// permit who should have attended did, and here is the reason for those
// who didn't". `workerId` is free-form text (no FK) so the standalone
// PTW product works against the Phase 8.A `ptw_manual_workers` registry
// just as well as against the full `workers` registry when the Workers
// module is licensed.
export const ptwNativeToolboxTalkAttendance = pgTable("ptw_native_toolbox_talk_attendance", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  toolboxTalkId: text("toolbox_talk_id").notNull(),
  workerId: text("worker_id").notNull(),
  workerName: text("worker_name"),
  attended: boolean("attended").notNull().default(false),
  // When `attended = false`, `missedReason` MUST be populated by the
  // capture flow — enforced by the route-level zod schema.
  missedReason: text("missed_reason"),
  recordedAt: timestamp("recorded_at", { withTimezone: true }).defaultNow().notNull(),
  recordedBy: text("recorded_by"),
}, (t) => ({
  idxPtwNativeTbtAttendanceTalk: index("idx_ptw_native_tbt_attendance_talk").on(t.toolboxTalkId),
  // Per-talk per-worker uniqueness — re-recording flips attendance instead
  // of duplicating rows.
  uniqPtwNativeTbtAttendanceTalkWorker: uniqueIndex("uniq_ptw_native_tbt_attendance_talk_worker")
    .on(t.toolboxTalkId, t.workerId),
}));
export const insertPtwNativeToolboxTalkAttendanceSchema = createInsertSchema(ptwNativeToolboxTalkAttendance).omit({
  id: true, tenantId: true, recordedAt: true,
});
export type InsertPtwNativeToolboxTalkAttendance = z.infer<typeof insertPtwNativeToolboxTalkAttendanceSchema>;
export type PtwNativeToolboxTalkAttendance = typeof ptwNativeToolboxTalkAttendance.$inferSelect;

// ─── PTW CAP-15 — Long-Term Isolation Register (LTIR) ────────────────
//
// Process plants frequently carry "long-term isolations" that span multiple
// shifts, multiple permits, and sometimes months (e.g. a vessel isolated
// for major maintenance). The legacy `ptw_isolations.long_term_flag` column
// is permit-bound — once the originating permit closes the isolation
// disappears from any operational view. CAP-15 adds a canonical register
// that survives permit closure, supports cross-permit reassignment, and
// enforces aged-review review cadence.
//
// Lifecycle (canonical statuses):
//   active        — placed and currently bound to a covering permit
//   under_review  — accountable engineer is mid-review
//   extended      — review extended (next_review_due_at pushed out)
//   removed       — physically removed (must occur inside a covering permit)
//
// Reassignment: when the `current_permit_id` permit closes/cancels and
// the LTI is not removed, the engine attempts to reassign to another
// active permit covering the same equipment_ref. If none exists,
// `current_permit_id` is set to NULL and `status` stays `active` — the
// register is the source of truth, not the originating permit.
export const PTW_LTI_REGISTER_STATUSES = [
  "active",
  "under_review",
  "extended",
  "removed",
] as const;
export type PtwLtiRegisterStatus = (typeof PTW_LTI_REGISTER_STATUSES)[number];

export const PTW_LTI_DEFAULT_REVIEW_CADENCE_DAYS = 30;

/**
 * Canonical notification + audit-event key for the aged-isolation alarm.
 * Emitted by `runLtiAgedReviewSweep()` and (transitively) by the per-LTI
 * lifecycle action handlers. Surfaces in the Console as a pseudo-state
 * badge, in the dashboard tile, and as an in-app notification.
 */
export const PTW_LTI_REVIEW_OVERDUE_NOTIFICATION_KEY = "ptw.lti.review_overdue" as const;
/** Task #3026 — notification key for the "review due in 24 hours" early-warning pass. */
export const PTW_LTI_REVIEW_DUE_SOON_NOTIFICATION_KEY = "ptw.lti.review_due_soon" as const;

export type PtwLtiHistoryEntry = {
  /** ISO 8601 timestamp the entry was recorded. */
  at: string;
  /** What happened — placed | reviewed | extended | removed | reassigned | review_overdue | review_due_soon */
  kind: "placed" | "reviewed" | "extended" | "removed" | "reassigned" | "review_overdue" | "review_due_soon";
  /** Free-text note (e.g. review findings, reason for extension). */
  note?: string;
  /** Acting user identity (when applicable). */
  actorId?: string;
  actorName?: string;
  actorRole?: string;
  /** Permit context for the event (covering or originating). */
  permitId?: string | null;
  /** New cadence in days when `kind === "extended"`. */
  newCadenceDays?: number;
  /** Computed next-review date after the event (ISO 8601). */
  nextReviewDueAt?: string | null;
};

export const ptwLongTermIsolationRegister = pgTable("ptw_long_term_isolation_register", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),

  // Sidecar to the original isolation row. Unique so a given placed
  // isolation can only ever appear once in the register; the engine
  // upserts on insert when long_term_flag flips on.
  isolationId: text("isolation_id").notNull(),

  // What is isolated. `tag` mirrors `ptw_isolations.point_or_tag`;
  // `equipment_ref` is the canonical equipment id (free-text to support
  // the standalone-PTW manual equipment registry from Phase 8.A) and
  // is used for cross-permit reassignment lookups.
  tag: text("tag"),
  equipmentRef: text("equipment_ref"),
  location: text("location"),
  isolationMethod: text("isolation_method").notNull().default("electrical"),
  hazardClass: text("hazard_class").notNull().default("general"),

  // Provenance + permit linkage.
  appliedAt: timestamp("applied_at", { withTimezone: true }).defaultNow().notNull(),
  appliedBy: text("applied_by"),
  appliedByName: text("applied_by_name"),
  expectedRemovalAt: timestamp("expected_removal_at", { withTimezone: true }),
  /** The permit that originally placed the LTI. Never reassigned. */
  originatingPermitId: text("originating_permit_id").notNull(),
  /**
   * The permit currently covering the LTI. NULL when reassignment failed
   * (no active permit covers the equipment) — the engine flags such rows
   * for the accountable engineer's attention via the dashboard tile.
   */
  currentPermitId: text("current_permit_id"),

  // Accountable engineer (always required — the register is engineer-owned).
  accountableEngineerId: text("accountable_engineer_id"),
  accountableEngineerName: text("accountable_engineer_name"),

  // Review cadence + state.
  reviewCadenceDays: integer("review_cadence_days").notNull().default(PTW_LTI_DEFAULT_REVIEW_CADENCE_DAYS),
  lastReviewedAt: timestamp("last_reviewed_at", { withTimezone: true }),
  lastReviewedBy: text("last_reviewed_by"),
  lastReviewedByName: text("last_reviewed_by_name"),
  nextReviewDueAt: timestamp("next_review_due_at", { withTimezone: true }),

  status: text("status").notNull().default("active"),

  // Removal record (only valid when status === "removed").
  removedAt: timestamp("removed_at", { withTimezone: true }),
  removedBy: text("removed_by"),
  removedByName: text("removed_by_name"),
  /** The covering permit at the time of removal — required by engine guard. */
  removalPermitId: text("removal_permit_id"),

  // Cross-permit history journal — every place / review / extend / remove
  // / reassign / review_overdue event is appended here so the regulator
  // report and Detail panel timeline render without a join.
  history: jsonb("history").$type<PtwLtiHistoryEntry[]>().notNull().default([]),

  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  // One LTI row per isolation per tenant — `isolation_id` is logically
  // unique already (it's a UUID), but the unique index also enforces the
  // engine's "place once, review forever" contract.
  uxLtiRegisterIsolation: uniqueIndex("ux_ptw_lti_register_isolation").on(t.isolationId),
  // Dashboard + drawer hot reads (per tenant, by status).
  idxLtiRegisterTenantStatus: index("idx_ptw_lti_register_tenant_status").on(t.tenantId, t.status),
  // Aged-review sweep + dashboard "next overdue" sort.
  idxLtiRegisterNextReview: index("idx_ptw_lti_register_next_review_due").on(t.nextReviewDueAt),
  // Permit-detail panel + reassignment lookup.
  idxLtiRegisterCurrentPermit: index("idx_ptw_lti_register_current_permit").on(t.currentPermitId),
  idxLtiRegisterOriginatingPermit: index("idx_ptw_lti_register_originating_permit").on(t.originatingPermitId),
  // Cross-permit equipment lookup for reassignment.
  idxLtiRegisterEquipmentRef: index("idx_ptw_lti_register_equipment_ref").on(t.tenantId, t.equipmentRef),
}));

export const insertPtwLongTermIsolationRegisterSchema = createInsertSchema(ptwLongTermIsolationRegister).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
});
// ─── PTW Phase 7.D (Task #1278) — Post-Event Review queue ─────────────
//
// Every `emergency_stop` (override) action now writes a post-event review
// row that the HSE / safety leadership uses to capture root cause,
// corrective actions and lessons learned. The row is created
// automatically by the engine when the override fires; it starts
// `status = "open"`, transitions to `in_review` when an HSE actor claims
// it, and to `signed_off` when the reviewer signs off with the required
// findings.
//
// Tenant-scoped (every read/write goes through `withTenantFilter`). The
// trigger metadata is captured at the moment of the override so the
// review survives later edits to the permit, the actor's profile, or
// the audit-log chain (the audit row is also referenced by id).
export const PTW_POST_EVENT_REVIEW_STATUSES = [
  "open",
  "in_review",
  "signed_off",
  "cancelled",
] as const;
export type PtwPostEventReviewStatus =
  (typeof PTW_POST_EVENT_REVIEW_STATUSES)[number];

export const ptwPostEventReviews = pgTable("ptw_post_event_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  // The trigger row — what fired the override, when, and by whom.
  triggerAuditId: text("trigger_audit_id"),
  triggerAction: text("trigger_action").notNull().default("emergency_stop"),
  triggerReason: text("trigger_reason").notNull(),
  triggerActorId: text("trigger_actor_id").notNull(),
  triggerActorName: text("trigger_actor_name").notNull(),
  triggerActorRole: text("trigger_actor_role").notNull(),
  triggeredAt: timestamp("triggered_at", { withTimezone: true }).notNull().defaultNow(),
  // Second sign-off captured atomically with the trigger so the audit
  // chain shows two human signatures on the override decision itself.
  secondSignerId: text("second_signer_id").notNull(),
  secondSignerName: text("second_signer_name").notNull(),
  secondSignerRole: text("second_signer_role").notNull(),
  secondSignerAttestation: text("second_signer_attestation"),
  secondSignedAt: timestamp("second_signed_at", { withTimezone: true }).notNull().defaultNow(),
  // Task #2683 — Step-up re-auth for second signer.
  // true when the second signer proved their identity by entering their
  // credentials at the terminal during the emergency stop. false when
  // verification was skipped (second signer not physically present).
  secondSignerVerified: boolean("second_signer_verified").notNull().default(false),
  // The ptw_reauth_challenges row that seals the step-up proof. Null
  // when verification was skipped.
  secondSignerChallengeId: text("second_signer_challenge_id"),
  // Lifecycle.
  status: text("status").notNull().default("open"),
  dueAt: timestamp("due_at", { withTimezone: true }),
  // HSE (or other authorised reviewer) claim + sign-off metadata.
  claimedById: text("claimed_by_id"),
  claimedByName: text("claimed_by_name"),
  claimedByRole: text("claimed_by_role"),
  claimedAt: timestamp("claimed_at", { withTimezone: true }),
  reviewerId: text("reviewer_id"),
  reviewerName: text("reviewer_name"),
  reviewerRole: text("reviewer_role"),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  // Free-text findings captured at sign-off.
  findings: text("findings"),
  rootCause: text("root_cause"),
  correctiveActions: text("corrective_actions"),
  lessonsLearned: text("lessons_learned"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  // Hot read paths: HSE inbox by tenant+status (open queue) and by permit.
  idxPostEventReviewsTenantStatus: index("idx_ptw_post_event_reviews_tenant_status").on(t.tenantId, t.status),
  idxPostEventReviewsPermit: index("idx_ptw_post_event_reviews_permit").on(t.permitId),
}));
export const insertPtwPostEventReviewSchema = createInsertSchema(ptwPostEventReviews).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwLongTermIsolationRegister = z.infer<typeof insertPtwLongTermIsolationRegisterSchema>;
export type PtwLongTermIsolationRegister = typeof ptwLongTermIsolationRegister.$inferSelect;
export type InsertPtwPostEventReview = z.infer<typeof insertPtwPostEventReviewSchema>;
export type PtwPostEventReview = typeof ptwPostEventReviews.$inferSelect;

// ─── PTW CAP-21 (Task #1374) — Tenant-Configurable Permit Numbering ──
//
// Per-tenant numbering schemes. Each scheme renders the next
// `permit_number` from a mustache-style template (e.g.
// `{tenantPrefix}-{operatorPrefix}-{siteCode}-{yyyy}-{mm}-{seq}`).
// Schemes are picked by precedence operator > permit_type > tenant —
// the most specific scope that matches the permit being created wins.
// The platform default scheme lives in `tenant_defaults.payload.ptw.numbering`
// and is overlaid into `tenants.settings.ptw.numbering` at provisioning
// time so every tenant has a working scheme even before they create a
// per-tenant override row in this table.
export const PTW_NUMBERING_SCOPES = ["tenant", "permit_type", "operator"] as const;
export type PtwNumberingScope = typeof PTW_NUMBERING_SCOPES[number];

export const PTW_NUMBERING_COUNTER_STRATEGIES = [
  "per-tenant",
  "per-site",
  "per-month",
  "per-year",
  "per-permit-type",
  "per-operator",
] as const;
export type PtwNumberingCounterStrategy = typeof PTW_NUMBERING_COUNTER_STRATEGIES[number];

export const PTW_NUMBERING_RESET_CADENCES = ["none", "yearly", "monthly"] as const;
export type PtwNumberingResetCadence = typeof PTW_NUMBERING_RESET_CADENCES[number];

/**
 * Shape of the platform-default scheme carried in `tenant_defaults.payload.ptw.numbering`.
 * Mirrors the per-row column shape so a default can be lifted into a
 * `ptw_numbering_schemes` row 1:1 by the SA "promote default" flow.
 */
export interface PtwNumberingDefault {
  name: string;
  template: string;
  counterStrategy: PtwNumberingCounterStrategy;
  resetCadence: PtwNumberingResetCadence;
  padding: number;
  seqStart?: number;
}

export const ptwNumberingSchemes = pgTable("ptw_numbering_schemes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  // Scope precedence on lookup: operator > permit_type > tenant. For the
  // tenant-wide fallback `scopeKey` is NULL; for permit_type/operator the
  // key carries the permit-type slug (matched against `ptw_permits.work_type`)
  // or the operator company id respectively.
  scope: text("scope").notNull().default("tenant"),
  scopeKey: text("scope_key"),
  template: text("template").notNull(),
  counterStrategy: text("counter_strategy").notNull().default("per-tenant"),
  resetCadence: text("reset_cadence").notNull().default("none"),
  padding: integer("padding").notNull().default(6),
  seqStart: integer("seq_start").notNull().default(1),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxPtwNumberingSchemesTenant: index("idx_ptw_numbering_schemes_tenant").on(t.tenantId),
  // One scheme per (tenant, scope, scope_key). The tenant-wide row uses
  // scope_key='' (NULLs are not deduped by Postgres unique indexes).
  uniqPtwNumberingSchemesScope: uniqueIndex("uniq_ptw_numbering_schemes_scope")
    .on(t.tenantId, t.scope, t.scopeKey),
}));
export const insertPtwNumberingSchemeSchema = createInsertSchema(ptwNumberingSchemes).omit({
  id: true, tenantId: true, createdAt: true, updatedAt: true,
}).extend({
  scope: z.enum(PTW_NUMBERING_SCOPES).default("tenant"),
  counterStrategy: z.enum(PTW_NUMBERING_COUNTER_STRATEGIES).default("per-tenant"),
  resetCadence: z.enum(PTW_NUMBERING_RESET_CADENCES).default("none"),
  padding: z.number().int().min(0).max(12).default(6),
  seqStart: z.number().int().min(0).max(10_000_000).default(1),
  template: z.string().min(1).max(200),
  name: z.string().min(1).max(120),
});
export type InsertPtwNumberingScheme = z.infer<typeof insertPtwNumberingSchemeSchema>;
export type PtwNumberingScheme = typeof ptwNumberingSchemes.$inferSelect;

// Per-counter state. Holds the current sequence value for one
// (scheme × counter-key) combination. Counter-key is computed from the
// scheme's `counterStrategy` + `resetCadence` (e.g. "tenant:2026" for
// `per-tenant` + `yearly`, "site:A:2026-04" for `per-site` + `monthly`).
// `pg_advisory_xact_lock(hashtext(counter_key))` is held while the
// counter is incremented so 100 concurrent submissions produce 100
// unique sequential numbers without an N+1 unique-constraint retry.
export const ptwNumberingCounters = pgTable("ptw_numbering_counters", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  schemeId: text("scheme_id").notNull(),
  counterKey: text("counter_key").notNull(),
  currentValue: integer("current_value").notNull().default(0),
  lastResetAt: timestamp("last_reset_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqPtwNumberingCountersKey: uniqueIndex("uniq_ptw_numbering_counters_key")
    .on(t.tenantId, t.schemeId, t.counterKey),
}));
export const insertPtwNumberingCounterSchema = createInsertSchema(ptwNumberingCounters).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertPtwNumberingCounter = z.infer<typeof insertPtwNumberingCounterSchema>;
export type PtwNumberingCounter = typeof ptwNumberingCounters.$inferSelect;

// ─── PTW CAP-22 — Industry-Code Mappings (Task #1375) ─────────────────
//
// Hazards / controls / PPE / step types in templates are tagged with one
// or more recognised industry-code identifiers (ISO 45001, OISD-105/137,
// IOGP 510/577, API RP 2210, OSHA PSM 1910.119) so reports + the
// regulator audit pack can address regulator expectations directly
// instead of via free-text translations.
//
// Three tables underpin the capability:
//   1. `ptw_industry_code_registry` — canonical catalogue of code entries
//      keyed by family + code. System-wide rows have `tenant_id IS NULL`
//      (loaded from the curated YAML file via
//      `scripts/load-ptw-industry-codes.ts`); tenant-owned augmentations
//      stamp `tenant_id` so a tenant can extend the platform catalogue
//      with internal cross-references without being able to mutate other
//      tenants' rows.
//   2. `ptw_industry_code_mappings` — join table that anchors a code id
//      to an arbitrary PTW entity (hazard library row / step type / etc).
//      Always tenant-stamped so cross-tenant reads are physically
//      impossible from the storage layer.
//   3. `ptw_tenant_industry_code_settings` — per-tenant feature toggle
//      controlling which families (`iso_45001`, `oisd`, `iogp`, `api_rp`,
//      `osha_psm`, …) are visible in the template editor's code-tag
//      picker. Defaults to "all enabled" when no row is present.
export const PTW_INDUSTRY_CODE_FAMILIES = [
  "iso_45001",
  "oisd_105",
  "oisd_137",
  "iogp_510",
  "iogp_577",
  "api_rp_2210",
  "osha_psm_1910_119",
] as const;
export type PtwIndustryCodeFamily = (typeof PTW_INDUSTRY_CODE_FAMILIES)[number];

export const PTW_INDUSTRY_CODE_FAMILY_LABELS: Record<PtwIndustryCodeFamily, string> = {
  iso_45001: "ISO 45001:2018",
  oisd_105: "OISD-105 (Hot Work Permit System)",
  oisd_137: "OISD-137 (Inspection of Process Plants)",
  iogp_510: "IOGP 510 (Operating Management System)",
  iogp_577: "IOGP 577 (Fabrication Site Construction Safety)",
  api_rp_2210: "API RP 2210 (Flame Arresters)",
  osha_psm_1910_119: "OSHA PSM 1910.119",
};

// Entity types a code can be tagged onto. `permit` and `template` are the
// rollup anchors (regulator-pack export reads from those + the per-step
// mappings to render the full "this permit cited these clauses" panel).
export const PTW_INDUSTRY_CODE_ENTITY_TYPES = [
  "hazard_library",
  "control_library",
  "ppe_library",
  "step_type",
  "template",
  "template_step",
  "permit",
] as const;
export type PtwIndustryCodeEntityType = (typeof PTW_INDUSTRY_CODE_ENTITY_TYPES)[number];

export const ptwIndustryCodeRegistry = pgTable("ptw_industry_code_registry", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // NULL = system-wide entry loaded from the curated YAML; non-NULL =
  // tenant-owned augmentation. The unique index below enforces "one row
  // per (tenant, family, code)".
  tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }),
  family: text("family").notNull(),
  code: text("code").notNull(),
  title: text("title").notNull(),
  description: text("description").notNull().default(""),
  // Reference URL / clause anchor (e.g. https://… / ISO portal). Surfaced
  // verbatim in the regulator-pack export footer so the auditor can
  // dereference the citation without leaving the PDF.
  sourceUrl: text("source_url"),
  // Optional clause / section anchor inside the parent document
  // (e.g. "8.1.2" inside ISO 45001:2018, "5.3.4" inside OISD-105).
  clause: text("clause"),
  // Free-form tags from the YAML loader (e.g. "hot_work", "isolation",
  // "psm_element_3") used by the template editor's code-tag picker as a
  // pre-filter so an admin tagging a hot-work step only sees relevant
  // codes by default.
  tags: text("tags").array().default([]),
  active: boolean("active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  // Provenance of the most recent loader pass that wrote the row, so a
  // re-run from a newer YAML revision can be diffed in audit logs.
  registryRevision: text("registry_revision"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  // Hot read path — code-tag picker fetches `family + active=true` ordered
  // by sort_order then code.
  idxPtwIndustryCodeFamilyActive: index("idx_ptw_industry_code_family_active")
    .on(t.family, t.active),
  // Per-tenant uniqueness — system rows (`tenant_id IS NULL`) are unique
  // per (family, code) by virtue of the partial index below; tenant rows
  // get a separate unique constraint so a tenant can mirror a system code
  // with their own augmentation without colliding.
  uxPtwIndustryCodeTenantFamilyCode: uniqueIndex("ux_ptw_industry_code_tenant_family_code")
    .on(t.tenantId, t.family, t.code),
}));
export const insertPtwIndustryCodeSchema = createInsertSchema(ptwIndustryCodeRegistry, {
  family: () => z.enum(PTW_INDUSTRY_CODE_FAMILIES),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPtwIndustryCode = z.infer<typeof insertPtwIndustryCodeSchema>;
export type PtwIndustryCode = typeof ptwIndustryCodeRegistry.$inferSelect;

export const ptwIndustryCodeMappings = pgTable("ptw_industry_code_mappings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // FK is intentionally soft (`text` instead of `references()`) so
  // deleting a system-wide registry row from the loader cannot cascade-
  // destroy a tenant's mapping history. The application layer guards
  // against orphan mappings.
  codeId: text("code_id").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  notes: text("notes"),
  createdBy: text("created_by"),
  createdByName: text("created_by_name"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  // Per-tenant uniqueness — the same code cannot be tagged onto the same
  // entity twice (idempotent re-tag becomes a no-op).
  uxPtwIndustryCodeMapping: uniqueIndex("ux_ptw_industry_code_mapping")
    .on(t.tenantId, t.codeId, t.entityType, t.entityId),
  // Hot read path — entity-detail panel fetches every mapping for one
  // (entityType, entityId) pair per tenant.
  idxPtwIndustryCodeMappingEntity: index("idx_ptw_industry_code_mapping_entity")
    .on(t.tenantId, t.entityType, t.entityId),
  // Reverse lookup — registry-edit confirm uses this to display every
  // entity currently citing a given code.
  idxPtwIndustryCodeMappingCode: index("idx_ptw_industry_code_mapping_code")
    .on(t.tenantId, t.codeId),
}));
export const insertPtwIndustryCodeMappingSchema = createInsertSchema(ptwIndustryCodeMappings, {
  entityType: () => z.enum(PTW_INDUSTRY_CODE_ENTITY_TYPES),
}).omit({ id: true, tenantId: true, createdAt: true });
export type InsertPtwIndustryCodeMapping = z.infer<typeof insertPtwIndustryCodeMappingSchema>;
export type PtwIndustryCodeMapping = typeof ptwIndustryCodeMappings.$inferSelect;

export const ptwTenantIndustryCodeSettings = pgTable("ptw_tenant_industry_code_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  // Whitelist of code families the tenant admin has activated. Empty
  // array (or row absent) is interpreted as "all families enabled" so
  // greenfield tenants get the full catalogue without any explicit
  // configuration step.
  enabledFamilies: text("enabled_families").array().notNull().default([]),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
});
export const insertPtwTenantIndustryCodeSettingsSchema = createInsertSchema(ptwTenantIndustryCodeSettings).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwTenantIndustryCodeSettings = z.infer<typeof insertPtwTenantIndustryCodeSettingsSchema>;
export type PtwTenantIndustryCodeSettings = typeof ptwTenantIndustryCodeSettings.$inferSelect;

/**
 * Resolve the effective enabled-families list for a tenant. An absent
 * settings row OR an empty `enabledFamilies` array both mean "all
 * families enabled" so the catalogue is not silently hidden from a
 * tenant that never visited the settings page.
 */
export function resolvePtwEnabledIndustryCodeFamilies(
  settings: PtwTenantIndustryCodeSettings | undefined | null,
): readonly PtwIndustryCodeFamily[] {
  const raw = settings?.enabledFamilies ?? [];
  if (raw.length === 0) return PTW_INDUSTRY_CODE_FAMILIES;
  const all = new Set<string>(PTW_INDUSTRY_CODE_FAMILIES);
  return raw.filter((f): f is PtwIndustryCodeFamily => all.has(f));
}

// ─── PTW CAP-25 (Task #1378) — Append-Only Audit + Signing Key Registry ──
//
// Two new tables back the DB-level append-only enforcement and the durable
// signing-key registry described in CAP-25:
//
//   1. `ptw_audit_tamper_log` — every UPDATE/DELETE attempt against
//      `ptw_audit_log` is recorded here by the BEFORE-trigger
//      `ptw_audit_log_block_mutation()`, which then **silently cancels**
//      the mutation by `RETURN NULL` (caller sees `0 rows affected`,
//      audit row is unchanged, tamper-log row commits with the calling
//      transaction). RAISE EXCEPTION was the earlier design and is
//      deliberately NOT used: a raised exception aborts the calling
//      statement and rolls back the same-transaction tamper-log INSERT,
//      defeating the forensic guarantee. The trigger runs `SECURITY
//      DEFINER` so the row writes even though INSERT on this table is
//      REVOKEd from PUBLIC — only the function owner writes, so every
//      row is provably the product of a real tamper attempt.
//
//   2. `ptw_signing_keys` — durable registry for PTW signing keys. Two
//      `purpose` families share one shape:
//        * `audit_chain`     — HMAC-SHA256 keys signing `ptw_audit_log`.
//        * `offline_verifier` — Ed25519 keypairs for the UXS-9 offline
//                               claim-bundle verifier.
//      Status is `active | rotated | revoked`. Rotated/revoked keys are
//      retained so historical seals stay verifiable. The Phase 6.B
//      verifier consults this registry via `signing_key_id` on each row.
//
// Both tables are created by `migrations/0116_ptw_cap25_audit_immutability_signing_keys.sql`
// and are also idempotently rebuilt by `server/schema-drift-fix.ts` so a
// cold-start scratch DB (drizzle-kit push) is kept in lock-step.
export const PTW_SIGNING_KEY_PURPOSES = ["audit_chain", "offline_verifier"] as const;
export type PtwSigningKeyPurpose = typeof PTW_SIGNING_KEY_PURPOSES[number];

export const PTW_SIGNING_KEY_STATUSES = ["active", "rotated", "revoked"] as const;
export type PtwSigningKeyStatus = typeof PTW_SIGNING_KEY_STATUSES[number];

export const PTW_TAMPER_LOG_OPERATIONS = ["UPDATE", "DELETE"] as const;
export type PtwTamperLogOperation = typeof PTW_TAMPER_LOG_OPERATIONS[number];

export const ptwSigningKeys = pgTable("ptw_signing_keys", {
  // `keyId` is the natural primary key — every signed row references this
  // value. We deliberately do NOT use a varchar `gen_random_uuid()` PK so
  // the value is operator-meaningful (e.g. `audit-chain-2026Q1-tenant-abc`).
  keyId: text("key_id").primaryKey(),
  // Tenant-scoped keys reference `tenants.id`; platform-wide keys (the
  // legacy `AUDIT_HMAC_KEY` cohort) carry `tenant_id IS NULL`. The FK uses
  // ON DELETE CASCADE so deleting a tenant cleans up its keys with the
  // rest of the tenant data.
  tenantId: text("tenant_id"),
  algorithm: text("algorithm").notNull(),
  purpose: text("purpose").notNull().default("audit_chain"),
  // The encrypted key material. For HMAC keys, this is the raw 32-byte
  // shared secret encrypted under `PII_ENCRYPTION_KEY` (AES-256-GCM via
  // `server/pii-crypto.ts`). For Ed25519, this is the encrypted private
  // half; the public half is published unencrypted in `publicKeyEncoded`.
  keyMaterialEncrypted: text("key_material_encrypted").notNull(),
  // Public key (base64url) for Ed25519 keys; NULL for HMAC keys.
  publicKeyEncoded: text("public_key_encoded"),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  rotatedAt: timestamp("rotated_at", { withTimezone: true }),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  revokedReason: text("revoked_reason"),
  // Cumulative use counter — incremented every time `createPtwAuditLog`
  // signs a row under this key. Useful for rotation triggers ("rotate
  // after N signatures") and forensic investigation.
  usageCount: integer("usage_count").notNull().default(0),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default({}),
}, (t) => ({
  idxPtwSigningKeysTenantPurposeStatus: index("idx_ptw_signing_keys_tenant_purpose_status").on(t.tenantId, t.purpose, t.status),
  idxPtwSigningKeysPurposeStatus: index("idx_ptw_signing_keys_purpose_status").on(t.purpose, t.status),
}));

// `keyId` is operator-supplied (e.g. by `scripts/rotate-ptw-signing-key.ts`)
// so we DO include it in the insert schema. Status defaults to `active`.
export const insertPtwSigningKeySchema = createInsertSchema(ptwSigningKeys).omit({
  createdAt: true,
  rotatedAt: true,
  revokedAt: true,
  revokedReason: true,
  usageCount: true,
});
export type InsertPtwSigningKey = z.infer<typeof insertPtwSigningKeySchema>;
export type PtwSigningKey = typeof ptwSigningKeys.$inferSelect;

export const ptwAuditTamperLog = pgTable("ptw_audit_tamper_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  attemptedAt: timestamp("attempted_at", { withTimezone: true }).notNull().defaultNow(),
  // `db_user` is the Postgres role that attempted the mutation. Application
  // code never connects as `ptw_audit_admin`, so seeing it here is itself
  // an alert-worthy event.
  dbUser: text("db_user").notNull(),
  operation: text("operation").notNull(),
  targetRowId: text("target_row_id"),
  targetPermitId: text("target_permit_id"),
  payloadBefore: jsonb("payload_before"),
  payloadAfter: jsonb("payload_after"),
  clientAddr: text("client_addr"),
  applicationName: text("application_name"),
  // `rejected` for the normal "trigger blocked the mutation" path; `allowed`
  // when ptw_audit_admin DELETEs a row under the documented retention path
  // (audit-the-auditor: every retention DELETE still writes a row here).
  errorState: text("error_state").notNull().default("rejected"),
}, (t) => ({
  idxPtwAuditTamperLogAttemptedAt: index("idx_ptw_audit_tamper_log_attempted_at").on(t.attemptedAt),
  idxPtwAuditTamperLogTargetPermit: index("idx_ptw_audit_tamper_log_target_permit").on(t.targetPermitId, t.attemptedAt),
}));

// The tamper log is written exclusively by the SECURITY DEFINER trigger
// `ptw_audit_log_block_mutation()`. Application code only reads from it
// (regulator inspection panel, alert pipeline). We expose an insert schema
// for completeness + storage tests, but routes MUST NOT INSERT into this
// table directly.
export const insertPtwAuditTamperLogSchema = createInsertSchema(ptwAuditTamperLog).omit({
  id: true,
  attemptedAt: true,
});
export type InsertPtwAuditTamperLog = z.infer<typeof insertPtwAuditTamperLogSchema>;
export type PtwAuditTamperLogEntry = typeof ptwAuditTamperLog.$inferSelect;

// ── PTW CAP-24 (Task #1377) — Configuration Sandbox + Bundle Apply ────
//
// Three additive tables that together implement the production-safe
// "Sandbox" lifecycle described in CAP-24:
//
//   1. `ptw_sandboxes`            — one row per tenant. Tracks the link to
//                                    the cloned sandbox tenant, lifecycle
//                                    status (`provisioning`/`ready`/`expired`/
//                                    `cleaning`/`destroyed`), TTL expiry,
//                                    last-activity timestamp + the snapshot
//                                    counters so the UI can render "cloned
//                                    N templates / M permits".
//   2. `ptw_config_bundles`       — serialised configuration artefacts
//                                    exported from the sandbox (templates,
//                                    role mappings, numbering, capacity,
//                                    retention, terminology, language
//                                    overrides). Carries a deterministic
//                                    SHA-256 checksum so the production
//                                    import handler can refuse a tampered
//                                    bundle.
//   3. `ptw_config_bundle_imports` — one row per **production** import
//                                    attempt. Records the diff vs target,
//                                    impact analysis (consumes A4.5
//                                    publish-preview + UXS-8 what-if), the
//                                    accept/reject decisions the admin
//                                    made, the re-auth verification status,
//                                    and the final apply outcome.
//
// All three are tenant-scoped (the sandbox row's `tenant_id` is the
// **production** tenant — the cloned sandbox tenant id lives in
// `sandbox_tenant_id`). Imports always have a target_tenant_id and a
// source bundle id; the canonical audit row for the import lives in
// `ptw_audit_log` keyed by the synthetic `permitId = sandbox-import:{id}`.

export const PTW_SANDBOX_STATUSES = [
  "provisioning",
  "ready",
  "expired",
  "cleaning",
  "destroyed",
  "failed",
] as const;
export type PtwSandboxStatus = (typeof PTW_SANDBOX_STATUSES)[number];

export type PtwSandboxSnapshotSummary = {
  // Counters from the clone step. The UI surfaces these in the Console
  // Settings → Sandbox panel ("Cloned 14 templates / 86 permits").
  templates?: number;
  steps?: number;
  roleMappings?: number;
  permits?: number;
  exportConfigs?: number;
  privacyConfigs?: number;
  manualWorkers?: number;
  manualEquipment?: number;
  manualSites?: number;
  // PII scrubbing summary — counters of fields the cloner redacted so the
  // tenant admin can audit the anonymisation.
  scrubbedRequestorNames?: number;
  scrubbedComments?: number;
  scrubbedPhoneNumbers?: number;
};

/** Default TTL — sandbox auto-expires 14 days after provisioning unless
 * extended (max one extension by default). Mirrored by the cleanup sweep
 * in `server/services/ptw-sandbox-service.ts`. */
export const PTW_SANDBOX_DEFAULT_TTL_DAYS = 14;
/** Maximum number of times a sandbox can be extended. */
export const PTW_SANDBOX_MAX_EXTENSIONS = 2;
/** How many days each extension adds. */
export const PTW_SANDBOX_EXTENSION_DAYS = 14;

export const ptwSandboxes = pgTable("ptw_sandboxes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // The **production** tenant the sandbox was cloned from. UNIQUE so each
  // production tenant can only have one sandbox at a time (matches the
  // CAP-24 "out of scope: multi-tenant sandbox" guard).
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  // The cloned **sandbox** tenant id. Points at a real `tenants` row that
  // has its own slug + isolated rows for every PTW table.
  sandboxTenantId: text("sandbox_tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("provisioning"),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  extensionsUsed: integer("extensions_used").notNull().default(0),
  lastActivityAt: timestamp("last_activity_at", { withTimezone: true }).defaultNow().notNull(),
  snapshotSummary: jsonb("snapshot_summary").$type<PtwSandboxSnapshotSummary>().notNull().default({}),
  errorMessage: text("error_message"),
  provisionedBy: text("provisioned_by"),
  provisionedByName: text("provisioned_by_name"),
  destroyedAt: timestamp("destroyed_at", { withTimezone: true }),
  destroyedBy: text("destroyed_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  uxPtwSandboxesTenant: uniqueIndex("ux_ptw_sandboxes_tenant").on(t.tenantId),
  idxPtwSandboxesStatus: index("idx_ptw_sandboxes_status").on(t.status),
  idxPtwSandboxesExpiresAt: index("idx_ptw_sandboxes_expires_at").on(t.expiresAt),
  // Reverse lookup — given a sandbox tenant id, find the production it
  // mirrors. Used by the cleanup sweep + the apply-to-prod handler so a
  // bundle exported from a sandbox can be tracebacked to its origin.
  idxPtwSandboxesSandboxTenantId: index("idx_ptw_sandboxes_sandbox_tenant_id").on(t.sandboxTenantId),
}));
export const insertPtwSandboxSchema = createInsertSchema(ptwSandboxes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwSandbox = z.infer<typeof insertPtwSandboxSchema>;
export type PtwSandbox = typeof ptwSandboxes.$inferSelect;

/** Top-level shape of a Configuration Bundle payload. Each section is
 * optional so older bundles without (e.g.) the privacy section still
 * deserialise cleanly. The serialiser strips ids and tenant pointers so
 * the bundle is portable between tenants. */
export type PtwConfigBundlePayload = {
  schemaVersion: number;
  source: { tenantId: string; tenantName: string; sandboxTenantId?: string | null };
  exportedAt: string;
  sections: {
    templates?: Array<{
      // Stable name+version pair so the import handler can match against
      // the target tenant's templates without leaking source ids.
      name: string;
      version: number;
      template: Omit<PtwWorkflowTemplate, "id" | "tenantId" | "createdAt" | "updatedAt">;
      steps: Array<Omit<PtwWorkflowStep, "id" | "tenantId" | "templateId">>;
    }>;
    roleMappings?: Array<Omit<PtwRoleMapping, "id" | "tenantId">>;
    exportDefaults?: Omit<PtwTenantExportDefaults, "id" | "tenantId" | "createdAt" | "updatedAt"> | null;
    privacyConfig?: Omit<PtwTenantPrivacyConfig, "id" | "tenantId" | "createdAt" | "updatedAt"> | null;
  };
};

export const PTW_CONFIG_BUNDLE_SECTIONS = [
  "templates",
  "roleMappings",
  "exportDefaults",
  "privacyConfig",
] as const;
export type PtwConfigBundleSection = (typeof PTW_CONFIG_BUNDLE_SECTIONS)[number];

export const ptwConfigBundles = pgTable("ptw_config_bundles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // The tenant the bundle was exported FROM (the sandbox tenant when the
  // bundle was produced via the sandbox export endpoint, or the prod
  // tenant when produced directly).
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // Optional pointer to the sandbox row this export was produced from. NULL
  // when the bundle was exported directly from production (rare).
  sandboxId: text("sandbox_id").references(() => ptwSandboxes.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  description: text("description"),
  payload: jsonb("payload").$type<PtwConfigBundlePayload>().notNull(),
  // SHA-256 of the canonical JSON representation of `payload`. Computed
  // server-side at insert. The apply handler recomputes and compares before
  // applying any changes — refuses on mismatch.
  checksum: text("checksum").notNull(),
  // Lightweight headline counters (mirrored from `payload.sections`) that
  // the bundle library can render without parsing the payload.
  sectionCounts: jsonb("section_counts").$type<Record<PtwConfigBundleSection, number>>().notNull().default({} as Record<PtwConfigBundleSection, number>),
  createdBy: text("created_by"),
  createdByName: text("created_by_name"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwConfigBundlesTenantCreated: index("idx_ptw_config_bundles_tenant_created").on(t.tenantId, t.createdAt),
  idxPtwConfigBundlesSandbox: index("idx_ptw_config_bundles_sandbox").on(t.sandboxId),
}));
export const insertPtwConfigBundleSchema = createInsertSchema(ptwConfigBundles).omit({
  id: true,
  tenantId: true,
  createdAt: true,
});
export type InsertPtwConfigBundle = z.infer<typeof insertPtwConfigBundleSchema>;
export type PtwConfigBundle = typeof ptwConfigBundles.$inferSelect;

export const PTW_CONFIG_BUNDLE_IMPORT_STATUSES = [
  "pending",      // import row created, awaiting decisions
  "previewed",    // diff + impact analysis ran, awaiting apply
  "reauth_required",
  "applying",
  "completed",
  "failed",
  "rejected",     // operator cancelled / reauth failed
] as const;
export type PtwConfigBundleImportStatus = (typeof PTW_CONFIG_BUNDLE_IMPORT_STATUSES)[number];

export type PtwConfigBundleImportDecision = {
  section: PtwConfigBundleSection;
  // Item identifier within the section. For templates this is the
  // `name@version` pair. For singleton sections (export defaults, privacy)
  // it's the literal string `"default"`.
  key: string;
  action: "accept" | "reject";
};

export type PtwConfigBundleImportDiffEntry = {
  section: PtwConfigBundleSection;
  key: string;
  // `new`     — section item exists in bundle but not target.
  // `update`  — section item exists in both, payloads differ.
  // `noop`    — section item exists in both, payloads identical.
  // `removed` — exists in target but not bundle (informational only;
  //             the apply handler never deletes target items).
  verdict: "new" | "update" | "noop" | "removed";
  fieldsChanged?: string[];
};

export type PtwConfigBundleImportImpact = {
  // Aggregated impact counters (consumes UXS-8 what-if + A4.5 preview).
  affectedTemplates: number;
  affectedActivePermits: number;
  newlyBlockingGates: number;
  actorRoleChanges: number;
  // Per-template what-if pointers — array of `{ templateName, summary }`.
  perTemplate: Array<{
    templateName: string;
    wouldPauseForAck: number;
    wouldFailNewGate: number;
    gateChangeCount: number;
    actorRoleChangeCount: number;
  }>;
};

export const ptwConfigBundleImports = pgTable("ptw_config_bundle_imports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Production tenant the bundle is being applied to.
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  bundleId: text("bundle_id").notNull().references(() => ptwConfigBundles.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("pending"),
  diff: jsonb("diff").$type<PtwConfigBundleImportDiffEntry[]>().notNull().default([]),
  impact: jsonb("impact").$type<PtwConfigBundleImportImpact>().notNull().default({
    affectedTemplates: 0,
    affectedActivePermits: 0,
    newlyBlockingGates: 0,
    actorRoleChanges: 0,
    perTemplate: [],
  }),
  decisions: jsonb("decisions").$type<PtwConfigBundleImportDecision[]>().notNull().default([]),
  // Re-auth proof. The apply handler refuses unless this is true and the
  // re-auth happened within the last N minutes (server enforced).
  reauthVerified: boolean("reauth_verified").notNull().default(false),
  reauthVerifiedAt: timestamp("reauth_verified_at", { withTimezone: true }),
  // Outcome counters (populated on apply).
  appliedSections: jsonb("applied_sections").$type<Record<PtwConfigBundleSection, number>>().notNull().default({} as Record<PtwConfigBundleSection, number>),
  errorMessage: text("error_message"),
  requestedBy: text("requested_by"),
  requestedByName: text("requested_by_name"),
  appliedBy: text("applied_by"),
  appliedByName: text("applied_by_name"),
  appliedAt: timestamp("applied_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwConfigBundleImportsTenantCreated: index("idx_ptw_config_bundle_imports_tenant_created").on(t.tenantId, t.createdAt),
  idxPtwConfigBundleImportsBundle: index("idx_ptw_config_bundle_imports_bundle").on(t.bundleId),
  idxPtwConfigBundleImportsTenantStatus: index("idx_ptw_config_bundle_imports_tenant_status").on(t.tenantId, t.status),
}));
export const insertPtwConfigBundleImportSchema = createInsertSchema(ptwConfigBundleImports).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwConfigBundleImport = z.infer<typeof insertPtwConfigBundleImportSchema>;
export type PtwConfigBundleImport = typeof ptwConfigBundleImports.$inferSelect;

// ─── PTW CAP-27 (Task #1380) — Cross-Permit Worker Concurrency &
//                                Exposure Conflicts ─────────────────
//
// A worker can legitimately appear on more than one active permit
// (multi-permit operators, escort workers, supervisors), but three
// classes of cross-permit conditions must be flagged and — at step
// completion — blocked:
//
//   1. ZONE PROXIMITY — two overlapping permits in the same hour assign
//      the same worker to physically distant zones (different sites,
//      or different non-adjacent areas under the same site). The
//      worker cannot be in two places, so the engine surfaces a
//      `worker_conflict` warning at create-time and blocks the next
//      `complete_step` action until the worker is removed from one
//      of the conflicting permits.
//
//   2. EXPOSURE BUDGET — for hazardous-class permits (H2S, benzene,
//      asbestos, radiation, …) the regulator caps cumulative exposure
//      time per worker per rolling window (e.g. 8h H2S in any 24h).
//      The engine sums hours of overlap across every active permit
//      with the same hazard class and warns / blocks once the budget
//      is reached.
//
//   3. FATIGUE — max consecutive permit-active hours per worker.
//      Permits whose `issued_start_time`..`issued_expiry_time` window
//      stitches into a continuous block exceeding the per-worker-class
//      fatigue ceiling trigger a fatigue conflict.
//
// Limits are tenant-configurable per worker class and per hazard
// class via `ptw_exposure_limits`. A platform-default seed is shipped
// in `PTW_DEFAULT_EXPOSURE_LIMITS` so every tenant has working caps
// immediately after provisioning.
//
// Detected conflicts are persisted in `ptw_worker_conflict_log` for
// reports / trends (CAP-8 dashboards) and stamped into
// `ptw_permits.permit_metadata.workerConflicts` so the `worker_conflict`
// pseudo-state lights up on the Console badge stack.
//
// The override path mirrors SIMOPS: warnings are advisory at create
// time (an authorized actor can proceed with an attestation) but
// `complete_step` is hard-blocked until the worker assignment is
// resolved.

export const PTW_WORKER_CONFLICT_TYPES = [
  "zone_proximity",
  "exposure_budget",
  "fatigue",
] as const;
export type PtwWorkerConflictType = typeof PTW_WORKER_CONFLICT_TYPES[number];

export const PTW_WORKER_CONFLICT_SEVERITIES = ["warning", "blocking"] as const;
export type PtwWorkerConflictSeverity = typeof PTW_WORKER_CONFLICT_SEVERITIES[number];

/**
 * One conflict entry. Stored as JSON inside `permit_metadata.workerConflicts`
 * and as a row in `ptw_worker_conflict_log` for reporting.
 */
export interface CrossPermitWorkerConflict {
  /** Stable id for dedupe across re-evaluations. */
  id: string;
  type: PtwWorkerConflictType;
  severity: PtwWorkerConflictSeverity;
  workerId: string;
  workerLabel?: string;
  /** Worker class used to look up the limit (default "default"). */
  workerClass: string;
  /** The other permit(s) that participate in the conflict. */
  conflictingPermitIds: string[];
  /** Hazard class (only for `exposure_budget`). */
  hazardClass?: string;
  /** Window the limit applies to (hours; only for `exposure_budget`/`fatigue`). */
  windowHours?: number;
  /** Limit value that was breached. */
  limitHours?: number;
  /** Observed value at evaluation time. */
  observedHours?: number;
  /** Plain-language explanation surfaced on the Console badge tooltip. */
  message: string;
  /** ISO timestamp of the evaluation. */
  detectedAt: string;
}

/**
 * Per-tenant configurable limits — one row per (workerClass, hazardClass).
 * `hazardClass = "*"` is the catch-all row used for fatigue / cumulative
 * caps that don't pivot on a specific hazard.
 */
export const ptwExposureLimits = pgTable("ptw_exposure_limits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  /** "default" matches every worker; can be overridden per role / certification class. */
  workerClass: text("worker_class").notNull().default("default"),
  /** Hazard slug (e.g. "h2s", "benzene", "radiation") or "*" for the catch-all row. */
  hazardClass: text("hazard_class").notNull().default("*"),
  /** Rolling window the cumulative cap is measured over (hours). */
  limitWindowHours: integer("limit_window_hours").notNull().default(24),
  /** Cap on cumulative permit-active hours of exposure within the window. */
  maxExposureHours: integer("max_exposure_hours").notNull().default(8),
  /** Cap on consecutive permit-active hours (fatigue). 0 disables the check. */
  maxConsecutivePermitHours: integer("max_consecutive_permit_hours").notNull().default(16),
  /** Free-text regulator citation surfaced in the warning banner. */
  regulatorReference: text("regulator_reference"),
  /** Whether the row is enforced. Disabled rows are kept for audit. */
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  uniqPtwExposureLimits: uniqueIndex("uniq_ptw_exposure_limits")
    .on(t.tenantId, t.workerClass, t.hazardClass),
  idxPtwExposureLimitsTenant: index("idx_ptw_exposure_limits_tenant").on(t.tenantId),
}));

export const insertPtwExposureLimitSchema = createInsertSchema(ptwExposureLimits).omit({
  id: true, tenantId: true, createdAt: true, updatedAt: true,
}).extend({
  workerClass: z.string().min(1).max(120).default("default"),
  hazardClass: z.string().min(1).max(120).default("*"),
  limitWindowHours: z.number().int().min(1).max(720).default(24),
  maxExposureHours: z.number().int().min(0).max(720).default(8),
  maxConsecutivePermitHours: z.number().int().min(0).max(720).default(16),
});
export type InsertPtwExposureLimit = z.infer<typeof insertPtwExposureLimitSchema>;
export type PtwExposureLimit = typeof ptwExposureLimits.$inferSelect;

/**
 * Platform default seed. Applied at tenant provisioning + used as the
 * fallback when no row matches the (workerClass, hazardClass) lookup.
 * Values reflect industry-typical regulator caps (UK HSE H2S 8h TWA,
 * generic 16h consecutive permit-active fatigue cap).
 */
export const PTW_DEFAULT_EXPOSURE_LIMITS: ReadonlyArray<{
  workerClass: string;
  hazardClass: string;
  limitWindowHours: number;
  maxExposureHours: number;
  maxConsecutivePermitHours: number;
  regulatorReference?: string;
}> = [
  { workerClass: "default", hazardClass: "*", limitWindowHours: 24, maxExposureHours: 24, maxConsecutivePermitHours: 16, regulatorReference: "Working Time Directive — 16h consecutive cap" },
  { workerClass: "default", hazardClass: "h2s", limitWindowHours: 24, maxExposureHours: 8, maxConsecutivePermitHours: 16, regulatorReference: "HSE EH40 — H2S 8h TWA" },
  { workerClass: "default", hazardClass: "benzene", limitWindowHours: 24, maxExposureHours: 4, maxConsecutivePermitHours: 16, regulatorReference: "OSHA 1910.1028 — Benzene PEL" },
  { workerClass: "default", hazardClass: "radiation", limitWindowHours: 24, maxExposureHours: 2, maxConsecutivePermitHours: 16, regulatorReference: "IRR17 — Radiation dose limits" },
];

/**
 * Per-conflict log row used by reports + trend dashboards. One row per
 * detected conflict per evaluation pass. The `permitIds` array preserves
 * every permit that participated so the analytics layer can pivot by
 * permit type / site without a join.
 */
export const ptwWorkerConflictLog = pgTable("ptw_worker_conflict_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id").notNull(),
  workerId: text("worker_id").notNull(),
  workerClass: text("worker_class").notNull().default("default"),
  conflictType: text("conflict_type").notNull(),
  severity: text("severity").notNull().default("warning"),
  hazardClass: text("hazard_class"),
  windowHours: integer("window_hours"),
  limitHours: integer("limit_hours"),
  observedHours: integer("observed_hours"),
  conflictingPermitIds: text("conflicting_permit_ids").array().notNull().default([]),
  message: text("message").notNull(),
  /** Either `creation` (advisory) or `step_completion` (blocked). */
  evaluationPhase: text("evaluation_phase").notNull().default("creation"),
  /** When `severity = "warning"` and the actor proceeded anyway, the override actor + reason are stamped here. */
  overrideActorId: text("override_actor_id"),
  overrideReason: text("override_reason"),
  detectedAt: timestamp("detected_at", { withTimezone: true }).defaultNow().notNull(),
}, (t) => ({
  idxPtwWorkerConflictLogTenant: index("idx_ptw_worker_conflict_log_tenant").on(t.tenantId),
  idxPtwWorkerConflictLogPermit: index("idx_ptw_worker_conflict_log_permit").on(t.permitId),
  idxPtwWorkerConflictLogWorker: index("idx_ptw_worker_conflict_log_worker").on(t.workerId),
  idxPtwWorkerConflictLogTrend: index("idx_ptw_worker_conflict_log_trend").on(t.tenantId, t.conflictType, t.detectedAt),
}));

export const insertPtwWorkerConflictLogSchema = createInsertSchema(ptwWorkerConflictLog).omit({
  id: true, tenantId: true, detectedAt: true,
}).extend({
  conflictType: z.enum(PTW_WORKER_CONFLICT_TYPES),
  severity: z.enum(PTW_WORKER_CONFLICT_SEVERITIES).default("warning"),
  evaluationPhase: z.enum(["creation", "step_completion"]).default("creation"),
});
export type InsertPtwWorkerConflictLog = z.infer<typeof insertPtwWorkerConflictLogSchema>;
export type PtwWorkerConflictLog = typeof ptwWorkerConflictLog.$inferSelect;
// ── PTW DOC-2 (Task #1384) — Regulator-Ready Audit Pack Registry ─────
//
// `ptw_audit_packs` is the durable provenance registry for every
// "Regulator Audit Pack" generated by the Console Settings → DOC-2
// generator. One row per requested pack. The actual PDF lives in
// object storage (referenced by `objectKey`); this row carries the
// scope filters that produced the pack, the section counts that landed
// in the artefact, and the Ed25519 (UXS-9) signing envelope so an
// auditor can verify provenance against the published key list.
//
// Lifecycle:
//   `queued`     — request accepted, background_job inserted.
//   `building`   — worker is aggregating sections + rendering the PDF.
//   `signing`    — PDF rendered, awaiting signing-key fetch + sign.
//   `ready`      — signed + uploaded to object storage; `objectKey`
//                  + `signature` populated; downloadable.
//   `failed`     — terminal failure; `errorMessage` populated.
//
// The `signature` envelope is `{ algorithm, signedAt, keyId,
// publicKeyEncoded, contentHash, signature }`. `algorithm` is
// `Ed25519` when the active offline_verifier signing key was available
// (UXS-9 keys), or `unsigned` when no key was registered for the
// tenant — the pack still ships but the verification certificate
// page declares the unsigned state explicitly so a regulator does not
// mistake the artefact for verified.

export const PTW_AUDIT_PACK_STATUSES = [
  "queued",
  "building",
  "signing",
  "ready",
  "failed",
] as const;
export type PtwAuditPackStatus = (typeof PTW_AUDIT_PACK_STATUSES)[number];

/**
 * Scope filters captured from the Console Settings request. All fields
 * are optional — a pack with no scope matches every permit in the
 * tenant within the date range. The aggregator interprets `siteIds`,
 * `permitTypes`, and `operatorIds` as inclusive set filters; an empty
 * array means "no filter on this dimension".
 */
export type PtwAuditPackScope = {
  /** Inclusive ISO-8601 lower bound on `ptw_permits.created_at`. */
  dateFrom?: string | null;
  /** Inclusive ISO-8601 upper bound on `ptw_permits.created_at`. */
  dateTo?: string | null;
  siteIds?: string[];
  permitTypes?: string[];
  operatorIds?: string[];
  /** BCP-47 language codes — defaults to `["en", "ar"]` per CAP-26. */
  languages?: string[];
  /** Free-text label the requestor attaches for the artefact cover. */
  label?: string;
};

/**
 * Aggregate counters for the sections that landed in the artefact.
 * Surfaced on the Console Settings list so a tenant admin can size
 * packs at a glance ("84 permits / 12 isolations / 314 audit rows").
 */
export type PtwAuditPackSectionCounts = {
  permits?: number;
  auditLogRows?: number;
  signingKeys?: number;
  longTermIsolations?: number;
  toolboxTalks?: number;
  fieldSafetyChecks?: number;
  attachments?: number;
  industryCodes?: number;
  trainingExclusions?: number;
  drillDisclosures?: number;
};

/**
 * Ed25519 signing envelope embedded in the artefact's verification
 * certificate page and stored on the registry row. `algorithm` is
 * `unsigned` when no offline_verifier key was registered for the
 * tenant or platform — the artefact still ships, but the verification
 * certificate declares the unsigned state explicitly.
 */
export type PtwAuditPackSignatureEnvelope = {
  algorithm: "Ed25519" | "unsigned";
  signedAt: string;
  keyId: string | null;
  publicKeyEncoded: string | null;
  /** Hex SHA-256 of the canonical PDF bytes. */
  contentHash: string;
  /** base64url-encoded Ed25519 signature over `contentHash`. NULL when unsigned. */
  signature: string | null;
};

export const ptwAuditPacks = pgTable("ptw_audit_packs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("queued"),
  /** Free-text label captured from the requestor (cover-page subtitle). */
  label: text("label"),
  scope: jsonb("scope").$type<PtwAuditPackScope>().notNull().default({}),
  sectionCounts: jsonb("section_counts").$type<PtwAuditPackSectionCounts>().notNull().default({}),
  signature: jsonb("signature").$type<PtwAuditPackSignatureEnvelope | null>(),
  /** Object key in the bucket once the artefact is uploaded; NULL until ready. */
  objectKey: text("object_key"),
  byteSize: integer("byte_size"),
  /** Background job id that produced the pack — joins to background_jobs.id. */
  jobId: text("job_id"),
  errorMessage: text("error_message"),
  requestedById: text("requested_by_id"),
  requestedByName: text("requested_by_name"),
  requestedByRole: text("requested_by_role"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  completedAt: timestamp("completed_at", { withTimezone: true }),
}, (t) => ({
  // Console Settings list — newest first, scoped to one tenant.
  idxPtwAuditPacksTenantCreated: index("idx_ptw_audit_packs_tenant_created")
    .on(t.tenantId, t.createdAt),
  // Operator dashboards — slice by status (queued / failed / ready).
  idxPtwAuditPacksTenantStatus: index("idx_ptw_audit_packs_tenant_status")
    .on(t.tenantId, t.status),
}));
export const insertPtwAuditPackSchema = createInsertSchema(ptwAuditPacks).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});
export type InsertPtwAuditPack = z.infer<typeof insertPtwAuditPackSchema>;
export type PtwAuditPack = typeof ptwAuditPacks.$inferSelect;

// ─── PTW Phase 12.B — Tenant Terminology Config (Task #1293) ───────────────
//
// Industries and regions use different words for the same PTW role / concept.
// E.g. "Issuer" (oil & gas) vs "Permit Authority" (utilities) vs
// "Authorising Officer" (UK HSE). Hard-coding English labels in the engine /
// UI breaks adoption in those industries. This table captures per-tenant
// overrides on top of a curated platform default (defined in
// `server/engines/ptw-config-engine.ts → PTW_DEFAULT_TERMINOLOGY`).
//
// Lookup precedence (resolved by `resolvePtwTerminology(tenantId)`):
//   tenant override  >  platform default
//
// Unknown keys in the override jsonb are silently dropped. The schema is
// intentionally a flat string→string map — no nested values, no per-locale
// alternates (locale alternates are handled by the i18n layer in Phase 12.A).
// A tenant that wants per-language terminology should configure terminology
// once and rely on i18n catalogs to translate the resolved label.
export const PTW_TERMINOLOGY_KEYS = [
  // Top-level concepts.
  "permit",
  "permits",
  // Actor roles.
  "issuer",
  "approver",
  "reviewer",
  "supervisor",
  "requestor",
  "holder",
  "performingCompany",
  "issuingCompany",
  // Locations.
  "worksite",
  "area",
  // Permit types.
  "hotWork",
  "coldWork",
  "confinedSpace",
  "workingAtHeight",
  "excavation",
  "electrical",
  "lifting",
  "diving",
  "radiography",
  // Safety controls.
  "gasTest",
  "isolation",
  "loto",
  "ppe",
  "toolboxTalk",
  "jsa",
  // Document labels.
  "permitNumber",
  "amendment",
  "extension",
] as const;
export type PtwTerminologyKey = (typeof PTW_TERMINOLOGY_KEYS)[number];

export const ptwTenantTerminology = pgTable("ptw_tenant_terminology", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().unique().references(() => tenants.id, { onDelete: "cascade" }),
  // Flat string→string overrides keyed by `PtwTerminologyKey`. Unknown keys
  // are dropped at the resolver layer.
  terms: jsonb("terms").$type<Partial<Record<PtwTerminologyKey, string>>>().notNull().default({}),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertPtwTenantTerminologySchema = createInsertSchema(ptwTenantTerminology).omit({
  id: true,
  tenantId: true,
  updatedAt: true,
  createdAt: true,
});
export type InsertPtwTenantTerminology = z.infer<typeof insertPtwTenantTerminologySchema>;
export type PtwTenantTerminology = typeof ptwTenantTerminology.$inferSelect;

// ─── PTW Phase 12.B — Locale / Units / Date / Time / Shift Config ──────────
//
// Tenants in different regions need different units (kPa vs psi), date
// formats (DD/MM vs MM/DD), 12h/24h clocks, IANA timezones, weekend policies
// (GCC = Fri/Sat; US = Sat/Sun), and shift calendars (8h three-shift vs
// 12h two-shift vs continental). This table holds those settings, with a
// per-site row taking precedence over the tenant default row.
//
// Resolution precedence (`resolvePtwLocaleConfig(tenantId, siteId)`):
//   site override (siteId IS NOT NULL)  >  tenant default (siteId IS NULL)
//   >  PTW_DEFAULT_LOCALE_CONFIG  (server/engines/ptw-config-engine.ts)
//
// Distinct from `tenant.settings.timezone/locale` (read by
// `server/utils/tenant-locale.ts`): that path supplies the BCP-47 locale and
// IANA timezone for date formatting. This table extends that with PTW-
// specific dimensions (units, weekend, shift calendar) AND lets a site
// override the tenant timezone (e.g. a multi-region tenant with a UAE site
// and an India site). The resolver in tenant-locale.ts continues to read
// from `tenant.settings` for backwards compatibility; PTW callers that
// want the richer structure use the new resolver instead.
export const PTW_UNITS_SYSTEMS = ["metric", "imperial", "mixed"] as const;
export type PtwUnitsSystem = (typeof PTW_UNITS_SYSTEMS)[number];

export const PTW_PRESSURE_UNITS = ["kpa", "psi", "bar", "atm"] as const;
export type PtwPressureUnit = (typeof PTW_PRESSURE_UNITS)[number];

export const PTW_TEMPERATURE_UNITS = ["c", "f"] as const;
export type PtwTemperatureUnit = (typeof PTW_TEMPERATURE_UNITS)[number];

export const PTW_LENGTH_UNITS = ["m", "ft"] as const;
export type PtwLengthUnit = (typeof PTW_LENGTH_UNITS)[number];

export const PTW_WIND_SPEED_UNITS = ["ms", "mph", "kmh", "knots"] as const;
export type PtwWindSpeedUnit = (typeof PTW_WIND_SPEED_UNITS)[number];

export const PTW_GAS_CONCENTRATION_UNITS = ["ppm", "percent", "mg_m3", "lel_percent"] as const;
export type PtwGasConcentrationUnit = (typeof PTW_GAS_CONCENTRATION_UNITS)[number];

export const PTW_DATE_FORMATS = ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD-MMM-YYYY"] as const;
export type PtwDateFormat = (typeof PTW_DATE_FORMATS)[number];

export const PTW_TIME_FORMATS = ["24h", "12h"] as const;
export type PtwTimeFormat = (typeof PTW_TIME_FORMATS)[number];

export const PTW_WEEKDAY_NAMES = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
] as const;
export type PtwWeekdayName = (typeof PTW_WEEKDAY_NAMES)[number];

export interface PtwShiftDefinitionConfig {
  /** Unique shift id, e.g. `morning`, `evening`, `night`. */
  id: string;
  /** Display label, e.g. "Morning". */
  label: string;
  /** Local-time start (HH:mm). */
  startTime: string;
  /** Local-time end (HH:mm). May wrap past midnight (e.g. 18:00 → 06:00). */
  endTime: string;
}

export interface PtwShiftCalendarConfig {
  /** Ordered list of shift definitions covering the work day. */
  shifts: PtwShiftDefinitionConfig[];
  /**
   * Rotation pattern that issuers see in the handover UI. Free-form text
   * because patterns vary widely (4-on/4-off, continental, dupont…).
   */
  rotationPattern?: string;
  /** The shift id that is "active by default" when issuance time is unknown. */
  defaultShiftId?: string;
}

export const ptwLocaleConfig = pgTable("ptw_locale_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // NULL = tenant-default row; non-NULL = per-site override row.
  siteId: text("site_id").references(() => sites.id, { onDelete: "cascade" }),
  unitsSystem: text("units_system").notNull().default("metric"),
  pressureUnit: text("pressure_unit").notNull().default("kpa"),
  temperatureUnit: text("temperature_unit").notNull().default("c"),
  lengthUnit: text("length_unit").notNull().default("m"),
  windSpeedUnit: text("wind_speed_unit").notNull().default("ms"),
  gasConcentrationUnit: text("gas_concentration_unit").notNull().default("ppm"),
  dateFormat: text("date_format").notNull().default("YYYY-MM-DD"),
  timeFormat: text("time_format").notNull().default("24h"),
  /** IANA timezone (e.g. "Asia/Riyadh"). */
  timeZone: text("time_zone").notNull().default("UTC"),
  /** Weekend day names — typically 2 entries; can be 1 (Israel: Sat) or 0. */
  weekendDays: text("weekend_days").array().notNull().default(sql`ARRAY['Saturday','Sunday']::text[]`),
  shiftCalendar: jsonb("shift_calendar").$type<PtwShiftCalendarConfig>().notNull().default({ shifts: [] }),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // One tenant-default row + at most one row per site. We use a partial-
  // unique index pattern: (tenant_id) where site_id IS NULL  AND
  // (tenant_id, site_id) where site_id IS NOT NULL — implemented via two
  // separate indexes since Postgres supports partial unique indexes.
  uxTenantDefault: uniqueIndex("ux_ptw_locale_config_tenant_default")
    .on(t.tenantId)
    .where(sql`site_id IS NULL`),
  uxSiteOverride: uniqueIndex("ux_ptw_locale_config_tenant_site")
    .on(t.tenantId, t.siteId)
    .where(sql`site_id IS NOT NULL`),
  // Hot lookup: list all rows for a tenant (Settings UI).
  idxTenantId: index("idx_ptw_locale_config_tenant").on(t.tenantId),
}));
export const insertPtwLocaleConfigSchema = createInsertSchema(ptwLocaleConfig).omit({
  id: true,
  updatedAt: true,
  createdAt: true,
});
export type InsertPtwLocaleConfig = z.infer<typeof insertPtwLocaleConfigSchema>;
export type PtwLocaleConfig = typeof ptwLocaleConfig.$inferSelect;

// ─── PTW Phase 12.B — Regulatory Retention Config (Task #1293) ─────────────
//
// Different regulators require different retention periods for different PTW
// record classes. E.g. UK HSE requires 5 years for hot-work records; OSHA
// 30 years for some chemical records; Saudi Aramco demands 10 years for
// permit + isolation register; ADNOC requires permit_records permanently.
//
// This table is the regulatory source-of-truth: one row per (tenant,
// recordClass). It is consumed by:
//
//   1. The PTW attachment retention sweep (`ptw-attachment-retention.ts`):
//      regulatory rows for `permit_attachments` / `step_attachments` /
//      `long_term_isolation_register` (etc.) override the generic
//      `tenant_retention_overrides` table for the equivalent policyKey.
//      `legalHold = true` on the regulatory row blocks the sweep for that
//      tenant + recordClass even when the per-row legalHold and per-tenant
//      override are both false.
//
//   2. The future regulator-export job: a row with `exportRequired = true`
//      and an `exportFormat` is included in the scheduled regulator pack.
//      Phase 12.B captures the metadata; the export pipeline ships in 12.C.
//
// `recordClass` is intentionally a bounded enum — adding a class requires
// engineering work to wire the sweep + export, so a free-form text field
// would invite drift.
export const PTW_RECORD_CLASSES = [
  "permit_records",          // ptw_permits row metadata (kept forever)
  "permit_attachments",      // ptw_permits.attachments JSONB column
  "step_attachments",        // ptw_step_instances.attachments JSONB column
  "gas_test_logs",           // field_safety_check_records (gas_test + hot_work_gas_test)
  "isolation_register",      // ptw_isolations
  "audit_trail",             // ptw_audit_log entries
  "toolbox_talks",           // ptw_native_toolbox_talks (+ attendance)
  "long_term_isolation_register", // ptw_long_term_isolation_register
  "external_approvals",      // ptw_external_approvals
] as const;
export type PtwRecordClass = (typeof PTW_RECORD_CLASSES)[number];

export const PTW_EXPORT_FORMATS = ["pdf_a", "csv", "xml", "json", "zip"] as const;
export type PtwExportFormat = (typeof PTW_EXPORT_FORMATS)[number];

export const ptwTenantRetentionConfig = pgTable("ptw_tenant_retention_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  recordClass: text("record_class").notNull(),
  /** Days to retain. Sweep engine treats this as a floor (max with default). */
  retentionDays: integer("retention_days").notNull(),
  /** Free-form regulator name, e.g. "OSHA" / "HSE_UK" / "ADNOC". */
  jurisdiction: text("jurisdiction"),
  /** Citation, e.g. "29 CFR 1910.119(p)(3)". */
  legalBasis: text("legal_basis"),
  /** When true, sweep engine treats this tenant+class as held. */
  legalHold: boolean("legal_hold").notNull().default(false),
  /** When true, the regulator-export job picks rows of this class up. */
  exportRequired: boolean("export_required").notNull().default(false),
  /** Required output format for the regulator export. */
  exportFormat: text("export_format"),
  notes: text("notes"),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uxTenantClass: uniqueIndex("ux_ptw_tenant_retention_tenant_class").on(t.tenantId, t.recordClass),
  idxTenantId: index("idx_ptw_tenant_retention_tenant").on(t.tenantId),
}));
export const insertPtwTenantRetentionConfigSchema = createInsertSchema(ptwTenantRetentionConfig).omit({
  id: true,
  updatedAt: true,
  createdAt: true,
});
export type InsertPtwTenantRetentionConfig = z.infer<typeof insertPtwTenantRetentionConfigSchema>;
export type PtwTenantRetentionConfig = typeof ptwTenantRetentionConfig.$inferSelect;

// ─── Task #2199: Create Permit Wizard Schema ──────────────────────────────
// Client-side Zod schema for the 5-step industrial create-permit wizard.
// Shared here so the server can also parse/validate the same shape when
// processing a wizard submission. The `.superRefine` validates time ordering
// and the 30-day cap rule.
//
// Task #3797 — PTW-Only Tenant Standalone Sites & Zones.
// siteId + areaId are now optional. The cross-field guard below enforces that
// either (siteId + areaId) OR manualSiteId is supplied, so PTW-only tenants
// without the access-management module can still create permits using manual
// site registry entries. Standard tenants always supply siteId + areaId.
export const createPermitSchema = z.object({
  templateId: z.string().min(1, "Please select a permit type"),
  workDescription: z.string().min(10, "Description must be at least 10 characters"),
  siteId: z.string().optional(),
  areaId: z.string().optional(),
  manualSiteId: z.string().optional(),
  manualAreaId: z.string().optional(),
  requestedStartTime: z.coerce.date({ required_error: "Start time is required" }),
  requestedEndTime: z.coerce.date({ required_error: "End time is required" }),
  referenceNumber: z.string().optional(),
  specialConditions: z.string().optional(),
  hazards: z.array(z.string()).min(1, "At least one hazard must be identified"),
  ppeRequired: z.array(z.string()).min(1, "At least one PPE item must be selected"),
  gasTestRequired: z.boolean().default(false),
  gasTypes: z.array(z.string()).optional(),
  isolationRequired: z.boolean().default(false),
  isolationDescription: z.string().optional(),
  workingAtHeight: z.boolean().default(false),
  hotWork: z.boolean().default(false),
  confinedSpace: z.boolean().default(false),
  workers: z.array(z.string()).optional(),
  equipment: z.array(z.string()).optional(),
  mode: z.enum(["production", "training", "drill"]).default("production"),
  drillId: z.string().optional(),
  drillFacilitator: z.string().optional(),
}).superRefine((data, ctx) => {
  // Hard validation: end must be after start.
  if (data.requestedEndTime <= data.requestedStartTime) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "End time must be after start time",
      path: ["requestedEndTime"],
    });
  }
  // Cross-field guard (Task #3797): either (siteId + areaId) OR manualSiteId required.
  // This allows PTW-only tenants to omit siteId/areaId when using manual locations.
  const hasSiteIds = Boolean(data.siteId?.trim()) && Boolean(data.areaId?.trim());
  const hasManualSite = Boolean(data.manualSiteId?.trim());
  if (!hasSiteIds && !hasManualSite) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please select a site and area, or choose a manual location",
      path: ["siteId"],
    });
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please select a site and area, or choose a manual location",
      path: ["areaId"],
    });
  }
  // Note: 30-day duration is a warning displayed inline by the UI,
  // not a blocking schema error (Task #2199).
});
export type CreatePermitFormData = z.infer<typeof createPermitSchema>;

// ─── PTW Task #2210 — Site Capacity Overrides ─────────────────────────────
//
// Per-site capacity cap table. Allows a Tenant Admin to override the
// tenant-level `ptw.permit_cap_per_site` tunable for a specific site.
// Resolution order in checkPtwSiteCap:
//   1. Row in this table for (tenant_id, site_id)
//   2. `ptw.permit_cap_per_site` tenant tunable
//   3. Platform default (50)
export const ptwSiteCapacityOverrides = pgTable("ptw_site_capacity_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  siteId: text("site_id").notNull().references(() => sites.id, { onDelete: "cascade" }),
  capLimit: integer("cap_limit").notNull(),
  createdBy: text("created_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uxTenantSite: uniqueIndex("ux_ptw_site_capacity_tenant_site").on(t.tenantId, t.siteId),
  idxTenantId: index("idx_ptw_site_capacity_tenant").on(t.tenantId),
}));
export const insertPtwSiteCapacityOverrideSchema = createInsertSchema(ptwSiteCapacityOverrides).omit({
  id: true,
  updatedAt: true,
  createdAt: true,
});
export type InsertPtwSiteCapacityOverride = z.infer<typeof insertPtwSiteCapacityOverrideSchema>;
export type PtwSiteCapacityOverride = typeof ptwSiteCapacityOverrides.$inferSelect;

// ─── Task #2697 — PTW Notification Outbox ────────────────────────────
//
// Transactional outbox for safety-critical PTW notification intents.
// All permit-decision notifications (amendment approved/rejected, extension
// confirmed, emergency stop) are written to this table so that a notification
// service outage or process crash after the permit mutation never silently
// drops the alert. A background drain job retries with exponential back-off
// and surfaces persistently-failed entries to the SA observability console
// after `max_attempts` are exhausted.
//
// Status state-machine:
//   pending → delivered  (drain job successfully delivers)
//   pending → failed     (drain job exhausts max_attempts)
// A `failed` row is immutable from the background worker side — operators
// can retry via the SA console (which resets status back to `pending`).

export const PTW_NOTIFICATION_OUTBOX_STATUSES = [
  "pending",
  "delivered",
  "failed",
] as const;
export type PtwNotificationOutboxStatus =
  (typeof PTW_NOTIFICATION_OUTBOX_STATUSES)[number];

export const ptwNotificationOutbox = pgTable("ptw_notification_outbox", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id").references(() => ptwPermits.id, { onDelete: "cascade" }),
  notificationType: text("notification_type").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  targetRole: text("target_role"),
  targetUserId: text("target_user_id"),
  channels: text("channels").array(),
  deepLink: text("deep_link"),
  status: text("status").notNull().default("pending"),
  attemptCount: integer("attempt_count").notNull().default(0),
  maxAttempts: integer("max_attempts").notNull().default(5),
  lastError: text("last_error"),
  nextAttemptAt: timestamp("next_attempt_at", { withTimezone: true }).notNull().defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  deliveredAt: timestamp("delivered_at", { withTimezone: true }),
  // Task #3096 — set to true when the outbox INSERT was committed inside a
  // db.transaction() alongside the permit state update, guaranteeing
  // atomicity. Legacy rows (false) were written outside any transaction.
  // The drain job uses this flag to prioritise atomically-inserted entries.
  atomicInsert: boolean("atomic_insert").notNull().default(false),
  // Task #3460 — when true the drain job passes breakGlass=true to
  // createPtwNotification so the notification bypasses both the DND
  // suppression AND the inAppOnly channel clamp. Must be set for every
  // emergency-stop notification so retried deliveries reach SMS/push/email
  // even when platform.notifications is OFF.
  breakGlass: boolean("break_glass").notNull().default(false),
}, (t) => ({
  idxTenantStatus: index("idx_ptw_notification_outbox_tenant_status").on(t.tenantId, t.status),
  idxNextAttempt: index("idx_ptw_notification_outbox_next_attempt").on(t.nextAttemptAt, t.status),
  idxPermit: index("idx_ptw_notification_outbox_permit").on(t.permitId),
  // Task #3460 — cleanup job scans by (status, created_at) to delete old
  // delivered/failed rows. Composite index keeps the DELETE fast at scale.
  idxStatusCreatedAt: index("idx_ptw_notification_outbox_status_created_at").on(t.status, t.createdAt),
}));
export const insertPtwNotificationOutboxSchema = createInsertSchema(ptwNotificationOutbox).omit({
  id: true,
  status: true,
  attemptCount: true,
  lastError: true,
  deliveredAt: true,
  createdAt: true,
  atomicInsert: true,
});
export type InsertPtwNotificationOutbox = z.infer<typeof insertPtwNotificationOutboxSchema>;
export type PtwNotificationOutbox = typeof ptwNotificationOutbox.$inferSelect;

// ─── Action Platform: Notifications ──────────────────────────────────

// ─── PTW-AUD-712 · IoT / gas-detector telemetry ingest ───────────────
// Live gas-detector readings ingested from hardware / Integrations Hub.
// The validation engine treats the latest readings as the authoritative
// gas-clearance source and auto-invalidates clearance on threshold breach
// or retest-window expiry (instead of a manual name-stamp result).
export const ptwGasReadings = pgTable("ptw_gas_readings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitId: text("permit_id").notNull().references(() => ptwPermits.id, { onDelete: "cascade" }),
  detectorId: text("detector_id").notNull(),
  gasType: text("gas_type").notNull(), // O2, LEL, H2S, CO, …
  value: numeric("value").notNull(),
  unit: text("unit").notNull().default("ppm"), // ppm, %, %LEL
  thresholdMin: numeric("threshold_min"),
  thresholdMax: numeric("threshold_max"),
  status: text("status").notNull().default("normal"), // normal | warning | breach
  source: text("source").notNull().default("iot"), // iot | manual
  readingAt: timestamp("reading_at", { withTimezone: true }).notNull().defaultNow(),
  ingestedAt: timestamp("ingested_at", { withTimezone: true }).notNull().defaultNow(),
  metadata: jsonb("metadata"),
}, (t) => ({
  idxPtwGasReadingsTenantPermit: index("idx_ptw_gas_readings_tenant_permit").on(t.tenantId, t.permitId),
  idxPtwGasReadingsPermitReadingAt: index("idx_ptw_gas_readings_permit_reading_at").on(t.permitId, t.readingAt),
}));
export const insertPtwGasReadingSchema = createInsertSchema(ptwGasReadings).omit({
  id: true,
  tenantId: true,
  status: true,
  ingestedAt: true,
});
export type InsertPtwGasReading = z.infer<typeof insertPtwGasReadingSchema>;
export type PtwGasReading = typeof ptwGasReadings.$inferSelect;

// ─── PTW-AUD-714 · Per-permit-type competency / training matrix ──────
// Tenant-configurable competency requirements keyed by permit type. The
// issue-time validation gate evaluates these against each named worker's
// credentials and produces a per-worker denial reason when a mandatory
// competency is missing.
export const ptwCompetencyRequirements = pgTable("ptw_competency_requirements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  permitType: text("permit_type").notNull(), // hot_work, confined_space, …
  competencyCode: text("competency_code").notNull(), // fire_watch, gas_tester, …
  competencyLabel: text("competency_label").notNull(),
  // "*" = applies to every worker on the permit; otherwise a PTW role key.
  appliesToRole: text("applies_to_role").notNull().default("*"),
  // Optional link to a credential type used to verify the competency.
  credentialType: text("credential_type"),
  mandatory: boolean("mandatory").notNull().default(true),
  active: boolean("active").notNull().default(true),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uqPtwCompReq: uniqueIndex("uq_ptw_comp_req_tenant_type_comp_role").on(
    t.tenantId, t.permitType, t.competencyCode, t.appliesToRole,
  ),
  idxPtwCompReqTenantType: index("idx_ptw_comp_req_tenant_type").on(t.tenantId, t.permitType),
}));
export const insertPtwCompetencyRequirementSchema = createInsertSchema(ptwCompetencyRequirements).omit({
  id: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertPtwCompetencyRequirement = z.infer<typeof insertPtwCompetencyRequirementSchema>;
export type PtwCompetencyRequirement = typeof ptwCompetencyRequirements.$inferSelect;

// ─── Task #3806 — PTW Blueprint Cascade Rollback Log ─────────────────────────
//
// Persisted log written BEFORE every in-place adopt mutation. A "pending" row
// that survives a server crash is evidence of an incomplete adopt and a
// candidate for manual recovery. On completion the row is resolved to either
// "succeeded" (clean adopt) or "rolled_back" (cascade failure or per-permit
// conflicts — template was restored to its pre-adopt snapshot).
//
// status values:
//   pending        — adopt is in-flight; process crash leaves row here
//   succeeded      — adopt completed cleanly; no rollback needed
//   rolled_back    — cascade failed (setup error or per-permit conflicts);
//                    template + steps restored to pre-adopt state
//   rollback_failed — rollback itself failed; template left at update_available
export const ptwBlueprintCascadeRollbackLog = pgTable("ptw_blueprint_cascade_rollback_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id"),
  tenantTemplateId: text("tenant_template_id").notNull(),
  blueprintId: text("blueprint_id").notNull(),
  blueprintVersion: integer("blueprint_version").notNull(),
  preAdoptSnapshot: jsonb("pre_adopt_snapshot").notNull(),
  preAdoptSteps: jsonb("pre_adopt_steps").notNull(),
  affectedPermitCount: integer("affected_permit_count").notNull().default(0),
  rollbackReason: text("rollback_reason"),
  cascadeError: text("cascade_error"),
  initiatedBy: text("initiated_by"),
  status: text("status").notNull().default("pending"),
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  idxTenantTemplate: index("idx_ptw_cascade_rollback_template").on(t.tenantTemplateId, t.createdAt),
  idxBlueprint: index("idx_ptw_cascade_rollback_blueprint").on(t.blueprintId, t.createdAt),
}));
export type PtwBlueprintCascadeRollbackLogEntry = typeof ptwBlueprintCascadeRollbackLog.$inferSelect;
